import React, { useState, useEffect, useRef } from "react";
import { Search, Shield, Star, CheckCircle, Circle, Upload, MessageSquare, Bell, User, Home, BarChart2, FileText, LogOut, ArrowLeft, MapPin, Award, Clock, Filter, X, Eye, EyeOff, Menu, ChevronRight, ChevronDown, ChevronUp, ChevronLeft, Phone, Mail, ExternalLink, BookOpen, TrendingUp, Users, Zap, Lock, AlertCircle, ThumbsUp, DollarSign, Globe } from "lucide-react";

const API_BASE = "https://certifieddesi.com/api";
// Local dev: const API_BASE = "http://localhost/api";

// ── PALETTE ───────────────────────────────────────────
// Cream:      #F2EDE4 / #F8F4EE / #EDE7DC
// Charcoal:   #1C1B19 / #2E2C28 / #4A4740
// Terracotta: #C4522A
// Gold:       #C49A2A
// Muted:      #8A8578 / #A8A298


const CATS = [
  { id:"immigration", label:"Immigration & Visas",    icon:"✈️",  desc:"H-1B, Green Card, Citizenship, DACA",           color:"#C4522A" },
  { id:"tax",         label:"Tax & Accounting",       icon:"📊",  desc:"NRI Tax, CPA, FBAR, FATCA, Bookkeeping",        color:"#C49A2A" },
  { id:"realestate",  label:"Real Estate",            icon:"🏠",  desc:"Buy, Sell, Mortgage, Vastu-aware agents",        color:"#2E7D52" },
  { id:"legal",       label:"Legal Services",         icon:"⚖️",  desc:"Family Law, Business Law, Estate Planning",      color:"#7B5EA7" },
  { id:"financial",   label:"Financial Planning",     icon:"💰",  desc:"CFP, NRI Wealth, Retirement, Investments",       color:"#C49A2A" },
  { id:"insurance",   label:"Insurance",              icon:"🛡️",  desc:"Life, Health, Auto & Home, Business",            color:"#4A90D9" },
  { id:"healthcare",  label:"Healthcare",             icon:"🏥",  desc:"Doctors, Dentists, Mental Health, Ayurveda",     color:"#26A69A" },
  { id:"wedding",     label:"Wedding Services",       icon:"💍",  desc:"Pandits, Photographers, Decorators, Catering",   color:"#E91E8C" },
  { id:"care",        label:"Care Services",          icon:"🤱",  desc:"Nanny, Elder Care, Childcare, Cook, Housekeeper", color:"#FF7043" },
  { id:"education",   label:"Education & Tutoring",   icon:"📚",  desc:"SAT, MCAT, Music, Dance, Language, College Prep", color:"#1565C0" },
  { id:"ittraining",  label:"IT Training & Jobs",     icon:"💻",  desc:"OPT/CPT Training, Java, Data Science, Placement", color:"#00897B" },
  { id:"astrology",   label:"Astrology & Spiritual",  icon:"🔮",  desc:"Vedic Astrology, Vastu, Horoscope, Pooja",       color:"#6A1B9A" },
  { id:"roommates",   label:"Roommates & Rentals",    icon:"🛋️",  desc:"Find Rooms, List Your Space, PG, Apartments",    color:"#F57C00" },
  { id:"business",    label:"Business Services",      icon:"💼",  desc:"LLC Formation, IT Consulting, HR & Payroll",      color:"#455A64" },
];

// Sub-categories for each category
const SUB_CATS = {
  immigration:  ["H-1B Visa","L-1 Visa","EB-2 / EB-3 Green Card","O-1 Visa","Family-Based Immigration","Citizenship & Naturalization","DACA / DAPA","B1/B2 Visitor Visa","Student Visa (F-1)","Employment Authorization","Deportation Defense","Asylum"],
  tax:          ["NRI Tax Filing","FBAR / FATCA Compliance","Dual-Country Tax","Business Tax (S-Corp, LLC)","Individual Tax Return","Bookkeeping","Payroll Services","Tax Planning","ITIN Services","Form 15CA / 15CB","Estate & Gift Tax","Sales Tax"],
  realestate:   ["Buy a Home","Sell Your Home","Mortgage & Home Loans","NRI Property Investment","Vastu Consultation","Commercial Real Estate","Property Management","Real Estate Law","Home Inspection","Rental Properties","First-Time Home Buyer","Investment Properties"],
  legal:        ["Family Law & Divorce","Business Formation","Estate Planning & Wills","Employment Law","Criminal Defense","Real Estate Law","Personal Injury","Consumer Protection","Intellectual Property","Contract Review","Notary Services","Power of Attorney"],
  financial:    ["CFP / Financial Planning","NRI Wealth Management","Retirement Planning (401k/IRA)","Investment Advisory","India Mutual Funds","PFIC Compliance","College Savings (529)","Cross-Border Wealth","Life Insurance Planning","Stock & Equity Comp","Social Security Planning","Estate Planning"],
  insurance:    ["Life Insurance","Health Insurance","Auto Insurance","Home & Renters Insurance","Business Insurance","Disability Insurance","Long-Term Care","Travel Insurance","Group Benefits","Medicare Planning","Key Person Insurance","NRI Insurance"],
  healthcare:   ["Internal Medicine","Family Physician","Cardiologist","Diabetes & Endocrinology","Mental Health & Therapy","Dentist","Pediatrician","OBGYN","Dermatologist","Orthopedic","Ayurveda & Wellness","Telehealth"],
  wedding:      ["Hindu / Vedic Pandit","Wedding Photographer","Wedding Videographer","Decorator & Florist","Catering Service","Mehndi Artist","Wedding DJ","Wedding Planner","Bridal Makeup Artist","Wedding Venue","Horse & Carriage","Tailoring & Lehenga"],
  care:         ["Nanny","Babysitter","Elder Care","Cook / Chef","Housekeeper","Childcare Center","Au Pair","Special Needs Care","Pet Sitter","Night Nurse","Home Health Aide","Companion Care"],
  education:    ["SAT / ACT Tutoring","MCAT / USMLE Prep","K-12 Math & Science","College Counseling","Bharatanatyam / Kathak","Carnatic / Hindustani Music","Hindi / Gujarati School","STEM Enrichment","Music Lessons","Art Classes","Language Tutoring","Chess & Coding"],
  ittraining:   ["Java / Full Stack","Data Science & AI","AWS / Cloud Certifications","Salesforce Training","Business Analyst","OPT/CPT Job Placement","IT Staffing Agency","QA / Testing","UI/UX Design","Cybersecurity","DevOps & Docker","SAP Training"],
  astrology:    ["Vedic Astrology","Kundali Matching","Vastu Shastra","Numerology","Tarot Reading","Pooja Services","Birth Chart Analysis","Career Astrology","Relationship Guidance","Gemstone Consultation","Temple Services","Spiritual Healing"],
  roommates:    ["Find a Roommate","List Your Room","Single Room","Shared Room","Paying Guest (PG)","1-BHK / 2-BHK Apt","Short-Term Rental","Student Housing","Female Only","Male Only","Near Temple / Desi Area","Furnished Room"],
  business:     ["LLC / Corp Formation","Business Consulting","IT Consulting","HR & Payroll","E-2 Visa Business","Import / Export","Desi Restaurant Setup","Social Media Marketing","Website Development","Accounting Setup","Business Loans","Franchise Consulting"],
};

// Top US cities for Desi community
const TOP_CITIES = [
  "All Cities","San Jose, CA","Fremont, CA","New York, NY","New Jersey, NJ","Chicago, IL",
  "Houston, TX","Dallas, TX","Atlanta, GA","Seattle, WA","Washington, DC",
  "Los Angeles, CA","Boston, MA","Austin, TX","Philadelphia, PA","Detroit, MI",
];

// Trending quick links
const TRENDING = [
  {label:"H-1B Lawyer NJ", q:"H-1B lawyer", cat:"immigration", city:"New Jersey, NJ"},
  {label:"Hindi CPA Houston", q:"Hindi CPA", cat:"tax", city:"Houston, TX"},
  {label:"Desi Wedding Photographer", q:"wedding photographer", cat:"wedding", city:""},
  {label:"Indian Elder Care Bay Area", q:"elder care", cat:"care", city:"San Jose, CA"},
  {label:"NRI Tax Filing", q:"NRI tax", cat:"tax", city:""},
  {label:"Green Card Attorney", q:"green card attorney", cat:"immigration", city:""},
  {label:"Vedic Pandit for Wedding", q:"pandit", cat:"wedding", city:""},
  {label:"OPT Job Placement", q:"OPT placement", cat:"ittraining", city:""},
];


const PROVIDERS = [
  { id:1, name:"Rajesh Sharma, CPA", cat:"tax", sub:"CPA Services", city:"Houston, TX", rating:4.9, reviews:127, badge:"elite", score:28, lang:["English","Hindi","Gujarati"], bio:"Specializing in NRI taxation, dual-country tax filing, and small business accounting for 15+ years. AICPA member, Texas Board certified.", fee:"$150–$500", resp:"< 2 hrs", img:"👨‍💼", creds:["CPA – Texas Board of Accountancy","AICPA Member","NRI Tax Specialist (15 yrs)"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:2, name:"Priya Mehta, Esq.", cat:"immigration", sub:"Immigration Law", city:"New Jersey, NJ", rating:4.8, reviews:203, badge:"elite", score:29, lang:["English","Hindi","Marathi"], bio:"Immigration attorney with 12+ years handling H1B, L1, EB-2/EB-3 green cards, naturalization, and DACA cases.", fee:"$200–$800", resp:"< 4 hrs", img:"👩‍💼", creds:["NJ State Bar (Active)","AILA Member","LL.M Immigration Law"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:3, name:"Vikram Patel, CFP", cat:"financial", sub:"Financial Advisor", city:"Chicago, IL", rating:4.7, reviews:89, badge:"verified", score:22, lang:["English","Gujarati","Hindi"], bio:"FINRA-registered CFP helping Desi families build wealth, plan retirement, and navigate the US investment landscape.", fee:"$200/hr or AUM", resp:"< 6 hrs", img:"👨‍💼", creds:["CFP – CFP Board","FINRA BrokerCheck Verified","Series 65 Licensed"], v:{id:true,license:true,bg:true,ins:true,ref:false} },
  { id:4, name:"Anita Krishnaswamy", cat:"realestate", sub:"Buyer's Agent", city:"Dallas, TX", rating:4.9, reviews:156, badge:"elite", score:26, lang:["English","Tamil","Telugu"], bio:"Top-rated DFW real estate agent specializing in helping South Asian families find their dream home. Vastu-aware consultations available.", fee:"Buyer commission-free", resp:"< 1 hr", img:"👩‍💼", creds:["TX Real Estate License (Active)","NAR Member","Certified Buyer Rep"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:5, name:"Suresh Nair, CLU", cat:"insurance", sub:"Life Insurance", city:"Fremont, CA", rating:4.6, reviews:74, badge:"verified", score:21, lang:["English","Malayalam","Hindi"], bio:"Chartered Life Underwriter with MDRT membership. Helping South Asian families protect their financial future.", fee:"Commission-based", resp:"< 3 hrs", img:"👨‍💼", creds:["CA Insurance License (Active)","CLU Designation","MDRT Member"], v:{id:true,license:true,bg:true,ins:false,ref:true} },
  { id:6, name:"Deepa Aggarwal, Esq.", cat:"legal", sub:"Business Law", city:"Atlanta, GA", rating:4.8, reviews:92, badge:"elite", score:27, lang:["English","Hindi","Punjabi"], bio:"Business attorney specializing in LLC formation, contracts, employment law, and E-2/EB-5 visa advisory for Desi entrepreneurs.", fee:"$250–$600", resp:"< 5 hrs", img:"👩‍💼", creds:["GA State Bar (Active)","Business Law Specialist","E-2 Visa Expert"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:7, name:"Dr. Meera Nambiar, MD", cat:"healthcare", sub:"Internal Medicine", city:"San Jose, CA", rating:4.9, reviews:211, badge:"elite", score:28, lang:["English","Malayalam","Tamil"], bio:"Board-certified internist with 18+ years serving South Asian patients. Specializes in chronic disease management, preventive care, and conditions prevalent in the Desi community including diabetes and heart disease.", fee:"Insurance accepted", resp:"< 3 hrs", img:"👩‍⚕️", creds:["MD – Stanford University","Board Certified Internal Medicine","AMA Member"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:8, name:"Ravi Khanna, MBA", cat:"business", sub:"Business Consulting", city:"Austin, TX", rating:4.7, reviews:63, badge:"verified", score:23, lang:["English","Hindi","Punjabi"], bio:"Helping Desi entrepreneurs launch and scale businesses in the US. Specializes in LLC formation, E-2 visa business plans, and IT consulting. Former Fortune 500 strategy consultant.", fee:"$150–$400/hr", resp:"< 4 hrs", img:"👨‍💼", creds:["MBA – Wharton School","Certified Business Consultant","E-2 Visa Business Plan Specialist"], v:{id:true,license:true,bg:true,ins:true,ref:false} },
  { id:9, name:"Arjun Bhatia, CFP", cat:"financial", sub:"NRI Wealth Planning", city:"New York, NY", rating:4.8, reviews:88, badge:"elite", score:26, lang:["English","Hindi","Punjabi"], bio:"Certified Financial Planner specializing in NRI cross-border wealth management, US retirement accounts, India mutual funds, and PFIC compliance. Serving Desi professionals across the tri-state area for 10+ years.", fee:"$250/hr or AUM", resp:"< 3 hrs", img:"👨‍💼", creds:["CFP – CFP Board","FINRA Series 65","NRI Cross-Border Specialist"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:10, name:"Pandit Rajeev Sharma", cat:"wedding", sub:"Hindu / Vedic Pandit", city:"New Jersey, NJ", rating:4.9, reviews:184, badge:"elite", score:27, lang:["English","Hindi","Sanskrit"], bio:"Performing authentic Vedic wedding ceremonies across the tri-state area for 20+ years. Specializes in Hindu, Gujarati, Punjabi, and South Indian wedding rituals. Available for havan, mundan, and griha pravesh.", fee:"$500–$1,500", resp:"< 4 hrs", img:"👨‍💼", creds:["Vedic Pandit – Kashi Vidyapith","20+ yrs performing ceremonies","References from 1,000+ families"], v:{id:true,license:false,bg:true,ins:false,ref:true} },
  { id:11, name:"Sunita Reddy", cat:"care", sub:"Elder Care Specialist", city:"San Jose, CA", rating:4.8, reviews:62, badge:"verified", score:22, lang:["English","Telugu","Tamil"], bio:"Certified elder care specialist with 10+ years providing in-home care for South Asian seniors. Understands dietary needs (vegetarian, South Indian), language preferences, and cultural comfort for elderly parents.", fee:"$22–$35/hr", resp:"< 2 hrs", img:"👩‍⚕️", creds:["Certified Nursing Assistant (CNA)","Elder Care Specialist Cert","10+ yrs South Asian elder care"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:12, name:"TechVista Training", cat:"ittraining", sub:"OPT/CPT Job Placement", city:"Dallas, TX", rating:4.7, reviews:143, badge:"elite", score:26, lang:["English","Hindi","Telugu"], bio:"STEM OPT job placement agency placing 200+ IT professionals per year. Specializes in Java Full Stack, Data Science, Salesforce, and AWS tracks. 90-day placement guarantee. W2 employment, not 1099.", fee:"No upfront fee", resp:"< 1 hr", img:"👨‍💼", creds:["NASBA Registered","E-Verify Enrolled","200+ placements in 2024"], v:{id:true,license:true,bg:true,ins:true,ref:true} },
  { id:13, name:"Preethi Krishnan", cat:"education", sub:"SAT / MCAT Tutoring", city:"Chicago, IL", rating:4.9, reviews:97, badge:"elite", score:28, lang:["English","Tamil","Hindi"], bio:"Harvard-educated tutor with 99th percentile SAT scores and board-certified MCAT coaching. Specializes in South Asian students preparing for competitive college admissions and medical school.", fee:"$80–$150/hr", resp:"< 3 hrs", img:"👩‍💼", creds:["M.Ed – Harvard Graduate School","SAT 1580 (99th percentile)","MCAT 524 (99th percentile)"], v:{id:true,license:true,bg:true,ins:false,ref:true} },
  { id:14, name:"Astro Guruji Mahesh", cat:"astrology", sub:"Vedic Astrology", city:"Houston, TX", rating:4.6, reviews:211, badge:"verified", score:21, lang:["English","Hindi","Tamil"], bio:"Third-generation Vedic astrologer with 15+ years of practice. Specializes in kundali analysis, Vastu shastra, kundali matching for marriages, career guidance, and annual horoscope readings.", fee:"$75–$200/session", resp:"< 6 hrs", img:"👨‍💼", creds:["Jyotish Shastra – Banaras Hindu University","3rd Generation Astrologer","15+ yrs practice in USA"], v:{id:true,license:false,bg:true,ins:false,ref:true} },
];

const BADGE = {
  elite:   { label:"Certified Desi Elite", bg:"#C4522A", tx:"#F2EDE4", icon:"🏆" },
  verified:{ label:"Verified Desi Pro",    bg:"#1C1B19", tx:"#F2EDE4", icon:"✓"  },
  listed:  { label:"Listed",               bg:"#8A8578", tx:"#F2EDE4", icon:"·"  },
};

const REVIEWS = [
  {name:"Sunil M.", r:5, text:"Excellent – filed my H1B extension flawlessly. Highly recommend to any Desi professional.", date:"2 weeks ago"},
  {name:"Kavita R.", r:5, text:"Explained everything in Hindi. The whole process was completely stress-free.", date:"1 month ago"},
  {name:"Arjun P.", r:4, text:"Very responsive and knowledgeable about NRI tax situations. Good experience overall.", date:"2 months ago"},
];

const STEPS_ONB = ["Basic Info","Professional Details","Upload Documents","Verification","Go Live 🚀"];


// ── ZIP → CITY lookup – all valid US 3-digit prefixes ──────────────
// Covers ~41,000 US ZIP codes via prefix matching (built once at module load)
const ZIP_PREFIX_MAP = (() => {
  // state abbreviation → nearest Desi hub city on our platform
  const STATE_CITY = {
    "NY":"New York, NY","NJ":"New Jersey, NJ","IL":"Chicago, IL","TX_H":"Houston, TX",
    "TX_D":"Dallas, TX","TX_A":"Austin, TX","GA":"Atlanta, GA","WA":"Seattle, WA",
    "DC":"Washington, DC","MD":"Washington, DC","VA":"Washington, DC",
    "CA_LA":"Los Angeles, CA","CA_SF":"San Jose, CA","CA_FR":"Fremont, CA",
    "MA":"Boston, MA","PA":"Philadelphia, PA","MI":"Detroit, MI",
  };
  const m = {};
  // New York state (100-149 = NYC metro; 005,006 = some NY)
  for(let i=100;i<=104;i++) m[String(i)]="New York, NY";
  for(let i=105;i<=115;i++) m[String(i)]="New York, NY"; // Hudson Valley, LI
  for(let i=116;i<=119;i++) m[String(i)]="New York, NY"; // LI + Staten Island
  for(let i=120;i<=149;i++) m[String(i)]="New York, NY"; // upstate NY → closest
  // New Jersey
  for(let i=70;i<=89;i++)  m[String(i).padStart(3,"0")]="New Jersey, NJ";
  // Massachusetts → Boston
  for(let i=10;i<=29;i++)  m[String(i).padStart(3,"0")]="Boston, MA";
  // Connecticut → Boston (closest)
  for(let i=60;i<=69;i++)  m[String(i).padStart(3,"0")]="Boston, MA";
  // Rhode Island → Boston
  for(let i=28;i<=29;i++)  m[String(i).padStart(3,"0")]="Boston, MA";
  // Vermont, NH, Maine → Boston
  for(let i=30;i<=49;i++)  m[String(i).padStart(3,"0")]="Boston, MA";
  // Pennsylvania → Philadelphia
  for(let i=150;i<=196;i++) m[String(i)]="Philadelphia, PA";
  // Delaware → Philadelphia
  for(let i=197;i<=199;i++) m[String(i)]="Philadelphia, PA";
  // DC / MD / VA → Washington DC
  for(let i=200;i<=212;i++) m[String(i)]="Washington, DC";
  for(let i=220;i<=246;i++) m[String(i)]="Washington, DC";  // VA
  for(let i=247;i<=268;i++) m[String(i)]="Washington, DC";  // WV → DC closest
  // North Carolina → Atlanta (closest Desi hub)
  for(let i=270;i<=289;i++) m[String(i)]="Atlanta, GA";
  // South Carolina → Atlanta
  for(let i=290;i<=299;i++) m[String(i)]="Atlanta, GA";
  // Georgia → Atlanta
  for(let i=300;i<=319;i++) m[String(i)]="Atlanta, GA";
  // Florida → Atlanta (closest hub; no FL city in list)
  for(let i=320;i<=349;i++) m[String(i)]="Atlanta, GA";
  // Alabama → Atlanta
  for(let i=350;i<=369;i++) m[String(i)]="Atlanta, GA";
  // Tennessee → Atlanta
  for(let i=370;i<=385;i++) m[String(i)]="Atlanta, GA";
  // Mississippi → Atlanta
  for(let i=386;i<=397;i++) m[String(i)]="Atlanta, GA";
  // Kentucky → Atlanta
  for(let i=400;i<=427;i++) m[String(i)]="Atlanta, GA";
  // Ohio → Detroit (closest)
  for(let i=430;i<=458;i++) m[String(i)]="Detroit, MI";
  // Indiana → Chicago
  for(let i=460;i<=479;i++) m[String(i)]="Chicago, IL";
  // Michigan → Detroit
  for(let i=480;i<=499;i++) m[String(i)]="Detroit, MI";
  // Wisconsin → Chicago
  for(let i=530;i<=549;i++) m[String(i)]="Chicago, IL";
  // Illinois → Chicago
  for(let i=600;i<=629;i++) m[String(i)]="Chicago, IL";
  // Minnesota → Chicago (closest)
  for(let i=550;i<=567;i++) m[String(i)]="Chicago, IL";
  // Iowa → Chicago
  for(let i=500;i<=528;i++) m[String(i)]="Chicago, IL";
  // Missouri → Dallas (midpoint; Chicago also close)
  for(let i=630;i<=658;i++) m[String(i)]="Dallas, TX";
  // Kansas → Dallas
  for(let i=660;i<=679;i++) m[String(i)]="Dallas, TX";
  // Nebraska → Dallas
  for(let i=680;i<=693;i++) m[String(i)]="Dallas, TX";
  // South Dakota, North Dakota → Chicago
  for(let i=570;i<=588;i++) m[String(i)]="Chicago, IL";
  // Wyoming, Montana → Seattle (closest)
  for(let i=590;i<=599;i++) m[String(i)]="Seattle, WA";
  for(let i=820;i<=831;i++) m[String(i)]="Seattle, WA";
  // Colorado → Dallas (Denver has Desi community but not in list)
  for(let i=800;i<=816;i++) m[String(i)]="Dallas, TX";
  // New Mexico → Dallas
  for(let i=870;i<=884;i++) m[String(i)]="Dallas, TX";
  // Oklahoma → Dallas
  for(let i=730;i<=749;i++) m[String(i)]="Dallas, TX";
  // Texas Dallas metro (750-759, 761-769)
  for(let i=750;i<=769;i++) m[String(i)]="Dallas, TX";
  // Texas Houston metro (770-779)
  for(let i=770;i<=779;i++) m[String(i)]="Houston, TX";
  // Texas Austin/San Antonio (780-789)
  for(let i=780;i<=789;i++) m[String(i)]="Austin, TX";
  // Texas El Paso, other (790-799)
  for(let i=790;i<=799;i++) m[String(i)]="Dallas, TX";
  // Louisiana → Houston (closest Desi hub)
  for(let i=700;i<=714;i++) m[String(i)]="Houston, TX";
  // Arkansas → Dallas
  for(let i=716;i<=729;i++) m[String(i)]="Dallas, TX";
  // Idaho → Seattle
  for(let i=832;i<=838;i++) m[String(i)]="Seattle, WA";
  // Washington state → Seattle
  for(let i=980;i<=994;i++) m[String(i)]="Seattle, WA";
  // Oregon → Seattle
  for(let i=970;i<=979;i++) m[String(i)]="Seattle, WA";
  // California – split by region
  // Southern CA (900-917) → Los Angeles
  for(let i=900;i<=917;i++) m[String(i)]="Los Angeles, CA";
  // Central CA (932-939) → San Jose (closest large hub)
  for(let i=932;i<=939;i++) m[String(i)]="San Jose, CA";
  // Bay Area (940-958) → San Jose / Fremont
  for(let i=940;i<=944;i++) m[String(i)]="San Jose, CA";
  for(let i=945;i<=946;i++) m[String(i)]="Fremont, CA";
  for(let i=947;i<=958;i++) m[String(i)]="San Jose, CA";
  // Sacramento area (956-961) → San Jose
  for(let i=956;i<=961;i++) m[String(i)]="San Jose, CA";
  // San Diego (919-921) → Los Angeles
  for(let i=919;i<=921;i++) m[String(i)]="Los Angeles, CA";
  // Inland Empire (917-925) → Los Angeles
  for(let i=917;i<=925;i++) m[String(i)]="Los Angeles, CA";
  // Nevada → Los Angeles (Las Vegas) 
  for(let i=889;i<=899;i++) m[String(i)]="Los Angeles, CA";
  // Arizona → Dallas / Los Angeles – split at 855
  for(let i=850;i<=855;i++) m[String(i)]="Los Angeles, CA"; // Phoenix
  for(let i=856;i<=865;i++) m[String(i)]="Dallas, TX";
  // Utah → Seattle
  for(let i=840;i<=847;i++) m[String(i)]="Seattle, WA";
  // Alaska → Seattle
  for(let i=995;i<=999;i++) m[String(i)]="Seattle, WA";
  // Hawaii → Los Angeles (closest)
  for(let i=967;i<=968;i++) m[String(i)]="Los Angeles, CA";
  // Puerto Rico & territories → New York (closest)
  for(let i=0;i<=9;i++)    m[String(i).padStart(3,"0")]="New York, NY";
  for(let i=900;i<=988;i++) { if(!m[String(i)]) m[String(i)]="Los Angeles, CA"; }
  return m;
})();

const lookupZip = (zip) => {
  if (!zip || zip.length < 3) return null;
  return ZIP_PREFIX_MAP[zip.slice(0,3)] || null;
};

// ── GLOBAL STYLES ─────────────────────────────────────
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;0,700;1,400;1,600&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: #F2EDE4; color: #1C1B19; }
  input, textarea, select, button { font-family: inherit; }
  input:focus, textarea:focus, select:focus { outline: none; }

  .cd-input {
    width: 100%; border: 1px solid #DDD8CE; border-radius: 8px;
    padding: 10px 12px; font-size: 0.85rem; background: #F2EDE4; color: #1C1B19;
    transition: border-color 0.15s;
  }
  .cd-input:focus { border-color: #C4522A; }
  textarea.cd-input { resize: none; }
  select.cd-input { cursor: pointer; }

  .cd-btn-primary {
    background: #C4522A; color: #F2EDE4; border: none; border-radius: 8px;
    padding: 11px 20px; font-size: 0.82rem; font-weight: 700; cursor: pointer;
    transition: background 0.15s;
  }
  button, a, select, [role="button"], label { cursor: pointer; }
  input { cursor: text; }
  input[type="checkbox"], input[type="radio"] { cursor: pointer; }
  .cd-btn-primary { transition: background 0.2s ease, box-shadow 0.2s ease; }
  .cd-btn-primary:hover { background: #D4623A; box-shadow: 0 3px 14px rgba(196,82,42,0.4); }
  .cd-btn-primary:active { background: #B84020; transform: translateY(1px); }
  .cd-btn-dark { transition: background 0.2s ease, border-color 0.2s ease; }
  .cd-btn-dark:hover { background: #3E3C38; border-color: #C4522A; }
  .cd-btn-outline { transition: background 0.2s ease; }
  .cd-btn-outline:hover { background: #C4522A11; }
  .cd-nav-link { transition: color 0.15s; cursor: pointer; }
  .cd-back { transition: color 0.15s; cursor: pointer; }
  .cd-back:hover { color: #C4522A; }
  .cd-footer-link { cursor: pointer; transition: color 0.15s; }
  .cd-footer-legal { cursor: pointer; transition: color 0.15s; }

  .cd-btn-outline {
    background: transparent; color: #C4522A; border: 2px solid #C4522A; border-radius: 8px;
    padding: 9px 20px; font-size: 0.82rem; font-weight: 700; cursor: pointer;
    transition: background 0.15s;
  }
  .cd-btn-outline:hover { background: #C4522A11; }

  .cd-btn-ghost {
    background: transparent; color: #8A8578; border: 1px solid #DDD8CE; border-radius: 8px;
    padding: 9px 20px; font-size: 0.82rem; cursor: pointer;
    transition: background 0.15s, color 0.15s, border-color 0.15s;
  }
  .cd-btn-ghost:hover { background: #EDE7DC; color: #1C1B19; border-color: #8A8578; }

  /* Dark/charcoal buttons (used on dark backgrounds) */
  .cd-btn-dark {
    background: #2E2C28; color: #F2EDE4; border: 1px solid #3E3C38; border-radius: 8px;
    padding: 11px 20px; font-size: 0.82rem; font-weight: 600; cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .cd-btn-dark:hover { background: #3E3C38; border-color: #C4522A; color: #F2EDE4; box-shadow: 0 0 0 1px #C4522A33; }

  /* Nav link hover */
  .cd-nav-link:hover { color: #F2EDE4; text-decoration: none; }

  /* Avatar button */
  .cd-avatar-btn:hover { box-shadow: 0 0 0 3px #C4522A44; transform: scale(1.05); transition: all 0.15s; }

  .cd-card {
    background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 12px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .cd-card:hover { box-shadow: 0 4px 20px rgba(196,82,42,0.08); border-color: #C4522A66; }

  .cd-label {
    display: block; font-size: 0.65rem; font-weight: 700; color: #8A8578;
    letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 4px;
  }

  /* Nav links */
  .cd-nav-link { color: #A8A298; font-size: 0.8rem; cursor: pointer; transition: color 0.15s; background: none; border: none; }
  .cd-nav-link:hover { color: #F2EDE4; }

  /* Header logo */
  .cd-logo { display: flex; align-items: center; gap: 10px; cursor: pointer; }

  /* Category cards */
  .cd-cat-card {
    padding: 24px 20px; background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 10px;
    cursor: pointer; transition: border-color 0.2s, background 0.2s;
  }
  .cd-cat-card:hover { border-color: #C4522A; background: #FDF9F5; }

  /* View all dashed card */
  .cd-cat-all {
    padding: 24px 20px; background: transparent; border: 2px dashed #DDD8CE; border-radius: 10px;
    cursor: pointer; display: flex; flex-direction: column; align-items: center; justify-content: center;
    transition: border-color 0.2s;
  }
  .cd-cat-all:hover { border-color: #C4522A; }

  /* Search pill tags */
  .cd-pill {
    font-size: 0.72rem; background: #2E2C28; color: #A8A298;
    padding: 6px 14px; border-radius: 99px; border: 1px solid #3E3C38;
    cursor: pointer; transition: background 0.15s, border-color 0.15s, color 0.15s;
  }
  .cd-pill:hover { background: #C4522A22; border-color: #C4522A; color: #C4522A; }

  /* Light pill (on cream bg) */
  .cd-pill-light {
    font-size: 0.7rem; background: #2E2C28; color: #A8A298;
    padding: 5px 12px; border-radius: 99px; border: 1px solid #3E3C38;
    cursor: pointer;
  }

  /* Upload area */
  .cd-upload {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    border: 2px dashed #DDD8CE; border-radius: 10px; padding: 12px 16px;
    cursor: pointer; transition: border-color 0.15s; background: #F2EDE4;
  }
  .cd-upload:hover { border-color: #C4522A; }

  /* Back button */
  .cd-back {
    display: flex; align-items: center; gap: 6px; color: #8A8578;
    font-size: 0.75rem; margin-bottom: 24px; background: none; border: none; cursor: pointer;
    transition: color 0.15s;
  }
  .cd-back:hover { color: #C4522A; }

  /* Sidebar nav item */
  .cd-side-btn {
    width: 100%; display: flex; align-items: center; gap: 8px;
    padding: 8px 12px; border-radius: 8px; font-size: 0.75rem; font-weight: 700;
    margin-bottom: 2px; border: none; cursor: pointer; transition: background 0.15s, color 0.15s;
    background: transparent; color: #4A4740;
  }
  .cd-side-btn:hover { background: #EDE7DC; }
  .cd-side-btn.active { background: #C4522A; color: #F2EDE4; }
  .cd-side-btn.active:hover { background: #D4623A; }

  /* Footer links */
  .cd-footer-link { cursor: pointer; font-size: 0.75rem; transition: color 0.15s; color: #8A8578; }
  .cd-footer-link:hover { color: #F2EDE4; }
  .cd-footer-legal { cursor: pointer; font-size: 0.7rem; color: #8A8578; text-decoration: none; transition: color 0.15s, text-decoration 0.15s; }
  .cd-footer-legal:hover { color: #F2EDE4; text-decoration: underline; }

  /* Message row */
  .cd-msg-row {
    display: flex; align-items: center; gap: 12px; padding: 16px;
    cursor: pointer; border-bottom: 1px solid #EDE7DC; transition: background 0.15s;
  }
  .cd-msg-row:hover { background: #EDE7DC; }

  /* Resource card */
  .cd-resource-card {
    padding: 24px; cursor: pointer; background: #F8F4EE;
    border: 1px solid #DDD8CE; border-radius: 12px;
    transition: box-shadow 0.2s, border-color 0.2s;
  }
  .cd-resource-card:hover { box-shadow: 0 4px 20px rgba(196,82,42,0.08); border-color: #C4522A66; }

  /* Press card */
  .cd-press-card {
    padding: 20px; display: flex; gap: 16px; align-items: flex-start;
    background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 12px;
    transition: box-shadow 0.2s, border-color 0.2s; cursor: pointer;
  }
  .cd-press-card:hover { box-shadow: 0 4px 20px rgba(196,82,42,0.08); border-color: #C4522A66; }

  /* Contact info card */
  .cd-contact-card {
    background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 12px;
    padding: 20px; display: flex; align-items: center; gap: 14px;
    transition: border-color 0.15s;
  }
  .cd-contact-card:hover { border-color: #C4522A66; }

  /* Dashboard metric card */
  .cd-metric {
    background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 12px; padding: 16px;
  }

  /* Profile tab */
  .cd-tab {
    padding: 12px 20px; font-size: 0.72rem; font-weight: 700; text-transform: capitalize;
    border: none; cursor: pointer; transition: color 0.15s, background 0.15s;
    color: #8A8578; background: transparent; border-bottom: 2px solid transparent;
  }
  .cd-tab:hover { color: #4A4740; }
  .cd-tab.active { color: #C4522A; background: #FDF9F5; border-bottom-color: #C4522A; }

  /* Category filter pill */
  .cd-filter-pill {
    padding: 6px 14px; border-radius: 99px; font-size: 0.72rem; font-weight: 700;
    white-space: nowrap; border: 1px solid #DDD8CE; cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    background: #F8F4EE; color: #4A4740;
  }
  .cd-filter-pill:hover { border-color: #C4522A66; }
  .cd-filter-pill.active { background: #C4522A; border-color: #C4522A; color: #F2EDE4; }

  /* Upload doc label hover */
  .cd-upload-label {
    display: flex; align-items: center; gap: 8px;
    border: 2px dashed #DDD8CE; border-radius: 10px; padding: 12px;
    cursor: pointer; transition: border-color 0.15s;
  }
  .cd-upload-label:hover { border-color: #C4522A; }

  /* Search input wrapper */
  .cd-search-wrap {
    flex: 1; display: flex; align-items: center; gap: 8px;
    background: #F8F4EE; border: 1px solid #DDD8CE; border-radius: 10px; padding: 0 14px;
    transition: border-color 0.15s;
  }
  .cd-search-wrap:focus-within { border-color: #C4522A; }

  /* Enhanced hovers */
  .cd-prov-card { transition: transform 0.18s, box-shadow 0.18s, border-color 0.18s; }
  .cd-prov-card:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(196,82,42,0.12); border-color: #C4522A88; }
  .cd-section-btn { transition: color 0.15s, transform 0.15s; }
  .cd-section-btn:hover { color: #C4522A; transform: translateX(2px); }
  .cd-avatar-btn { transition: transform 0.15s, box-shadow 0.15s; }
  .cd-avatar-btn:hover { transform: scale(1.08); box-shadow: 0 0 0 3px #C4522A44; }
  .cd-stat-card { transition: transform 0.18s; }
  .cd-stat-card:hover { transform: translateY(-3px); }
  .cd-badge-pill { transition: opacity 0.15s, transform 0.15s; cursor: default; }
  .cd-badge-pill:hover { opacity: 0.85; transform: scale(1.03); }
  .cd-profile-connect-btn { transition: transform 0.15s; }
  .cd-profile-connect-btn:hover { transform: translateY(-1px); }
  .cd-how-step { transition: transform 0.2s; }
  .cd-how-step:hover { transform: translateY(-4px); }
  .cd-trust-item { transition: color 0.15s; }
  .cd-trust-item:hover { color: #F2EDE4; }
  a, [role="button"] { outline-offset: 3px; }
  [role="button"]:focus-visible { outline: 2px solid #C4522A; border-radius: 4px; }
  .cd-recent-item:hover { background: #EDE7DC; }
  .cd-trend-pill:hover { background: #FFFFFF28!important; border-color: #C4522A!important; color: #F2EDE4!important; }
  .cd-pill-light:hover { background: #EDE7DC!important; border-color: #8A8578!important; }
  /* ── TWO-ROW HEADER ─────────────────────────────── */
  .cd-header-bar1 {
    background: #1C1B19; border-bottom: 1px solid #2E2C28;
    position: sticky; top: 0; z-index: 40;
  }
  .cd-header-bar2 {
    background: #2A2824; border-bottom: 1px solid #3E3C38;
    position: sticky; top: 58px; z-index: 39;
  }
  /* Inline search in header row 1 */
  .cd-header-search {
    display: flex; align-items: stretch; flex: 1 1 0; min-width: 140px; max-width: 620px;
    border-radius: 8px; overflow: hidden; height: 36px;
    border: 2px solid transparent;
    /* Smooth fade – border separate, opacity/transform for show/hide */
    transition: border-color 0.2s ease, opacity 0.35s ease, transform 0.35s ease, width 0.3s ease;
    will-change: opacity, transform;
  }
  .cd-header-search:focus-within { border-color: #C49A2A; }
  /* Header search – hidden while hero is visible (simple opacity fade) */
  .cd-header-search.hero-active {
    opacity: 0;
    pointer-events: none;
    transform: translateY(-4px);
  }
  .cd-header-search.hero-hidden {
    opacity: 1;
    pointer-events: auto;
    transform: translateY(0);
  }
  .cd-header-search-cat-wrap {
    position: relative; display: inline-flex; align-items: center;
    background: #EDE7DC; border-right: 1px solid #C8C0B0;
    flex-shrink: 0;
  }
  .cd-header-search-cat {
    background: #EDE7DC; border: none;
    padding: 0 28px 0 12px;  /* right pad for chevron */
    font-size: 0.76rem; color: #1C1B19; cursor: pointer; outline: none;
    font-family: inherit; appearance: none; -webkit-appearance: none;
    white-space: nowrap; height: 36px;
    /* field-sizing:content is the modern way; fallback: just let it be wide enough */
    field-sizing: content;
    min-width: 88px;   /* "All Services" minimum */
  }
  .cd-header-search-cat option { background: #F2EDE4; color: #1C1B19; }
  .cd-search-cat-chevron {
    position: absolute; right: 8px; pointer-events: none;
    color: #4A4740; font-size: 0.65rem; top: 50%; transform: translateY(-50%);
    font-weight: 700;
  }
  .cd-header-search-input {
    flex: 1; border: none; outline: none; background: #F8F4EE;
    color: #1C1B19; font-size: 0.82rem; padding: 0 10px; min-width: 0;
  }
  .cd-header-search-btn {
    background: #C4522A; border: none; padding: 0 14px;
    cursor: pointer; display: flex; align-items: center; transition: background 0.15s;
  }
  .cd-header-search-btn:hover { background: #D4623A; }
  /* Category strip – Row 2 */
  .cd-cat-strip-wrap {
    flex: 1; min-width: 0; overflow: hidden; position: relative;
  }
  .cd-cat-strip-wrap::after {
    content: ""; position: absolute; right: 0; top: 0; bottom: 0; width: 28px;
    background: linear-gradient(to right, transparent, #2A2824);
    pointer-events: none; z-index: 1;
  }
  .cd-cat-strip {
    display: flex; align-items: center;
    overflow-x: auto; scrollbar-width: none;
    cursor: grab; user-select: none;
  }
  .cd-cat-strip.grabbing { cursor: grabbing; }
  .cd-cat-strip::-webkit-scrollbar { display: none; }
  .cd-cat-link {
    color: #A8A298; font-size: 0.75rem; padding: 0 11px; height: 38px;
    display: inline-flex; align-items: center; width: auto; cursor: pointer;
    white-space: nowrap; flex-shrink: 0; gap: 4px;
    border-right: 1px solid #3E3C38;
    transition: color 0.15s ease, background 0.15s ease;
    background: transparent; border-top: none; border-bottom: none; border-left: none;
    font-family: inherit;
  }
  .cd-cat-link:first-child { border-left: 1px solid #3E3C38; }
  .cd-cat-link:hover { color: #F2EDE4; background: #3E3C38; }
  .cd-cat-link.active { color: #F2EDE4; background: #3E3C38; border-bottom: 2px solid #C4522A; }
  /* Mega menu overlay – always in DOM */
  .cd-mega-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.6); z-index: 45;
    opacity: 0; pointer-events: none; transition: opacity 0.3s ease;
  }
  .cd-mega-overlay.open { opacity: 1; pointer-events: auto; }
  /* Sidebar panel – Amazon-style left drawer */
  .cd-mega-menu {
    position: fixed; top: 0; left: 0; width: 300px; bottom: 0;
    background: #1C1B19; z-index: 101;  /* above overlay */
    box-shadow: 6px 0 32px rgba(0,0,0,0.6);
    display: flex; flex-direction: column; overflow: hidden;
    transform: translateX(-300px);
    transition: transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94);
    will-change: transform;
  }
  .cd-mega-menu.open { transform: translateX(0); }
  /* Panel – always in DOM, transform slide */
  .cd-mega-menu-list { overflow-y: auto; flex: 1; scrollbar-width: thin; scrollbar-color: #3E3C38 transparent; }
  .cd-mega-menu-list::-webkit-scrollbar { width: 4px; }
  .cd-mega-menu-list::-webkit-scrollbar-thumb { background: #3E3C38; border-radius: 2px; }
  /* Accordion submenu */
  .cd-submenu { overflow:hidden; max-height:0; opacity:0; transition:max-height 0.3s ease,opacity 0.25s ease; background:#161514; }
  .cd-submenu.open { max-height:600px; opacity:1; }
  .cd-submenu-item { padding:10px 20px 10px 48px; font-size:0.75rem; color:#8A8578; cursor:pointer; display:block; border-bottom:1px solid #1E1D1C; transition:color 0.12s,background 0.12s; }
  .cd-submenu-item:hover { color:#F2EDE4; background:#232220; }
  /* Menu header */
  .cd-mega-header { background:#2E2C28; padding:14px 20px; flex-shrink:0; border-bottom:1px solid #3E3C38; display:flex; align-items:center; gap:10px; }
  /* Slide views inside the panel */
  .cd-menu-views { flex:1; overflow:hidden; position:relative; display:flex; flex-direction:column; }
  .cd-menu-main, .cd-menu-sub {
    position:absolute; top:0; left:0; right:0; bottom:0;
    overflow-y:auto; scrollbar-width:thin; scrollbar-color:#3E3C38 transparent;
    transition: transform 0.28s cubic-bezier(0.25,0.46,0.45,0.94),
                opacity   0.22s ease;
    will-change: transform;
  }
  .cd-menu-main::-webkit-scrollbar, .cd-menu-sub::-webkit-scrollbar { width:4px; }
  .cd-menu-main::-webkit-scrollbar-thumb, .cd-menu-sub::-webkit-scrollbar-thumb { background:#3E3C38; border-radius:2px; }
  /* Main view: visible by default */
  .cd-menu-main           { transform:translateX(0);     opacity:1; }
  .cd-menu-main.slide-out { transform:translateX(-100%); opacity:0; }
  /* Sub view: hidden off-right by default */
  .cd-menu-sub           { transform:translateX(100%); opacity:0; }
  .cd-menu-sub.slide-in  { transform:translateX(0);    opacity:1; }
  /* Sub header */
  .cd-menu-sub-header {
    padding:14px 20px; background:#252320; border-bottom:1px solid #1E1D1C;
    display:flex; align-items:center; gap:10px; flex-shrink:0; position:sticky; top:0; z-index:1;
  }
  .cd-menu-back-btn {
    background:none; border:none; color:#A8A298; cursor:pointer; padding:0;
    display:flex; align-items:center; gap:6px; font-size:0.8rem; font-family:inherit;
    transition:color 0.15s;
  }
  .cd-menu-back-btn:hover { color:#F2EDE4; }
  /* Category row in sidebar */
  .cd-mega-cat {
    padding: 12px 20px; cursor: pointer; transition: background 0.12s;
    display: flex; align-items: center; gap: 12px; border-bottom: 1px solid #252320;
  }
  .cd-mega-cat:hover { background: #2E2C28; }
  .cd-mega-cat.selected { background: #2E2C28; border-left: 3px solid #C4522A; }
  /* Sub-panel on right */
  .cd-mega-sub-panel {
    position: fixed; top: 96px; left: 320px; width: 360px; bottom: 0;
    background: #242220; border-right: 1px solid #2E2C28; z-index: 38;
    overflow-y: auto; padding: 20px; animation: slideInLeft 0.15s ease;
    scrollbar-width: thin; scrollbar-color: #3E3C38 transparent;
  }
  .cd-mega-sub-panel::-webkit-scrollbar { width: 4px; }
  .cd-mega-sub { font-size: 0.75rem; color: #A8A298; padding: 8px 12px; border-radius: 6px; cursor: pointer; transition: all 0.12s; display: block; }
  .cd-mega-sub:hover { color: #F2EDE4; background: #2E2C28; }
  /* Location button (replaces pill) */
  .cd-location-btn {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 0 10px; height: 34px;
    border: 1px solid #3E3C38; border-radius: 6px;
    background: transparent; color: #A8A298; font-size: 0.72rem;
    cursor: pointer; white-space: nowrap; flex-shrink: 0;
    font-family: inherit; transition: border-color 0.15s, color 0.15s;
  }
  .cd-location-btn:hover { border-color: #C49A2A; color: #F2EDE4; }
  .cd-loc-opt:hover { border-color: #C4522A !important; background: #F8F4EE !important; }
  .cd-location-btn-text { max-width: 100px; overflow:hidden; text-overflow:ellipsis; }
  /* Keep old pill styles for any legacy use */
  .cd-location-pill {
    display: flex; align-items: center; gap: 0; padding: 0;
    border: 1px solid #3E3C38; border-radius: 6px; cursor: pointer;
    background: transparent; color: #A8A298; font-size: 0.72rem;
    transition: border-color 0.15s, color 0.15s; white-space: nowrap;
    font-family: inherit; flex-shrink: 0; overflow: hidden; height: 34px;
  }
  .cd-location-pill:hover { border-color: #C49A2A; color: #F2EDE4; }
  .cd-location-icon {
    display: flex; align-items: center; justify-content: center;
    padding: 0 8px; height: 100%; background: #2E2C28;
    border-right: 1px solid #3E3C38; flex-shrink: 0;
  }
  .cd-location-select {
    background: transparent; border: none; color: inherit; cursor: pointer;
    outline: none; font-family: inherit; font-size: 0.72rem;
    padding: 0 8px; height: 100%; max-width: 130px;
  }
  .verify-chevron:hover { background: #3E3C38!important; }
  .cd-recent-remove:hover { background: #DDD8CE!important; color: #C4522A!important; }
  /* Recent searches dropdown – shown only when search box is focused */
  .cd-recent-dropdown {
    position: absolute; top: calc(100% + 6px); left: 0; right: 0;
    background: #F2EDE4; border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.18);
    overflow: hidden; z-index: 50; border: 1px solid #DDD8CE;
    opacity: 0; pointer-events: none; transform: translateY(-4px);
    transition: opacity 0.15s, transform 0.15s;
  }
  .cd-search-outer:focus-within .cd-recent-dropdown {
    opacity: 1; pointer-events: auto; transform: translateY(0);
  }

  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: #EDE7DC; }
  ::-webkit-scrollbar-thumb { background: #C4522A66; border-radius: 2px; }

  /* ── Dark mode transitions ── */
  * { transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease; }
  button, input, select, textarea { transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.15s ease; }

  /* ── Dark mode body ── */
  [data-theme="dark"] body { background: #1C1B19; color: #F2EDE4; }
  [data-theme="dark"] .cd-card { background: #2E2C28 !important; border-color: #3E3C38 !important; }
  [data-theme="dark"] .cd-input { background: #2E2C28 !important; border-color: #3E3C38 !important; color: #F2EDE4 !important; }
  [data-theme="dark"] .cd-btn-ghost { color: #C8C4BC !important; border-color: #3E3C38 !important; }
  [data-theme="dark"] .cd-label { color: #A8A298 !important; }

  /* ── Comprehensive dark mode overrides ── */
  [data-theme="dark"] { background: #1C1B19; }
  [data-theme="dark"] .cd-card  { background: #2A2824 !important; border-color: #3E3C38 !important; color: #F2EDE4 !important; }
  [data-theme="dark"] .cd-input { background: #2A2824 !important; border-color: #3E3C38 !important; color: #F2EDE4 !important; }
  [data-theme="dark"] .cd-input::placeholder { color: #6A6660 !important; }
  [data-theme="dark"] .cd-btn-ghost  { color: #C8C4BC !important; border-color: #3E3C38 !important; }
  [data-theme="dark"] .cd-btn-outline { color: #E8714A !important; border-color: #E8714A !important; }
  [data-theme="dark"] .cd-btn-dark   { background: #2A2824 !important; border-color: #3E3C38 !important; color: #C8C4BC !important; }
  [data-theme="dark"] .cd-back       { color: #A8A298 !important; }
  [data-theme="dark"] .cd-prov-card  { background: #2A2824 !important; border-color: #3E3C38 !important; }
  [data-theme="dark"] .cd-prov-card:hover { background: #332F2A !important; border-color: #E8714A !important; }
  [data-theme="dark"] .cd-side-btn   { color: #C8C4BC !important; }
  [data-theme="dark"] .cd-side-btn.active { background: #E8714A !important; color: #F2EDE4 !important; }
  [data-theme="dark"] .cd-footer-link { color: #8A8578 !important; }
  [data-theme="dark"] .cd-footer-link:hover { color: #F2EDE4 !important; }
  [data-theme="dark"] select { background: #2A2824 !important; color: #F2EDE4 !important; border-color: #3E3C38 !important; }
  [data-theme="dark"] option { background: #2A2824 !important; color: #F2EDE4 !important; }

  /* Hero search bar in dark mode */
  [data-theme="dark"] #hero-search { color: #F2EDE4 !important; }
  [data-theme="dark"] #hero-cat    { color: #F2EDE4 !important; background: transparent !important; }

  /* ── Structural dark sections – always dark regardless of theme ── */
  .cd-hero-dark { background: #1C1B19 !important; color: #F2EDE4 !important; }
  .cd-hero-dark * { color: inherit; }
  [data-theme="dark"] .cd-hero-dark { background: #0F0E0C !important; }

  /* ── Focus visible for a11y ── */
  :focus-visible { outline: 2px solid #C4522A; outline-offset: 2px; }
  button:focus-visible, a:focus-visible, [role="button"]:focus-visible { outline: 2px solid #C4522A; outline-offset: 2px; border-radius: 4px; }
  .trust-scroll {
    overflow: hidden; position: relative; cursor: grab;
  }
  .trust-scroll.dragging { cursor: grabbing; }
  .trust-scroll::-webkit-scrollbar { height: 0; }
  .trust-marquee {
    display: flex; gap: 36px; width: max-content;
    animation: trustScroll 33s linear infinite;
    will-change: transform;
  }
  .trust-marquee.paused { animation-play-state: paused; }
  @keyframes trustScroll {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-33.333%); }
  }
  .faq-item { border-bottom: 1px solid #DDD8CE; }
  .faq-q { width:100%; display:flex; align-items:center; justify-content:space-between;
    padding: 16px 0; background: none; border: none; cursor: pointer; text-align: left;
    font-size: 0.9rem; font-weight: 600; color: #1C1B19; }
  .faq-a { padding: 0 0 16px; font-size: 0.82rem; color: #4A4740; line-height: 1.7; }

  /* Service category illustrations */
  .cat-img-immigration { background: linear-gradient(135deg, #1C1B19 0%, #2E2C28 50%, #C4522A22 100%); }
  .cat-img-tax { background: linear-gradient(135deg, #1a2740 0%, #243550 50%, #C49A2A22 100%); }
  .cat-img-realestate { background: linear-gradient(135deg, #1C3020 0%, #2A4030 50%, #4CAF5022 100%); }
  .cat-img-legal { background: linear-gradient(135deg, #2A1C30 0%, #3A2840 50%, #7B5EA722 100%); }
  .cat-img-financial { background: linear-gradient(135deg, #2A2010 0%, #3A3020 50%, #C49A2A22 100%); }
  .cat-img-insurance { background: linear-gradient(135deg, #1C2030 0%, #2E3040 50%, #4A90D922 100%); }
  .cat-img-healthcare { background: linear-gradient(135deg, #1C2A2A 0%, #2A3A3A 50%, #26A69A22 100%); }
  .cat-img-business { background: linear-gradient(135deg, #201820 0%, #302830 50%, #C4522A22 100%); }

  @media(max-width:767px){.desk-nav{display:none!important}.mob-btn{display:flex!important}#header-bar2{display:none!important}}
  @media(min-width:768px){.mob-btn{display:none!important}.desk-nav{display:flex!important}.sm-show{display:block!important}#header-bar2{display:flex!important}}
  @media(max-width:767px){.sm-show{display:none!important}}
  @media(min-width:1024px){.lg-show{display:block!important}}
  @media(min-width:768px){.profile-grid{grid-template-columns:1fr 320px!important}}
  @media(min-width:768px){.contact-grid{grid-template-columns:1fr 2fr!important}}
  @media(min-width:768px){.story-even,.story-odd{grid-template-columns:1fr 1fr!important}}
  /* Category page sub-cat buttons */
  .cd-subcat-btn { transition: all 0.15s; }
  .cd-subcat-btn:hover { transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,0,0,0.1); }
  /* Roommate card */
  .cd-rm-card { transition: transform 0.18s, box-shadow 0.18s; }
  .cd-rm-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.1); }
  /* App download buttons */
  .cd-app-btn { transition: transform 0.15s, box-shadow 0.15s; }
  .cd-app-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.15); }
  /* India services card hover */
  .cd-india-sub { transition: background 0.12s, color 0.12s; }
  .cd-india-sub:hover { background: #C4522A!important; color: #F2EDE4!important; border-color: #C4522A!important; }
  /* Hero tab buttons */
  .cd-hero-tab { transition: all 0.2s; }
  .cd-hero-tab:hover { background: rgba(255,255,255,0.1)!important; }
  /* (mega overlay defined above with correct z-index) */
`;

export default function App() {
    // ── Theme (dark/light) ─────────────────────────────────────
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem("cd_theme");
      if (saved) return saved;
    } catch(e) {}
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });
  const isDark = theme === "dark";

  // Persist theme + apply to <html>
  useEffect(() => {
    try { localStorage.setItem("cd_theme", theme); } catch(e) {}
    document.documentElement.setAttribute("data-theme", theme);
    document.documentElement.setAttribute("lang", "en");
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const toggleTheme = () => setTheme(t => t === "dark" ? "light" : "dark");

  const [page, setPage] = useState(() => {
    const path = window.location.pathname.replace(/^\//, "").replace(/\/$/, "") || "landing";
    const valid = ["landing","join","auth","search","profile","consumer-dash","onboarding","provider-dash","how-it-works","safety","post-need","help","verification-process","success-stories","resources","about","contact","privacy","terms","press","wedding","care","education","ittraining","astrology","roommates","india-services","saved","category","reset-password","immigration","tax","realestate","legal","financial","insurance","healthcare","education","ittraining","astrology","roommates","business"];
    return valid.includes(path) ? path : "landing";
  });
  const [user, setUser] = useState(()=>{
    // Restore session from sessionStorage (survives tab navigation, not fresh browser open)
    try {
      const saved = sessionStorage.getItem("cd_user");
      return saved ? JSON.parse(saved) : null;
    } catch(e) { return null; }
  });

  // Persist user to sessionStorage whenever it changes
  useEffect(()=>{
    if(user) sessionStorage.setItem("cd_user", JSON.stringify(user));
    else sessionStorage.removeItem("cd_user");
  }, [user]);

  // On mount: verify session token with server (runs once)
  useEffect(()=>{
    const token = sessionStorage.getItem("cd_token");
    if(!token || user) return; // Already have user from sessionStorage
    fetch(API_BASE+"/submit_auth.php?action=me",{
      headers:{"Authorization":"Bearer "+token}
    })
    .then(r=>r.ok?r.json():null)
    .then(d=>{
      if(d?.success && d.user){
        const u=d.user;
        setUser({type:u.type,name:u.name,email:u.email,id:u.id,verified:u.verified,city:u.city,category:u.category,avatar_url:u.avatar_url||""});
      } else {
        // Token invalid – clear
        sessionStorage.removeItem("cd_token");
        sessionStorage.removeItem("cd_user");
        setUser(null);
      }
    })
    .catch(()=>{});
  }, []); // eslint-disable-line
  // ── Dynamic meta per page ──────────────────────────────────
  useEffect(()=>{
    let title = "CertifiedDESI – Verified South Asian Professionals";
    let desc = "Find verified South Asian professionals across the US. Background-checked, license-verified Desi CPAs, lawyers, doctors, real estate agents and more.";
    switch(page){
    case "landing": title="CertifiedDESI – Verified South Asian Professionals | Find Trusted Desi CPAs, Lawyers, Doctors & More"; desc="Find verified South Asian professionals across the US. CertifiedDESI connects you with background-checked, license-verified Desi CPAs, immigration lawyers, doctors, real estate agents and more."; break;
    case "search": title="Search Verified Desi Professionals | CertifiedDESI"; desc="Browse verified South Asian professionals by category, city and language. Every pro is background-checked and license-verified."; break;
    case "profile": title="Professional Profile | CertifiedDESI"; desc="View credentials, reviews and contact a verified Desi professional on CertifiedDESI."; break;
    case "immigration": title="Verified Immigration Lawyers & Visa Attorneys | CertifiedDESI"; desc="Find verified Desi immigration attorneys for H-1B, Green Card, EB-2 NIW and more. All lawyers are license-verified and community-reviewed."; break;
    case "tax": title="Verified Desi CPAs & Tax Professionals | CertifiedDESI"; desc="Find NRI tax specialists, CPAs and accountants who understand FBAR, FATCA and dual-country filing. Verified and reviewed by the Desi community."; break;
    case "realestate": title="Verified Desi Real Estate Agents | CertifiedDESI"; desc="Find Vastu-aware, South Asian real estate agents for buying, selling and mortgages across major US cities."; break;
    case "legal": title="Verified Desi Lawyers & Legal Services | CertifiedDESI"; desc="Find verified South Asian attorneys for family law, business law, estate planning and more."; break;
    case "financial": title="Verified Desi Financial Advisors & CFPs | CertifiedDESI"; desc="Find NRI wealth managers, CFPs and financial advisors who understand cross-border investing and US retirement accounts."; break;
    case "healthcare": title="Verified Desi Doctors & Healthcare Providers | CertifiedDESI"; desc="Find South Asian physicians, dentists, therapists and healthcare providers who understand your cultural background."; break;
    case "wedding": title="Verified Desi Wedding Services | CertifiedDESI"; desc="Find verified Desi wedding pandits, photographers, decorators and caterers for your Hindu, Sikh or Muslim wedding."; break;
    case "join": title="Join as a Verified Professional | CertifiedDESI"; desc="Get listed on CertifiedDESI as a verified Desi professional. Reach thousands of South Asian clients actively looking for trusted experts."; break;
    case "how-it-works": title="How CertifiedDESI Works | Verified South Asian Professionals"; desc="Learn how CertifiedDESI's 30-point verification process works and how to find or become a verified Desi professional."; break;
    case "safety": title="Safety & Trust | CertifiedDESI Verification Process"; desc="CertifiedDESI verifies every professional with license checks, background screening, insurance verification and community reviews."; break;
    case "contact": title="Contact CertifiedDESI | hello@certifieddesi.com"; desc="Get in touch with the CertifiedDESI team. We respond within 24 hours."; break;
    case "about": title="About CertifiedDESI | Our Mission"; desc="CertifiedDESI was built to bring trust and transparency to the South Asian professional services market in the US."; break;
      default: break;
    }
    document.title = title;
    let m = document.querySelector('meta[name="description"]');
    if(!m){ m=document.createElement("meta"); m.name="description"; document.head.appendChild(m); }
    m.content = desc;
    // Open Graph
    let og = document.querySelector('meta[property="og:title"]');
    if(!og){ og=document.createElement("meta"); og.setAttribute("property","og:title"); document.head.appendChild(og); }
    og.content = title;

    // Canonical URL
    let can = document.querySelector('link[rel="canonical"]');
    if(!can){ can=document.createElement("link"); can.rel="canonical"; document.head.appendChild(can); }
    can.href = "https://certifieddesi.com/" + (page==="landing"?"":page);
  },[page]);

  const [authMode, setAuthMode]   = useState("login");
  const [authDirect, setAuthDirect] = useState(null); // "provider"|"consumer" to skip tiles
  const [csrfToken, setCsrfToken]   = useState("");

  // Fetch CSRF token on mount
  useEffect(() => {
    fetch(API_BASE + "/auth.php?action=csrf", {credentials:"include"})
      .then(r => r.json())
      .then(d => { if(d.csrf) setCsrfToken(d.csrf); })
      .catch(() => {});
  }, []);
  const [uType, setUType]         = useState("consumer");
  const [searchQ, setSearchQ]     = useState("");
  const [searchCat, setSearchCat] = useState("");
  const [subCatPage, setSubCatPage] = useState(""); // active subcategory
  const [recentSearches, setRecentSearches] = useState([]);
  const [selProv, setSelProv]     = useState(null);
  const [provFromCat, setProvFromCat] = useState("");
  const [showPwd, setShowPwd]     = useState(false);
  const [step, setStep]           = useState(0);
  const [uploads, setUploads]     = useState({});
  const [toast, setToast]         = useState(null);
  const [chatOpen, setChatOpen]   = useState(false);
  const [chatMsg, setChatMsg]     = useState("");
  const [chatHist, setChatHist]   = useState([{from:"ai",text:"Hi! 👋 I'm Milo, your CertifiedDESI guide. Tell me what you're looking for – like 'I need a CPA' or 'find me an immigration lawyer' – and I'll take you right there.",action:null}]);
  const [profTab, setProfTab]     = useState("credentials");
  const [dashSec, setDashSec]     = useState("overview");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [filterBadge, setFilterBadge] = useState("");
  const [filterLang, setFilterLang]   = useState("");
  const [sortBy, setSortBy]           = useState("rating");
  const [reqOpen, setReqOpen]     = useState(false);
  const [reqSent, setReqSent]     = useState(false);
  const [msgOpen, setMsgOpen]     = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [agreed, setAgreed]     = useState(false);
  const [reqNote, setReqNote]   = useState("");
  const [faqOpen, setFaqOpen]   = useState({});
  const [postNeed, setPostNeed] = useState({cat:"",desc:"",budget:"",lang:"",contact:""});
  const [postSent, setPostSent] = useState(false);
  const [contactForm, setContactForm] = useState({name:"",email:"",subject:"",msg:""});
  const [contactSent, setContactSent] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState(() => {
    try {
      const s = sessionStorage.getItem("cd_saved");
      return s ? JSON.parse(s) : [];
    } catch(e) { return []; }
  });

  // Persist savedProfiles to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem("cd_saved", JSON.stringify(savedProfiles)); } catch(e) {}
  }, [savedProfiles]);
  const [userCity, setUserCity]     = useState("All Cities");
  const [heroTab, setHeroTab]       = useState("find");   // "find" | "post"
  const [menuOpen, setMenuOpen]     = useState(false);    // mega-menu drawer
  const [menuActiveCat, setMenuActiveCat] = useState(null);
  const [menuExpandedCat, setMenuExpandedCat] = useState(null);
  const [menuView, setMenuView]         = useState("main");  // "main" | "sub"
  const menuMainScrollRef = useRef(0);          // saves main-view scroll position
  const [heroVisible, setHeroVisible]     = useState(true);
  const [notifyModal, setNotifyModal]     = useState(false);
  const [locationModal, setLocationModal] = useState(false);
  const [locating, setLocating]           = useState(false);   // GPS in-progress
  const [emailStep, setEmailStep]   = useState(0);        // 0=form,1=verify,2=done

  // ── SCROLL TO TOP on page change ──────────────────
  const topRef = useRef(null);
  useEffect(() => {
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "instant" });
  }, [page]);

  // ── Reset filters when category changes on search page ──────
  // Track previous searchCat to detect actual change
  const prevCatRef = useRef(searchCat);
  useEffect(() => {
    if (page === "search" && searchCat !== prevCatRef.current) {
      setFilterBadge("");
      setFilterLang("");
      setSortBy("rating");
    }
    prevCatRef.current = searchCat;
  }, [searchCat, page]);

  // ── Hero/header search swap – scroll-based ──────────
  // On landing: hide header search while hero search is visible.
  // Use both IntersectionObserver AND scroll fallback for reliability.
  useEffect(() => {
    if (page !== "landing") {
      setHeroVisible(false);
      return;
    }
    setHeroVisible(true); // start: hero visible

    const checkVisibility = () => {
      const sentinel = document.getElementById("hero-search-sentinel");
      if (!sentinel) return;
      const rect = sentinel.getBoundingClientRect();
      // Sentinel is visible if its top is still within the viewport
      setHeroVisible(rect.bottom > 0);
    };

    // Wire scroll listener
    const scroller = document.documentElement;
    scroller.addEventListener("scroll", checkVisibility, { passive: true });
    window.addEventListener("scroll", checkVisibility, { passive: true });

    // Also try IntersectionObserver as primary (cleaner)
    let obs = null;
    const rafId = requestAnimationFrame(() => {
      const sentinel = document.getElementById("hero-search-sentinel");
      if (!sentinel) return;
      obs = new IntersectionObserver(
        ([entry]) => setHeroVisible(entry.isIntersecting),
        { threshold: 0 }
      );
      obs.observe(sentinel);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (obs) obs.disconnect();
      scroller.removeEventListener("scroll", checkVisibility);
      window.removeEventListener("scroll", checkVisibility);
    };
  }, [page]);

  // ── Esc closes menu; body scroll lock ──────────────
  useEffect(() => {
    const fn = (e) => { if(e.key==="Escape"){setMenuOpen(false);setMenuExpandedCat(null);} };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, []);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  // ── Server-Sent Events (real-time) ────────────────────────
  useEffect(() => {
    if (!user?.id) return;
    let es;
    let retryTimeout;
    let retryDelay = 2000;

    const connect = () => {
      es = new EventSource(API_BASE + "/events.php", {withCredentials: true});

      es.addEventListener("message", (e) => {
        try {
          const data = JSON.parse(e.data);
          showToast(data.msg || "New message", "success");
        } catch {}
      });

      es.addEventListener("notification", (e) => {
        try {
          const data = JSON.parse(e.data);
          showToast(data.msg || "Notification", data.type || "success");
        } catch {}
      });

      es.addEventListener("lead", (e) => {
        try {
          const data = JSON.parse(e.data);
          showToast("New lead: " + (data.from || "someone"), "success");
        } catch {}
      });

      es.addEventListener("reconnect", () => {
        es.close();
        retryTimeout = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      });

      es.onerror = () => {
        es.close();
        retryTimeout = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30000);
      };

      es.addEventListener("connected", () => {
        retryDelay = 2000; // Reset backoff on successful connect
      });
    };

    connect();
    return () => {
      clearTimeout(retryTimeout);
      if (es) es.close();
    };
  }, [user?.id]); // eslint-disable-line

  // ── Browser back / forward ─────────────────────────────
  useEffect(() => {
    const valid = ["landing","join","auth","search","profile","consumer-dash","onboarding","provider-dash","how-it-works","safety","post-need","help","verification-process","success-stories","resources","about","contact","privacy","terms","press","wedding","care","education","ittraining","astrology","roommates","india-services","saved","category","reset-password","immigration","tax","realestate","legal","financial","insurance","healthcare","education","ittraining","astrology","roommates","business"];
    // Set initial history state
    const initPath = window.location.pathname.replace(/^\//, "").replace(/\/$/, "") || "landing";
    window.history.replaceState({ page: initPath }, "", window.location.pathname);
    const onPop = (e) => {
      const p = e.state?.page ||
                window.location.pathname.replace(/^\//, "").split("/")[0] ||
                "landing";
      const target = valid.includes(p) ? p : "landing";
      setPage(target);
      setMobileMenu(false); setMenuOpen(false);
      if (target === "landing") {
        setFilterBadge(""); setFilterLang(""); setSortBy("rating");
        setSearchQ(""); setSearchCat("");
      }
      // Restore profile from state
      if(target==="profile" && e.state?.id){
        const found = PROVIDERS.find(pr=>pr.id===e.state.id);
        if(found) setSelProv(found);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);  // eslint-disable-line

  // ── Scroll lock when modals open ──────────────────────
  useEffect(() => {
    if (locationModal || notifyModal) document.body.style.overflow = "hidden";
    else if (!menuOpen) document.body.style.overflow = "";
    return () => { if (!menuOpen) document.body.style.overflow = ""; };
  }, [locationModal, notifyModal]);

  const nav = (p) => {
    setPage(p); setMobileMenu(false); setMenuOpen(false);
    if (p === "landing") {
      setFilterBadge(""); setFilterLang(""); setSortBy("rating");
      setSearchQ(""); setSearchCat("");
    }
    // Clean URL: certifieddesi.com/contact not certifieddesi.com/#contact
    const url = p === "landing" ? "/" : "/" + p;
    window.history.pushState({page: p}, "", url);
  };
  const showToast = (msg, type="success") => {
    setToast({msg,type});
    setTimeout(()=>setToast(null), 3000);
  };

  // doAuth removed – all auth logic is inside Auth component's handleAuth

  const addRecent = (q) => {
    if (!q || !q.trim()) return;
    setRecentSearches(prev => {
      const filtered = prev.filter(r => r !== q.trim());
      return [q.trim(), ...filtered].slice(0, 3);
    });
  };
  const goSearch = (q, cat, city) => {
    if (q !== undefined) setSearchQ(q);
    if (cat !== undefined) setSearchCat(cat);
    if (city !== undefined && city) setUserCity(city);
    if (q && q.trim()) addRecent(q.trim());
    nav("search");
    setTimeout(() => {
      const el = document.getElementById("hero-search");
      if (el) el.value = "";
    }, 50);
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem("cd_user");
    nav("landing");
    showToast("Logged out");
  };

  const handleUpload = (key, file) => {
    if (!file) return;
    // For photo uploads, read as base64 so it shows in UI
    if (key === "photo" || file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploads(p=>({...p, [key]: file.name, [key+"_url"]: e.target.result}));
        showToast("Photo uploaded ✓");
      };
      reader.readAsDataURL(file);
    } else {
      setUploads(p=>({...p, [key]: file.name}));
      showToast(file.name+" uploaded ✓");
    }
  };

  // Milo – navigation-only chatbot
  // Messages stored as {from, text, action?}
  // action: {label, fn} → renders a "Take me there" button
  const sendChat = (msg) => {
    if (!msg || !msg.trim()) return;
    setChatHist(h=>[...h,{from:"user",text:msg,action:null}]);
    const m = msg.toLowerCase();
    let text = "", action = null;

    // Helper
    const go = (label, fn) => { action = {label, fn}; };

    if (/h[- ]?1b|h1b|visa|work permit|opt|cpt|green card|gc|eb-?[12345]|perm|permanent resident|citizenship|naturali|immigration|uscis|daca|asylum/.test(m)) {
      text = "I found Immigration & Visa professionals for you.";
      go("Show Immigration Lawyers →", ()=>{ goSearch("","immigration"); setChatOpen(false); });
    } else if (/tax|cpa|fbar|fatca|nri tax|itr|india tax|accountant|bookkeep|payroll|itin/.test(m)) {
      text = "Found Tax & Accounting professionals – CPAs, NRI tax specialists, and more.";
      go("Show Tax & CPA Pros →", ()=>{ goSearch("","tax"); setChatOpen(false); });
    } else if (/real estate|home|buy.*house|mortgage|vastu|property|agent|realtor/.test(m)) {
      text = "Here are verified Desi real estate agents near you.";
      go("Show Real Estate Agents →", ()=>{ goSearch("","realestate"); setChatOpen(false); });
    } else if (/lawyer|attorney|legal|divorce|family law|estate plan|will|notary|lawsuit|contract/.test(m)) {
      text = "Found Legal Services professionals – attorneys and counselors.";
      go("Show Legal Professionals →", ()=>{ goSearch("","legal"); setChatOpen(false); });
    } else if (/financial|invest|retirement|cfp|wealth|401k|ira|nri.*invest|portfolio|stocks/.test(m)) {
      text = "Found certified financial planners specialising in NRI & cross-border wealth.";
      go("Show Financial Advisors →", ()=>{ goSearch("","financial"); setChatOpen(false); });
    } else if (/insurance|life insurance|health insurance|auto insurance|medicare|disability/.test(m)) {
      text = "Found licensed insurance professionals for life, health, auto and more.";
      go("Show Insurance Agents →", ()=>{ goSearch("","insurance"); setChatOpen(false); });
    } else if (/doctor|physician|healthcare|dentist|health|medical|mental health|therapist|specialist|ayurveda/.test(m)) {
      text = "Found board-certified South Asian healthcare professionals.";
      go("Show Healthcare Providers →", ()=>{ goSearch("","healthcare"); setChatOpen(false); });
    } else if (/wedding|pandit|priest|mehndi|decorator|photographer|videographer|dj|caterer|wedding planner/.test(m)) {
      text = "Found verified wedding service professionals.";
      go("Show Wedding Services →", ()=>{ nav("wedding"); setChatOpen(false); });
    } else if (/nanny|babysit|child care|elder care|cook|housekeeper|daycare|senior care|caregiver/.test(m)) {
      text = "Found trusted care service providers.";
      go("Show Care Services →", ()=>{ nav("care"); setChatOpen(false); });
    } else if (/tutor|sat|mcat|school|class|lesson|music|dance|bharatanatyam|carnatic|hindustani|education/.test(m)) {
      text = "Found education and tutoring professionals.";
      go("Show Education & Tutors →", ()=>{ nav("education"); setChatOpen(false); });
    } else if (/it training|java|data science|aws|cloud|salesforce|opt placement|cpt|tech job|placement/.test(m)) {
      text = "Found IT training and job placement services.";
      go("Show IT Training & Jobs →", ()=>{ nav("ittraining"); setChatOpen(false); });
    } else if (/astrologer|astrology|kundali|vastu|horoscope|pandit|pooja|numerology|tarot/.test(m)) {
      text = "Found Vedic astrology and spiritual service providers.";
      go("Show Astrology & Spiritual →", ()=>{ nav("astrology"); setChatOpen(false); });
    } else if (/room|roommate|rent|pg|paying guest|apartment|housing|accommodation|flat/.test(m)) {
      text = "Found roommate and rental listings from the Desi community.";
      go("Show Roommates & Rentals →", ()=>{ nav("roommates"); setChatOpen(false); });
    } else if (/business|llc|startup|incorporation|entrepreneur|e-2|eid|hr|payroll|marketing/.test(m)) {
      text = "Found business service professionals.";
      go("Show Business Services →", ()=>{ goSearch("","business"); setChatOpen(false); });
    } else if (/india|nri|oci|pio|property in india|lawyer india|remittance|india tax/.test(m)) {
      text = "Found NRI-focused India services.";
      go("Show India Services →", ()=>{ nav("india-services"); setChatOpen(false); });
    } else if (/post.*need|need help|get quote|request|someone.*contact me/.test(m)) {
      text = "You can describe your need and have verified professionals reach out to you directly.";
      go("Post a Need →", ()=>{ nav("post-need"); setChatOpen(false); });
    } else if (/save|saved|bookmark|favourite/.test(m)) {
      text = "Here are the professionals you've saved.";
      go("My Saved Professionals →", ()=>{ user?nav("saved"):nav("auth"); setChatOpen(false); });
    } else if (/login|sign in|log in|my account/.test(m)) {
      text = "I'll take you to the login page.";
      go("Go to Login →", ()=>{ setAuthMode("login"); nav("auth"); setChatOpen(false); });
    } else if (/sign up|register|join|create account/.test(m)) {
      text = "Let's get you set up.";
      go("Create Account →", ()=>{ setAuthMode("signup"); setAuthDirect(null); nav("auth"); setChatOpen(false); });
    } else if (/list.*service|join.*professional|get verified|become.*provider|add.*profile/.test(m)) {
      text = "I can take you to the professional sign-up flow.";
      go("Join as Professional →", ()=>{ setAuthMode("signup"); setUType("provider"); setAuthDirect(null); nav("auth"); setChatOpen(false); });
    } else if (/dashboard|my profile|my account/.test(m)) {
      text = user ? "Here's your dashboard." : "You need to be logged in first.";
      go(user?"Go to Dashboard →":"Login First →", ()=>{ user?nav(user.type==="provider"?"provider-dash":"consumer-dash"):nav("auth"); setChatOpen(false); });
    } else if (/how.*work|about|what is certifieddesi|certifieddesi/.test(m)) {
      text = "Let me show you how CertifiedDESI works.";
      go("How It Works →", ()=>{ nav("how-it-works"); setChatOpen(false); });
    } else if (/safe|trust|verif|background|license|30.point|guarantee/.test(m)) {
      text = "Our 30-point verification is the most rigorous in the industry.";
      go("View Safety & Trust →", ()=>{ nav("safety"); setChatOpen(false); });
    } else if (/success|story|stories|testimonial|review/.test(m)) {
      text = "Read real stories from the Desi community.";
      go("Success Stories →", ()=>{ nav("success-stories"); setChatOpen(false); });
    } else if (/help|faq|support|contact|question/.test(m)) {
      text = "I'll take you to the Help Center.";
      go("Help Center →", ()=>{ nav("help"); setChatOpen(false); });
    } else if (/home|homepage|main page|back|start/.test(m)) {
      text = "Taking you back to the homepage.";
      go("Go Home →", ()=>{ nav("landing"); setChatOpen(false); });
    } else if (/browse|all professional|everyone|list|search/.test(m)) {
      text = "Here are all verified professionals.";
      go("Browse All Professionals →", ()=>{ goSearch("",""); setChatOpen(false); });
    } else if (/hello|hi|hey|namaste|namaskar|hola|howdy/.test(m)) {
      text = "Namaste! 🙏 Tell me what you're looking for – a CPA, immigration lawyer, doctor, or anything else – and I'll take you straight there.";
    } else {
      text = "I can navigate you anywhere on CertifiedDESI. Try: 'I need a CPA', 'find immigration lawyer', 'wedding pandit', 'post a need', or 'how it works'.";
    }

    setTimeout(()=>setChatHist(h=>[...h,{from:"ai",text,action}]),500);
  };

  const sendChatFromInput = () => {
    const el = document.getElementById("chat-input");
    if (!el || !el.value.trim()) return;
    sendChat(el.value.trim());
    el.value = "";
    // Scroll chat to bottom, not page top
    setTimeout(()=>{
      const bottom = document.getElementById("chat-bottom");
      if(bottom) bottom.scrollIntoView({behavior:"smooth", block:"nearest"});
    }, 100);
  };

  // Smart search: keyword synonyms + weighted scoring
  const SEARCH_SYNONYMS = {
    "h1b":["immigration","visa","h-1b","work permit"],
    "green card":["immigration","eb2","eb3","permanent resident"],
    "cpa":["tax","accounting","accountant"],
    "nri":["tax","financial","banking","india"],
    "fbar":["tax","nri","foreign account"],
    "fatca":["tax","nri","foreign"],
    "cfp":["financial","investment","advisor","wealth"],
    "llc":["business","incorporation","startup"],
    "vastu":["realestate","real estate","home"],
    "mortgage":["realestate","financial","home loan"],
    "immigration":["immigration","visa","h1b","green card"],
    "lawyer":["legal","immigration","attorney"],
    "attorney":["legal","immigration","lawyer"],
    "doctor":["healthcare","physician","medical"],
    "dentist":["healthcare","dental"],
    "therapist":["healthcare","mental health"],
  };
  const scoreProvider = (p, q) => {
    if (!q) return 1;
    const qLow = q.toLowerCase();
    const words = qLow.split(" ").filter(w=>w.length>0);
    let score = 0;
    const fields = [
      p.name.toLowerCase(), p.sub.toLowerCase(), p.bio.toLowerCase(),
      p.city.toLowerCase(), p.cat.toLowerCase(), ...p.lang.map(l=>l.toLowerCase()),
      ...p.creds.map(c=>c.toLowerCase())
    ].join(" ");
    // Exact phrase match
    if (fields.includes(qLow)) score += 10;
    // Each word match
    words.forEach(w => { if (fields.includes(w)) score += 3; });
    // Synonym expansion
    Object.entries(SEARCH_SYNONYMS).forEach(([syn, expansions]) => {
      if (qLow.includes(syn) || words.some(w=>syn.includes(w))) {
        expansions.forEach(ex => { if (p.cat.includes(ex) || fields.includes(ex)) score += 2; });
      }
    });
    // City match bonus
    if (words.some(w => p.city.toLowerCase().includes(w))) score += 4;
    // Language match bonus
    if (p.lang.some(l => words.some(w => l.toLowerCase().includes(w)))) score += 4;
    return score;
  };
  // City-aware filtering: match city first; if <2 results fallback to all
  const cityToken = (userCity && userCity !== "All Cities")
    ? userCity.split(",")[0].toLowerCase() : null;
  const baseFilter = p => {
    const q = searchQ.toLowerCase();
    const score = scoreProvider(p, q);
    return (q === "" || score > 0)
      && (!searchCat || p.cat===searchCat)
      && (!filterBadge || p.badge===filterBadge)
      && (!filterLang || p.lang.includes(filterLang));
  };
  const sortFn = (a,b) => {
    // City-priority: bump local results to top
    if (cityToken) {
      const aLocal = a.city.toLowerCase().includes(cityToken);
      const bLocal = b.city.toLowerCase().includes(cityToken);
      if (aLocal && !bLocal) return -1;
      if (!aLocal && bLocal) return 1;
    }
    if (searchQ) {
      const diff = scoreProvider(b,searchQ) - scoreProvider(a,searchQ);
      if (diff !== 0) return diff;
    }
    if(sortBy==="rating")  return b.rating-a.rating;
    if(sortBy==="reviews") return b.reviews-a.reviews;
    if(sortBy==="newest")  return b.id-a.id;
    return b.score-a.score;
  };
  const cityFiltered = cityToken
    ? PROVIDERS.filter(p => baseFilter(p) && p.city.toLowerCase().includes(cityToken))
    : [];
  // If city is selected and has results – show city-only; if no results – fallback to all
  const filtered = cityToken && cityFiltered.length > 0
    ? [...cityFiltered].sort(sortFn)
    : PROVIDERS.filter(baseFilter).sort(sortFn);

  // ── SHARED ────────────────────────────────────────
  const S = isDark ? {
    cream:    "#1C1B19",
    creamMid: "#2E2C28",
    creamDark:"#161514",
    charcoal: "#F2EDE4",
    charMid:  "#3E3C38",
    charLight:"#C8C4BC",
    terra:    "#E8714A",
    gold:     "#D4AA3A",
    muted:    "#8A8578",
    mutedLt:  "#A8A298",
  } : {
    cream:    "#F2EDE4",
    creamMid: "#F8F4EE",
    creamDark:"#EDE7DC",
    charcoal: "#1C1B19",
    charMid:  "#2E2C28",
    charLight:"#4A4740",
    terra:    "#C4522A",
    gold:     "#C49A2A",
    muted:    "#8A8578",
    mutedLt:  "#A8A298",
  };

  const T = (styles) => ({ style: styles });

  const BadgePill = ({badge, score}) => {
    const b = BADGE[badge] || BADGE.listed;
    return (
      <span style={{background:b.bg, color:b.tx, display:"inline-flex", alignItems:"center", gap:4,
        fontSize:"0.68rem", fontWeight:700, padding:"3px 10px", borderRadius:99, letterSpacing:"0.03em"}}>
        {b.icon} {b.label} · {score}/30
      </span>
    );
  };

  const Stars = ({n}) => (
    <span style={{display:"flex", gap:2}}>
      {[1,2,3,4,5].map(i => (
        <Star key={i} size={11} style={{color:i<=n?"#C49A2A":"#DDD8CE", fill:i<=n?"#C49A2A":"none"}}/>
      ))}
    </span>
  );

  const SectionHead = ({label, title, sub, light=false}) => (
    <div style={{textAlign:"center", marginBottom:40}}>
      {label && <div style={{display:"inline-flex", alignItems:"center", gap:6, background:light?"#C4522A22":"#C4522A11",
        color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase",
        padding:"5px 14px", borderRadius:99, marginBottom:12}}>
        {label}
      </div>}
      <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(1.8rem,4vw,2.6rem)", fontWeight:700,
        color:light?S.cream:S.charcoal, lineHeight:1.1, marginBottom:10}}>
        {title}
      </h2>
      {sub && <p style={{color:light?S.mutedLt:S.muted, fontSize:"0.85rem", maxWidth:560, margin:"0 auto", lineHeight:1.7}}>{sub}</p>}
    </div>
  );

  const ProvCard = ({p}) => (
    <div className="cd-card cd-prov-card" onClick={()=>{
    setSelProv(p);setProfTab("credentials");setReqSent(false);setProvFromCat(p.cat);
    const slug=p.name.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
    setPage("profile");
    window.history.pushState({page:"profile",slug,id:p.id},""," /profile/"+slug);
  }} role="button" tabIndex={0} onKeyDown={e=>e.key==="Enter"&&(()=>{
    setSelProv(p);setProfTab("credentials");setReqSent(false);setProvFromCat(p.cat);
    const slug=p.name.toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");
    setPage("profile");window.history.pushState({page:"profile",slug,id:p.id},""," /profile/"+slug);
  })()}
      style={{padding:20, cursor:"pointer"}}>
      <div style={{display:"flex", gap:14}}>
        <div style={{width:56, height:56, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem", flexShrink:0}}>
          {p.img}
        </div>
        <div style={{flex:1, minWidth:0}}>
          <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.05rem", color:S.charcoal}}>{p.name}</h3>
          <p style={{color:S.terra, fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginTop:2}}>{p.sub}</p>
          <div style={{display:"flex", alignItems:"center", gap:6, marginTop:4}}>
            <Stars n={Math.round(p.rating)}/><span style={{fontWeight:700, fontSize:"0.78rem"}}>{p.rating}</span>
            <span style={{color:S.muted, fontSize:"0.7rem"}}>({p.reviews})</span>
          </div>
          <div style={{display:"flex", alignItems:"center", gap:4, color:S.muted, fontSize:"0.7rem", marginTop:2}}>
            <MapPin size={9}/>{p.city}
          </div>
          <p style={{color:S.charLight, fontSize:"0.72rem", lineHeight:1.5, marginTop:6,
            overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical"}}>{p.bio}</p>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10, flexWrap:"wrap", gap:6}}>
            <BadgePill badge={p.badge} score={p.score}/>
            <span style={{color:S.gold, fontSize:"0.72rem", fontWeight:700}}>{p.fee}</span>
          </div>
          <div style={{display:"flex", flexWrap:"wrap", gap:4, marginTop:6}}>
            {p.lang.map(l => (
              <span key={l} style={{background:S.creamDark, color:S.charLight, fontSize:"0.63rem", padding:"2px 8px", borderRadius:99}}>{l}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );



  // ── TRUST BAR: auto-scroll, pause on hover, drag to scroll ──
  const TrustBar = () => {
    const scrollRef = useRef(null);
    const marqRef  = useRef(null);
    const dragState = useRef({dragging:false, startX:0, scrollLeft:0, animOffset:0});

    const ITEMS = [
      ["🔐","30-Point Verification"],["🏛️","License DB Verified"],["🔍","Background Checked"],
      ["⭐","Community Reviewed"],["🔄","Annually Re-verified"],["🛡️","E&O Insurance Checked"],
      ["🤖","AI-Monitored Nightly"],["📋","Verified Reviews Only"],["⚖️","Licensed Attorneys"],
      ["💼","Credentialed CPAs"],["🏠","Certified Agents"],["🩺","Board-Certified Doctors"],
      ["🔏","Identity Verified"],["📞","References Checked"],["🏆","Elite Badge System"],["🤝","Dispute Resolution"],
    ];
    // Triple-duplicate so dragging never reveals an end
    const ALL = [...ITEMS, ...ITEMS, ...ITEMS];

    const pause  = () => marqRef.current && marqRef.current.classList.add("paused");
    const resume = () => {
      if (!dragState.current.dragging && marqRef.current)
        marqRef.current.classList.remove("paused");
    };

    const onMouseDown = (e) => {
      const el = scrollRef.current;
      if (!el) return;
      pause(); // pause only while actively dragging, not on hover
      el.classList.add("dragging");
      dragState.current = { dragging:true, startX: e.pageX, scrollLeft: el.scrollLeft };
      // Attach to document so drag continues outside the element
      const move = (ev) => {
        if (!dragState.current.dragging) return;
        ev.preventDefault();
        scrollRef.current.scrollLeft = dragState.current.scrollLeft - (ev.pageX - dragState.current.startX);
      };
      const up = () => {
        dragState.current.dragging = false;
        if (scrollRef.current) scrollRef.current.classList.remove("dragging");
        resume();
        document.removeEventListener("mousemove", move);
        document.removeEventListener("mouseup", up);
      };
      document.addEventListener("mousemove", move);
      document.addEventListener("mouseup", up);
    };
    const onMouseUp = () => {};  // handled by document listener above

    return (
      <section style={{background:"#2E2C28", borderBottom:"1px solid #3E3C38", padding:"10px 0", userSelect:"none"}}>
        <div
          ref={scrollRef}
          className="trust-scroll"
          style={{overflowX:"auto", scrollbarWidth:"none"}}
          onMouseDown={onMouseDown}
        >
          <div ref={marqRef} className="trust-marquee" style={{fontSize:"0.72rem", color:"#8A8578", fontWeight:500}}>
            {ALL.map(([ic,lb],i)=>(
              <span key={i} style={{display:"flex", alignItems:"center", gap:6, whiteSpace:"nowrap", padding:"2px 0"}}>
                <span style={{color:"#C4522A"}}>{ic}</span> {lb}
                <span style={{color:"#3E3C38", margin:"0 4px"}}>·</span>
              </span>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // ── AVATAR MENU – proper component (hooks can't be in IIFE) ────
  const AvatarMenu = () => {
    const [open, setOpen] = useState(false);
    if (!user) return null;
    const firstName = user.name.split(" ")[0];
    const initial   = firstName[0].toUpperCase();
    return (
      <div style={{position:"relative"}}>
        {/* Avatar circle – shows first initial */}
        <div onClick={()=>setOpen(o=>!o)} className="cd-avatar-btn" title={"Hello, "+user.name}
          style={{width:34,height:34,borderRadius:"50%",background:S.terra,color:S.cream,
            fontWeight:700,fontSize:"0.88rem",cursor:"pointer",display:"flex",alignItems:"center",
            justifyContent:"center",flexShrink:0,userSelect:"none",letterSpacing:0}}>
          {initial}
        </div>
        {open && (
          <>
            {/* Click-outside backdrop – above everything */}
            <div onClick={()=>setOpen(false)} style={{position:"fixed",inset:0,zIndex:200}}/>
            {/* Dropdown – fixed so it escapes sticky header stacking context */}
            <div style={{position:"fixed",right:"max(16px, calc(50vw - 608px))",top:66,
              background:S.creamMid,border:`1px solid #DDD8CE`,borderRadius:14,
              boxShadow:"0 8px 32px rgba(0,0,0,0.2)",zIndex:201,minWidth:220,overflow:"hidden"}}>
              {/* Greeting */}
              <div style={{padding:"14px 16px",borderBottom:`1px solid #DDD8CE`,background:S.creamDark}}>
                <p style={{fontSize:"0.68rem",color:S.muted,marginBottom:2}}>Hello,</p>
                <p style={{fontWeight:700,fontSize:"0.9rem",color:S.charcoal}}>{user.name}</p>
                <p style={{fontSize:"0.65rem",color:S.muted,marginTop:2,textTransform:"capitalize"}}>
                  {user.type==="provider"?"Professional":"Member"} account
                </p>
              </div>
              {/* Menu items */}
              {[
                ["🏠","My Dashboard",()=>nav(user.type==="provider"?"provider-dash":"consumer-dash")],
                ["🔖","Saved Professionals",()=>nav("saved")],
                ["📋","My Posts & Requests",()=>nav("post-need")],
                ["🛡️","Safety & Trust",()=>nav("safety")],
              ].map(([ic,label,fn])=>(
                <div key={label} onClick={()=>{fn();setOpen(false);}}
                  style={{padding:"11px 16px",display:"flex",alignItems:"center",gap:10,
                    cursor:"pointer",fontSize:"0.78rem",color:S.charcoal,transition:"background 0.12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background=S.creamDark}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <span style={{fontSize:"1rem"}}>{ic}</span>{label}
                </div>
              ))}
              <div style={{borderTop:`1px solid #DDD8CE`}}>
                <div onClick={()=>{logout();setOpen(false);}}
                  style={{padding:"11px 16px",display:"flex",alignItems:"center",gap:10,
                    cursor:"pointer",fontSize:"0.78rem",color:S.terra,fontWeight:600,
                    transition:"background 0.12s"}}
                  onMouseEnter={e=>e.currentTarget.style.background="#C4522A11"}
                  onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                  <LogOut size={13}/> Sign Out
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const Header = () => {
    const hSearchId = "header-search-input";
    // Local state – fully independent from hero and menu bar
    const [hCat, setHCat] = useState("");
    const doHeaderSearch = () => {
      const q   = document.getElementById(hSearchId)?.value||"";
      const cat = document.getElementById("header-search-cat")?.value || hCat;
      // Ensure we always pass the current userCity from closure
      goSearch(q, cat, userCity);
      const el = document.getElementById(hSearchId);
      if(el) el.value = "";
    };
    return (
      <>
      {/* ── ROW 1: Logo + Search + Location + Login ─────────────── */}
      <header className="cd-header-bar1">
        <div style={{maxWidth:1280, margin:"0 auto", padding:"0 24px", height:58, display:"flex", alignItems:"center", gap:14, overflow:"hidden"}}>

          {/* LOGO */}
          <div style={{display:"flex", alignItems:"center", gap:9, cursor:"pointer", flexShrink:0, marginRight:8}}
            onClick={()=>nav("landing")} role="button" tabIndex={0} onKeyDown={e=>e.key==="Enter"&&nav("landing")}>
            <div style={{width:32, height:32, background:S.terra, borderRadius:7, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
              <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontStyle:"italic", fontSize:"0.9rem"}}>CD</span>
            </div>
            <span style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.15rem", color:S.cream, lineHeight:1}} className="sm-show">
              Certified<span style={{color:S.terra}}>DESI</span>
            </span>
          </div>

          {/* LOCATION BUTTON – opens modal */}
          <button className="cd-location-btn" onClick={()=>setLocationModal(true)}
            title="Change your location">
            <MapPin size={12} style={{color:S.terra, flexShrink:0}}/>
            <span className="cd-location-btn-text">
              {userCity === "All Cities" ? "Location" : userCity.split(",")[0]}
            </span>
            <span style={{fontSize:"0.58rem", color:"#6A6660", lineHeight:1}}>▾</span>
          </button>

          {/* SEARCH BAR – dominant center, flex-grows/shrinks freely */}
          <div className={`cd-header-search ${heroVisible?"hero-active":"hero-hidden"}`} style={{flex:"1 1 0", minWidth:0}}>
            {/* Cat wrapper – chevron + auto-width */}
            <div className="cd-header-search-cat-wrap">
              <select id="header-search-cat" className="cd-header-search-cat"
                value={hCat}
                onChange={e=>setHCat(e.target.value)}>
                <option value="">All Services</option>
                {CATS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <span className="cd-search-cat-chevron">▾</span>
            </div>
            <input id={hSearchId} className="cd-header-search-input"
              placeholder="Search professionals, services, languages..."
              onKeyDown={e=>{ if(e.key==="Enter"){e.preventDefault();doHeaderSearch();}}}/>
            <button className="cd-header-search-btn" onClick={doHeaderSearch} aria-label="Search">
              <Search size={15} style={{color:"#F2EDE4"}}/>
            </button>
          </div>

          {/* RIGHT – Safety · Login/Avatar (desktop) */}
          <div style={{display:"flex", alignItems:"center", gap:12, flexShrink:0, whiteSpace:"nowrap"}} className="desk-nav">
            <span onClick={()=>nav("how-it-works")} className="cd-nav-link" style={{fontSize:"0.72rem", whiteSpace:"nowrap"}}>How It Works</span>
            <span onClick={()=>nav("safety")} className="cd-nav-link" style={{fontSize:"0.72rem", whiteSpace:"nowrap"}}>Safety</span>

            {/* Dark / Light mode toggle */}
            <button
              onClick={toggleTheme}
              aria-label={isDark ? "Switch to light theme" : "Switch to dark theme"}
              title={isDark ? "Switch to light theme" : "Switch to dark theme"}
              style={{
                background: isDark ? "#2E2C28" : "#F2EDE4",
                border: `1px solid ${isDark ? "#3E3C38" : "#DDD8CE"}`,
                cursor:"pointer",
                color: isDark ? "#F2EDE4" : "#1C1B19",
                fontSize:"0.78rem",
                fontWeight:600,
                padding:"5px 12px",
                borderRadius:20,
                display:"flex", alignItems:"center", gap:5,
                transition:"all 0.2s",
                fontFamily:"inherit",
                letterSpacing:"0.01em",
                whiteSpace:"nowrap",
              }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor="#C4522A";}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor=isDark?"#3E3C38":"#DDD8CE";}}>
              {isDark ? "☀️ Light" : "🌙 Dark"}
            </button>

            {!user ? (
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <button onClick={()=>{setAuthMode("login");setEmailStep(0);nav("auth");}}
                  className="cd-btn-dark" style={{padding:"7px 16px",fontSize:"0.78rem",border:"1px solid #3E3C38",borderRadius:7,background:"transparent",color:"#C8C2BA",cursor:"pointer",fontFamily:"inherit",transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor="#C49A2A";e.currentTarget.style.color="#F2EDE4";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="#3E3C38";e.currentTarget.style.color="#C8C2BA";}}>
                  Sign In
                </button>
                <button onClick={()=>{setAuthMode("signup");setAuthDirect(null);setEmailStep(0);nav("auth");}}
                  className="cd-btn-primary" style={{padding:"7px 18px",fontSize:"0.78rem"}}>
                  Join Free →
                </button>
              </div>
            ) : (
              <AvatarMenu/>
            )}
          </div>

          {/* HAMBURGER – mobile only */}
          <button className="mob-btn" onClick={()=>setMobileMenu(!mobileMenu)}
            style={{background:"none", border:"none", cursor:"pointer", display:"none", flexShrink:0, marginLeft:"auto"}}>
            <Menu size={20} style={{color:S.mutedLt}}/>
          </button>
        </div>
      </header>

      {/* ── ROW 2: Category strip + Post a Need CTA ─────────────── */}
      <div className="cd-header-bar2" style={{display:"none"}} id="header-bar2">
        <div style={{maxWidth:1280, margin:"0 auto", padding:"0 24px", width:"100%", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          {/* Left: draggable category strip */}
          <div className="cd-cat-strip-wrap">
            <div className="cd-cat-strip" ref={el=>{
              if(!el) return;
              // Per-frame delta drag – fixes over-shoot on touch devices
              let dragging=false, lastX=0;
              // Mouse drag
              const onMove=e=>{
                if(!dragging)return;
                e.preventDefault();
                const dx = lastX - e.pageX;
                lastX = e.pageX;
                el.scrollLeft += dx;
              };
              const onUp=()=>{
                if(!dragging)return;
                dragging=false;
                el.classList.remove("grabbing");
                document.removeEventListener("mousemove",onMove);
                document.removeEventListener("mouseup",onUp);
              };
              el.onmousedown=e=>{
                dragging=true; lastX=e.pageX;
                el.classList.add("grabbing");
                document.addEventListener("mousemove",onMove);
                document.addEventListener("mouseup",onUp);
              };
              // Touch drag – one category width ≈ 130px, swipe responds immediately
              let touchLast=0;
              el.ontouchstart=e=>{
                touchLast=e.touches[0].pageX;
                dragging=true;
              };
              el.ontouchmove=e=>{
                if(!dragging)return;
                const dx = touchLast - e.touches[0].pageX;
                touchLast = e.touches[0].pageX;
                el.scrollLeft += dx;
              };
              el.ontouchend=()=>{ dragging=false; };
            }}>
              <button onClick={()=>setMenuOpen(o=>!o)} className="cd-cat-link"
                style={{fontWeight:700,gap:5,color:menuOpen?"#F2EDE4":"#A8A298",background:menuOpen?"#3E3C38":"transparent"}}>
                <Menu size={12}/> All
              </button>
              {CATS.map(cat=>(
                <button key={cat.id} onClick={()=>goSearch("",cat.id)}
                  className={`cd-cat-link ${searchCat===cat.id?"active":""}`}>
                  {cat.icon} {cat.label}
                </button>
              ))}
              <button onClick={()=>nav("india-services")} className="cd-cat-link" style={{color:"#C49A2A",borderLeft:"1px solid #3E3C38"}}>
                🇮🇳 India Services
              </button>
            </div>
          </div>
          {/* Right: Post a Need */}
          <button onClick={()=>nav("post-need")}
            style={{background:S.terra, color:S.cream, border:"none", borderRadius:6, padding:"7px 16px",
              fontSize:"0.75rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:6,
              flexShrink:0, transition:"background 0.15s", whiteSpace:"nowrap"}}
            className="cd-btn-primary">
            📋 Post a Need
          </button>
        </div>
      </div>

      {/* Overlay + panel rendered at app root – see return() below */}
      {/* (mega menu panel rendered at app root) */}

            {/* ── LOCATION MODAL ──────────────────────────────────────── */}
      {locationModal && (
        <div
          style={{
            position:"fixed", inset:0, background:"rgba(0,0,0,0.65)",
            zIndex:202, display:"flex", alignItems:"center", justifyContent:"center",
            padding:16, backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)",
            overflowY:"hidden", touchAction:"none"
          }}
          onClick={e=>{if(e.target===e.currentTarget)setLocationModal(false);}}
          onTouchMove={e=>e.preventDefault()}
          onWheel={e=>e.preventDefault()}>
          <div style={{
            background:S.creamMid, borderRadius:16, padding:28,
            width:"100%", maxWidth:400, boxShadow:"0 20px 60px rgba(0,0,0,0.4)",
            border:`1px solid #DDD8CE`
          }}>
            {/* Header */}
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
              <div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.4rem", color:S.charcoal}}>
                  Set Your Location
                </h3>
                <p style={{fontSize:"0.72rem", color:S.muted, marginTop:3}}>
                  We'll show you professionals near you first
                </p>
              </div>
              <button onClick={()=>setLocationModal(false)}
                style={{background:"none",border:"none",color:S.muted,cursor:"pointer",fontSize:"1.1rem",lineHeight:1,padding:4}}>
                ✕
              </button>
            </div>

            {/* Option 1: Detect my location */}
            <button
              onClick={()=>{
                setLocating(true);
                if(!navigator.geolocation){ showToast("Geolocation not supported","error"); setLocating(false); return; }
                navigator.geolocation.getCurrentPosition(
                  (pos)=>{
                    const lat=pos.coords.latitude, lng=pos.coords.longitude;
                    // Map GPS to nearest Desi community city hub
                    const COORDS={
                      "San Jose, CA":[37.34,-121.89],"Fremont, CA":[37.55,-121.98],
                      "New York, NY":[40.71,-74.01],"New Jersey, NJ":[40.07,-74.41],
                      "Chicago, IL":[41.88,-87.63],"Houston, TX":[29.76,-95.37],
                      "Dallas, TX":[32.78,-96.80],"Atlanta, GA":[33.75,-84.39],
                      "Seattle, WA":[47.61,-122.33],"Washington, DC":[38.89,-77.04],
                      "Los Angeles, CA":[34.05,-118.24],"Boston, MA":[42.36,-71.06],
                      "Austin, TX":[30.27,-97.74],"Philadelphia, PA":[39.95,-75.17],
                      "Detroit, MI":[42.33,-83.05]
                    };
                    let nearest="All Cities", minDist=Infinity;
                    Object.entries(COORDS).forEach(([city,[clat,clng]])=>{
                      const d=Math.sqrt((lat-clat)**2+(lng-clng)**2);
                      if(d<minDist){minDist=d;nearest=city;}
                    });
                    setUserCity(nearest);
                    setLocating(false);
                    setLocationModal(false);
                    showToast("Location set to "+nearest+" 📍");
                  },
                  (err)=>{
                    setLocating(false);
                    // err.code: 1=denied, 2=unavailable, 3=timeout
                    const msg = err.code===1
                      ? "Location access denied – please allow it in browser settings, or use ZIP / city below"
                      : "Could not detect location – try ZIP code or select a city below";
                    showToast(msg,"error");
                  },
                  {timeout:8000, maximumAge:60000}
                );
              }}
              style={{
                width:"100%", padding:"12px 16px", borderRadius:10, border:"1px solid #DDD8CE",
                background:S.creamDark, cursor:"pointer", display:"flex", alignItems:"center",
                gap:10, marginBottom:12, transition:"border-color 0.15s, background 0.15s",
                fontFamily:"inherit"
              }}
              className="cd-loc-opt">
              <span style={{fontSize:"1.2rem"}}>📍</span>
              <div style={{textAlign:"left"}}>
                <p style={{fontSize:"0.82rem", fontWeight:600, color:S.charcoal}}>
                  {locating ? "Detecting…" : "Detect My Location"}
                </p>
                <p style={{fontSize:"0.68rem", color:S.muted, marginTop:2}}>Use GPS to find the nearest city</p>
              </div>
              {locating && <span style={{marginLeft:"auto", fontSize:"0.8rem"}}>⏳</span>}
            </button>

            {/* Divider */}
            <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
              <div style={{flex:1, height:1, background:"#DDD8CE"}}/>
              <span style={{fontSize:"0.65rem", color:S.muted, fontWeight:600}}>OR</span>
              <div style={{flex:1, height:1, background:"#DDD8CE"}}/>
            </div>

            {/* Option 2: ZIP code */}
            <div style={{marginBottom:12}}>
              <label style={{display:"block", fontSize:"0.62rem", fontWeight:800, color:S.muted, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:6}}>
                Search by ZIP Code
              </label>
              <div style={{display:"flex", gap:8}}>
                <input
                  className="cd-input"
                  placeholder="e.g. 77001, 10001, 60601"
                  id="loc-zip-input"
                  defaultValue=""
                  onInput={e=>{e.target.value=e.target.value.replace(/[^0-9]/g,"").slice(0,5);}}
                  onKeyDown={e=>{
                    if(e.key==="Enter"){
                      const zip=(e.target.value||"").replace(/\D/g,"").slice(0,5);
                      const city=lookupZip(zip);
                      if(city){setUserCity(city);e.target.value="";setLocationModal(false);showToast("Location set to "+city+" 📍");}
                      else{showToast("ZIP not recognised – try a city below","error");}
                    }
                  }}
                  maxLength={5}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  style={{flex:1, fontSize:"0.85rem"}}/>
                <button
                  onClick={()=>{
                    const zipEl=document.getElementById("loc-zip-input");
                    const zip=(zipEl?.value||"").replace(/\D/g,"").slice(0,5);
                    const city=lookupZip(zip);
                    if(city){setUserCity(city);if(zipEl)zipEl.value="";setLocationModal(false);showToast("Location set to "+city+" 📍");}
                    else{showToast("ZIP not recognised – try selecting a city below","error");}
                  }}
                  className="cd-btn-primary" style={{padding:"0 14px", borderRadius:8, fontSize:"0.78rem"}}>
                  Go
                </button>
              </div>
            </div>

            {/* Divider */}
            <div style={{display:"flex", alignItems:"center", gap:10, marginBottom:12}}>
              <div style={{flex:1, height:1, background:"#DDD8CE"}}/>
              <span style={{fontSize:"0.65rem", color:S.muted, fontWeight:600}}>OR</span>
              <div style={{flex:1, height:1, background:"#DDD8CE"}}/>
            </div>

            {/* Option 3: City list */}
            <div>
              <label style={{display:"block", fontSize:"0.62rem", fontWeight:800, color:S.muted, letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:8}}>
                Choose a City
              </label>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6}}>
                {TOP_CITIES.map(city=>(
                  <button key={city}
                    onClick={()=>{setUserCity(city);setLocationModal(false);showToast(city==="All Cities"?"Showing all cities 🌎":`Location set to ${city} 📍`);}}
                    style={{
                      padding:"8px 10px", borderRadius:8, fontSize:"0.74rem", fontWeight:500,
                      border:`1px solid ${userCity===city?"#C4522A":"#DDD8CE"}`,
                      background:userCity===city?"#C4522A11":S.cream,
                      color:userCity===city?S.terra:S.charcoal,
                      cursor:"pointer", textAlign:"left", fontFamily:"inherit",
                      transition:"all 0.15s"
                    }}>
                    {city==="All Cities" ? "🌎 All Cities" : city}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MOBILE MENU ──────────────────────────────────────────── */}
      {mobileMenu && (
        <div style={{background:"#252320", borderTop:`1px solid #3E3C38`, zIndex:40, position:"sticky", top:58}}>
          <div style={{padding:"12px 16px", maxHeight:"75vh", overflowY:"auto"}}>
            {/* Mobile search */}
            <div style={{display:"flex", background:S.creamMid, borderRadius:8, overflow:"hidden", marginBottom:12, height:40}}>
              <input placeholder="Search professionals..."
                style={{flex:1, border:"none", outline:"none", padding:"0 12px", fontSize:"0.82rem", color:S.charcoal, background:"transparent"}}
                onKeyDown={e=>{if(e.key==="Enter"){goSearch(e.target.value,"");setMobileMenu(false);}}}/>
              <button onClick={e=>{const inp=e.target.closest("div").querySelector("input");goSearch(inp?.value||"","");setMobileMenu(false);}}
                style={{background:S.terra, border:"none", padding:"0 14px", cursor:"pointer"}}>
                <Search size={14} style={{color:S.cream}}/>
              </button>
            </div>
            {/* Location */}
            <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:10, padding:"6px 0", borderBottom:`1px solid #3E3C38`}}>
              <MapPin size={12} style={{color:S.terra}}/>
              <select value={userCity} onChange={e=>setUserCity(e.target.value)}
                style={{background:"transparent", border:"none", color:S.mutedLt, fontSize:"0.82rem", outline:"none", fontFamily:"inherit", flex:1}}>
                {TOP_CITIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {/* Nav */}
            {[["📋 Post a Need",()=>nav("post-need")],["🔍 Find Professionals",()=>goSearch("","")],["ℹ️ How It Works",()=>nav("how-it-works")],["🛡️ Safety & Trust",()=>nav("safety")]].map(([label,fn])=>(
              <div key={label} onClick={()=>{fn();setMobileMenu(false);}} style={{color:S.mutedLt, padding:"11px 0", cursor:"pointer", borderBottom:`1px solid #3E3C38`, fontSize:"0.85rem"}}>{label}</div>
            ))}
            {/* Categories grid */}
            <p style={{color:"#6A6660", fontSize:"0.6rem", fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", padding:"10px 0 6px"}}>All Categories</p>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, marginBottom:10}}>
              {CATS.map(c=>(
                <div key={c.id} onClick={()=>{goSearch("",c.id);setMobileMenu(false);}}
                  style={{color:S.mutedLt, padding:"9px 10px", cursor:"pointer", fontSize:"0.78rem", background:"#2E2C28", borderRadius:6, display:"flex", alignItems:"center", gap:6}}>
                  <span>{c.icon}</span><span style={{overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{c.label}</span>
                </div>
              ))}
              <div onClick={()=>{nav("india-services");setMobileMenu(false);}}
                style={{color:"#C49A2A", padding:"9px 10px", cursor:"pointer", fontSize:"0.78rem", background:"#2E2C28", borderRadius:6, display:"flex", alignItems:"center", gap:6}}>
                🇮🇳 India Services
              </div>
              <div onClick={()=>{nav("roommates");setMobileMenu(false);}}
                style={{color:"#F57C00", padding:"9px 10px", cursor:"pointer", fontSize:"0.78rem", background:"#2E2C28", borderRadius:6, display:"flex", alignItems:"center", gap:6}}>
                🛋️ Roommates
              </div>
            </div>
            {/* Auth */}
            {!user ? (
              <button onClick={()=>{nav("auth");setMobileMenu(false);}} className="cd-btn-primary" style={{width:"100%", marginTop:8, padding:"11px"}}>Login</button>
            ) : (
              <div style={{borderTop:`1px solid #3E3C38`, marginTop:4}}>
                <div onClick={()=>{nav(user.type==="provider"?"provider-dash":"consumer-dash");setMobileMenu(false);}} style={{color:S.mutedLt, padding:"11px 0", cursor:"pointer", fontSize:"0.85rem"}}>Dashboard</div>
                <div onClick={()=>{logout();setMobileMenu(false);}} style={{color:S.terra, padding:"11px 0", cursor:"pointer", fontSize:"0.85rem"}}>Logout</div>
              </div>
            )}
          </div>
        </div>
      )}
      </>
    );
  };

  const Toast = () => toast ? (
    <div role="alert" aria-live="polite" aria-atomic="true" style={{position:"fixed", top:20, right:20, zIndex:50, background:toast.type==="error"?S.terra:S.charcoal,
      color:S.cream, padding:"12px 20px", borderRadius:8, fontSize:"0.8rem", fontWeight:600,
      display:"flex", alignItems:"center", gap:8, boxShadow:"0 8px 32px rgba(0,0,0,0.25)"}}>
      <CheckCircle size={14}/> {toast.msg}
    </div>
  ) : null;

  const ChatWidget = () => (
    <div style={{position:"fixed", bottom:20, right:20, zIndex:50}}>
      {chatOpen && (
        <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16,
          boxShadow:"0 12px 48px rgba(0,0,0,0.15)", width:300, marginBottom:12, overflow:"hidden"}}>
          {/* Header */}
          <div style={{background:S.charcoal, color:S.cream, padding:"12px 16px", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", alignItems:"center", gap:10}}>
              <div style={{width:32, height:32, background:S.terra, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", fontWeight:700, fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic"}}>M</div>
              <div>
                <p style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1rem"}}>Milo</p>
                <p style={{color:"#C49A2A", fontSize:"0.6rem", fontWeight:600, letterSpacing:"0.04em"}}>Your CertifiedDESI Guide</p>
              </div>
            </div>
            <button onClick={()=>setChatOpen(false)} style={{background:"none", border:"none", color:S.muted, cursor:"pointer"}}><X size={14}/></button>
          </div>
          {/* Messages */}
          <div id="chat-messages" style={{height:240, overflowY:"auto", padding:12, display:"flex", flexDirection:"column", gap:10, background:S.cream}}>
            {chatHist.map((m,i)=>(
              <div key={i} style={{display:"flex", flexDirection:"column", alignItems:m.from==="user"?"flex-end":"flex-start", gap:6}}>
                <div style={{maxWidth:220, padding:"9px 13px", borderRadius:12,
                  borderBottomRightRadius:m.from==="user"?2:12,
                  borderBottomLeftRadius:m.from==="user"?12:2,
                  fontSize:"0.74rem", lineHeight:1.55,
                  background:m.from==="user"?S.terra:S.creamMid,
                  color:m.from==="user"?S.cream:S.charcoal,
                  border:m.from==="user"?"none":`1px solid #DDD8CE`}}>
                  {m.text}
                </div>
                {/* Action button – "Take me there" style */}
                {m.action && (
                  <button onClick={m.action.fn}
                    style={{background:S.terra, color:S.cream, border:"none", borderRadius:8,
                      padding:"7px 14px", fontSize:"0.72rem", fontWeight:700, cursor:"pointer",
                      display:"flex", alignItems:"center", gap:5, transition:"background 0.15s",
                      boxShadow:"0 2px 8px rgba(196,82,42,0.3)"}}>
                    {m.action.label}
                  </button>
                )}
              </div>
            ))}
            <div id="chat-bottom"/>
          </div>
          {/* Input */}
          <div style={{borderTop:`1px solid #DDD8CE`, padding:"10px 12px", display:"flex", gap:8, background:S.creamMid}}>
            <input
              id="chat-input"
              className="cd-input"
              style={{flex:1, fontSize:"0.74rem", padding:"8px 10px"}}
              placeholder='e.g. "I need a CPA in Houston"'
              onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();sendChatFromInput();}}}
            />
            <button onClick={sendChatFromInput} className="cd-btn-primary" style={{padding:"8px 13px", fontSize:"0.78rem", borderRadius:8}}>→</button>
          </div>
        </div>
      )}
      {/* Launcher */}
      <button onClick={()=>setChatOpen(!chatOpen)}
        style={{background:S.terra, color:S.cream, width:50, height:50, borderRadius:"50%", border:"none",
          cursor:"pointer", boxShadow:"0 4px 20px rgba(196,82,42,0.45)",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.1rem", fontStyle:"italic",
          transition:"transform 0.2s, box-shadow 0.2s"}}>
        {chatOpen ? "✕" : "M"}
      </button>
    </div>
  );

  const Footer = () => (
    <footer style={{background:"#0F0E0C", color:"#8A8578", marginTop:60}}>
      {/* Back to top */}
      <div style={{background:"#1C1B19", borderBottom:"1px solid #2E2C28", textAlign:"center", padding:"12px", cursor:"pointer"}}
        onClick={()=>window.scrollTo({top:0,behavior:"smooth"})}>
        <span style={{color:"#A8A298", fontSize:"0.78rem"}}>↑ Back to top</span>
      </div>

      {/* Main footer grid – Amazon-style value columns */}
      <div style={{maxWidth:1200, margin:"0 auto", padding:"48px 32px 40px"}}>
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:36, marginBottom:48}}>
          {/* Column 1: Find a Professional */}
          <div>
            <p style={{color:"#F2EDE4", fontWeight:700, fontSize:"0.85rem", marginBottom:16}}>Find a Professional</p>
            <ul style={{display:"flex", flexDirection:"column", gap:9}}>
              {[["Browse All Professionals",()=>goSearch("","")],["Immigration & Visas",()=>goSearch("","immigration")],["Tax & CPA",()=>goSearch("","tax")],["Real Estate Agents",()=>goSearch("","realestate")],["Healthcare Doctors",()=>goSearch("","healthcare")],["Wedding Services",()=>nav("wedding")],["Care Services",()=>nav("care")],["IT Training & Jobs",()=>nav("ittraining")],["India Services 🇮🇳",()=>nav("india-services")]].map(([l,fn])=>(
                <li key={l} onClick={fn} className="cd-footer-link">{l}</li>
              ))}
            </ul>
          </div>
          {/* Column 2: Grow Your Practice */}
          <div>
            <p style={{color:"#F2EDE4", fontWeight:700, fontSize:"0.85rem", marginBottom:16}}>Grow Your Practice</p>
            <ul style={{display:"flex", flexDirection:"column", gap:9}}>
              {[["Join as Professional",()=>{setAuthMode("signup");setUType("provider");nav("auth");}],["Get Verified – 30-Point Badge",()=>nav("verification-process")],["How Verification Works",()=>nav("safety")],["Professional Dashboard",()=>nav("provider-dash")],["Success Stories",()=>nav("success-stories")],["Resources & Guides",()=>nav("resources")],["List Your Service Free",()=>{setAuthMode("signup");setUType("provider");nav("auth");}]].map(([l,fn])=>(
                <li key={l} onClick={fn} className="cd-footer-link">{l}</li>
              ))}
            </ul>
          </div>
          {/* Column 3: We Can Help You */}
          <div>
            <p style={{color:"#F2EDE4", fontWeight:700, fontSize:"0.85rem", marginBottom:16}}>We Can Help You</p>
            <ul style={{display:"flex", flexDirection:"column", gap:9}}>
              {[["Post a Need",()=>nav("post-need")],["How It Works",()=>nav("how-it-works")],["Safety & Trust",()=>nav("safety")],["Help Center & FAQ",()=>nav("help")],["Saved Professionals",()=>{user?nav("saved"):nav("auth");}],["Dispute Resolution",()=>nav("safety")],["Roommates & Rentals",()=>nav("roommates")]].map(([l,fn])=>(
                <li key={l} onClick={fn} className="cd-footer-link">{l}</li>
              ))}
            </ul>
          </div>
          {/* Column 4: About CertifiedDESI */}
          <div>
            <p style={{color:"#F2EDE4", fontWeight:700, fontSize:"0.85rem", marginBottom:16}}>About CertifiedDESI</p>
            <ul style={{display:"flex", flexDirection:"column", gap:9}}>
              {[["Our Mission",()=>nav("about")],["The Verification Standard",()=>nav("verification-process")],["Press & Media",()=>nav("press")],["Contact Us",()=>nav("contact")],["Privacy Policy",()=>nav("privacy")],["Terms of Service",()=>nav("terms")]].map(([l,fn])=>(
                <li key={l} onClick={fn} className="cd-footer-link">{l}</li>
              ))}
            </ul>
            <div style={{marginTop:20}}>
              <p style={{color:"#F2EDE4", fontWeight:700, fontSize:"0.85rem", marginBottom:12}}>CertifiedDESI App</p>
              <p style={{fontSize:"0.72rem", lineHeight:1.6, marginBottom:10}}>Find verified professionals on the go. iOS &amp; Android – coming soon.</p>
              <button onClick={()=>{user?showToast("You'll be notified when the app launches! 📱"):nav("auth");}}
                style={{background:"#2E2C28", border:"1px solid #3E3C38", color:"#A8A298", borderRadius:8, padding:"8px 14px", fontSize:"0.73rem", cursor:"pointer", display:"flex", alignItems:"center", gap:6, transition:"all 0.15s"}}
                className="cd-btn-dark">
                📱 Notify me at launch
              </button>
            </div>
          </div>
        </div>

        {/* Logo + legal strip */}
        <div style={{borderTop:"1px solid #2E2C28", paddingTop:24}}>
          <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:16, marginBottom:16}}>
            {/* Logo */}
            <div style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer"}} onClick={()=>nav("landing")}>
              <div style={{width:26, height:26, background:S.terra, borderRadius:4, display:"flex", alignItems:"center", justifyContent:"center"}}>
                <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontStyle:"italic", fontSize:"0.78rem"}}>CD</span>
              </div>
              <span style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, color:"#A8A298", fontSize:"0.95rem"}}>
                Certified<span style={{color:S.terra}}>DESI</span>
              </span>
            </div>
            {/* Trust badges */}
            <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
              {["🔒 SOC 2 Compliant","🛡️ 256-bit Encrypted","🇺🇸 USA Based","✓ License Verified"].map(b=>(
                <span key={b} style={{fontSize:"0.68rem", color:"#6A6660", padding:"3px 10px", border:"1px solid #2E2C28", borderRadius:99}}>{b}</span>
              ))}
            </div>
          </div>
          <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", color:"#4A4740", fontSize:"0.68rem"}}>
            <span>© 2026 CertifiedDESI</span>
            <span>·</span>
            <span>All rights reserved</span>
            <span>·</span>
            <span onClick={()=>nav("privacy")} className="cd-footer-legal">Privacy Policy</span>
            <span>·</span>
            <span onClick={()=>nav("terms")} className="cd-footer-legal">Terms &amp; Conditions</span>
            <span>·</span>
            <span style={{color:"#4A4740"}}>CertifiedDESI is not a law firm and does not provide legal advice</span>
          </div>
        </div>
      </div>
    </footer>
  );


  // ────────────────────────────────────────────────────
  // COMPONENT: INTERACTIVE VERIFICATION
  // ────────────────────────────────────────────────────
  const InteractiveVerification = () => {
    const [openSteps, setOpenSteps] = useState([]);      // which dropdowns are open
    const [selectedSteps, setSelectedSteps] = useState([]); // which are checked (score)
    const [score, setScore] = useState(0);

    const PILLARS = [
      { id:"identity",   n:"1", icon:"🪪", title:"Identity",             pts:6, color:"#C4522A",
        checks:["Government Photo ID (Passport / Driver's License)","SSN verified against credit bureau","Biometric face-match liveness check","US address confirmed","Phone OTP verification","Professional email domain check"],
        why:"Fake practitioners often use borrowed or fabricated credentials. Identity verification ensures the person listing is exactly who they claim to be." },
      { id:"license",    n:"2", icon:"🎓", title:"Professional License",  pts:8, color:"#7B5EA7",
        checks:["Active license verified against official state database","License not suspended or revoked","Expiry date tracked with auto-alerts","Educational credentials confirmed","Professional certifications verified (CPA, CFP, CLU...)","E&O / Malpractice insurance active","Regulatory board disciplinary history clear","State complaint records screened"],
        why:"The #1 Desi community scam: unlicensed consultants charging for filings they can't legally do. We verify every license, every time." },
      { id:"business",   n:"3", icon:"🏢", title:"Business Entity",       pts:6, color:"#C49A2A",
        checks:["LLC or Corp verified via Secretary of State","Active business status confirmed","EIN verification against IRS records","Physical business address verified","Business operating for 6+ months","Business bank account verified"],
        why:"Real professionals run real businesses. Shell companies and pop-up consultancies don't survive our business entity check." },
      { id:"background", n:"4", icon:"🔍", title:"Background & Compliance",pts:6, color:"#2E7D52",
        checks:["Federal criminal history check","State criminal records (all states operated in)","National Sex Offender registry","OFAC / Sanctions screening","Bankruptcy and civil judgments","AML screening"],
        why:"You're trusting this person with your immigration status, finances, home purchase. Background checks ensure there are no hidden risks." },
      { id:"community",  n:"5", icon:"⭐", title:"Community Trust",        pts:4, color:"#C4522A",
        checks:["3 client references verified by phone call","Community rating maintained at 4.0+ stars","Response rate 80%+ within 24 hours","Every review AI-scanned for authenticity"],
        why:"Other platforms have fake reviews. We phone-verify references and AI-scan every review. The South Asian community is the final authority." },
    ];

    const toggleSelect = (pillar, e) => {
      e.stopPropagation();
      const sel = selectedSteps.includes(pillar.id)
        ? selectedSteps.filter(x => x !== pillar.id)
        : [...selectedSteps, pillar.id];
      setSelectedSteps(sel);
      setScore(sel.reduce((acc,id) => acc + (PILLARS.find(p=>p.id===id)?.pts||0), 0));
    };

    const toggleOpen = (id, e) => {
      e.stopPropagation();
      setOpenSteps(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);
    };

    const badgeLabel = score >= 25 ? "Certified Desi Elite" : score >= 16 ? "Verified Desi Pro" : score > 0 ? "Listed" : "No badge yet";
    const badgeColor = score >= 25 ? "#C4522A" : score >= 16 ? "#7B5EA7" : score > 0 ? "#8A8578" : "#4A4740";

    return (
      <section style={{padding:"60px 16px", background:"#1C1B19", color:"#F2EDE4"}}>
        <div style={{maxWidth:900, margin:"0 auto"}}>

          {/* Header */}
          <div style={{textAlign:"center", marginBottom:32}}>
            <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22",
              color:"#C4522A", fontSize:"0.62rem", fontWeight:800, letterSpacing:"0.12em",
              textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:14}}>
              How Verification Works
            </div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(1.8rem,4vw,2.6rem)", fontWeight:700, marginBottom:10}}>
              The 30-Point Verification System
            </h2>
            <p style={{color:"#A8A298", fontSize:"0.85rem", lineHeight:1.7, maxWidth:520, margin:"0 auto"}}>
              Select each pillar to add it to your score. Click the arrow to see what we check.
            </p>
          </div>

          {/* SCORE RING – top centre */}
          <div style={{display:"flex", flexDirection:"column", alignItems:"center", marginBottom:36}}>
            <div style={{position:"relative", width:120, height:120}}>
              <svg viewBox="0 0 120 120" style={{width:"100%", height:"100%", transform:"rotate(-90deg)"}}>
                <circle cx="60" cy="60" r="50" fill="none" stroke="#2E2C28" strokeWidth="10"/>
                <circle cx="60" cy="60" r="50" fill="none"
                  stroke={badgeColor} strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={`${(score/30)*314} 314`}
                  style={{transition:"stroke-dasharray 0.4s ease, stroke 0.3s ease"}}/>
              </svg>
              <div style={{position:"absolute", inset:0, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center"}}>
                <span style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.2rem", color:"#F2EDE4", lineHeight:1}}>{score}</span>
                <span style={{fontSize:"0.6rem", color:"#8A8578"}}>/ 30 pts</span>
              </div>
            </div>
            <div style={{marginTop:12, display:"inline-flex", alignItems:"center", gap:6,
              background:badgeColor+"22", border:"1px solid "+badgeColor+"44",
              color:score>0?badgeColor:"#4A4740", fontSize:"0.75rem", fontWeight:700,
              padding:"6px 16px", borderRadius:99, transition:"all 0.4s"}}>
              {score >= 25 ? "🏆" : score >= 16 ? "✓" : "·"} {badgeLabel}
            </div>
            <p style={{fontSize:"0.68rem", color:"#8A8578", marginTop:8}}>
              {score === 0 ? "Select pillars below to build your score" :
               score < 16 ? `${16-score} more pts to reach Verified Desi Pro` :
               score < 25 ? `${25-score} more pts to reach Certified Desi Elite` :
               "Maximum verification level reached ✓"}
            </p>
          </div>

          {/* PILLARS LIST */}
          <div style={{display:"flex", flexDirection:"column", gap:8}}>
            {PILLARS.map(pillar => {
              const sel  = selectedSteps.includes(pillar.id);
              const open = openSteps.includes(pillar.id);
              return (
                <div key={pillar.id}
                  style={{background:"#111110", border:"1px solid "+(sel?pillar.color+"55":"#2E2C28"),
                    borderRadius:12, overflow:"hidden", transition:"border-color 0.2s"}}>
                  {/* Row – click row body to select/deselect, click chevron to open/close */}
                  <div style={{padding:"14px 18px", display:"flex", alignItems:"center", gap:14, cursor:"pointer"}}
                    onClick={e=>toggleSelect(pillar,e)}>

                    {/* Checkbox */}
                    <div style={{width:22, height:22, borderRadius:6, flexShrink:0, display:"flex",
                      alignItems:"center", justifyContent:"center", transition:"all 0.2s",
                      background:sel?pillar.color:"transparent",
                      border:"2px solid "+(sel?pillar.color:"#4A4740")}}>
                      {sel && <span style={{color:"#F2EDE4", fontSize:"0.7rem", fontWeight:800}}>✓</span>}
                    </div>

                    {/* Icon + title */}
                    <span style={{fontSize:"1.1rem", flexShrink:0}}>{pillar.icon}</span>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
                        <span style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1rem", color:"#F2EDE4"}}>{pillar.title}</span>
                        <span style={{background:pillar.color+"22", color:pillar.color, fontSize:"0.6rem",
                          fontWeight:800, padding:"2px 8px", borderRadius:99}}>{pillar.pts} pts</span>
                        {sel && <span style={{color:pillar.color, fontSize:"0.65rem", fontWeight:700}}>+{pillar.pts} added</span>}
                      </div>
                      <p style={{fontSize:"0.68rem", color:"#8A8578", marginTop:2}}>{pillar.checks.length} verification checks</p>
                    </div>

                    {/* Chevron – toggle dropdown independently */}
                    <div onClick={e=>toggleOpen(pillar.id,e)}
                      style={{width:28, height:28, borderRadius:6, background:"#2E2C28",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        cursor:"pointer", flexShrink:0, transition:"background 0.15s"}}
                      className="verify-chevron">
                      <span style={{color:"#8A8578", fontSize:"0.7rem", display:"inline-block",
                        transition:"transform 0.2s", transform:open?"rotate(180deg)":"none"}}>▾</span>
                    </div>
                  </div>

                  {/* Detail panel – only shown when chevron toggled */}
                  {open && (
                    <div style={{padding:"0 18px 18px", borderTop:"1px solid #2E2C28"}}>
                      <div style={{paddingTop:14, display:"grid", gap:16}} className="verify-inner">
                        <div>
                          <p style={{fontSize:"0.6rem", fontWeight:800, color:pillar.color,
                            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:10}}>What We Check</p>
                          {pillar.checks.map(c=>(
                            <div key={c} style={{display:"flex", alignItems:"flex-start", gap:8,
                              fontSize:"0.74rem", color:"#A8A298", marginBottom:7, lineHeight:1.55}}>
                              <span style={{color:pillar.color, flexShrink:0, marginTop:1}}>✓</span>{c}
                            </div>
                          ))}
                        </div>
                        <div style={{background:"#1C1B19", borderRadius:10, padding:14, borderLeft:"3px solid "+pillar.color}}>
                          <p style={{fontSize:"0.6rem", fontWeight:800, color:pillar.color,
                            letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:8}}>Why This Matters</p>
                          <p style={{fontSize:"0.75rem", color:"#A8A298", lineHeight:1.7}}>{pillar.why}</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{display:"flex", justifyContent:"center", gap:14, marginTop:32, flexWrap:"wrap"}}>
            <button onClick={()=>nav("safety")} className="cd-btn-dark" style={{padding:"11px 24px"}}>Full Safety Details</button>
            <button onClick={()=>{setAuthMode("signup");nav("auth");}} className="cd-btn-primary">Get Verified</button>
          </div>
        </div>
      </section>
    );
  };
  // COMPONENT: INTERACTIVE VERIFICATION
  // ────────────────────────────────────────────────────
  // ────────────────────────────────────────────────────
  // PAGE: LANDING
  // ────────────────────────────────────────────────────
  const Landing = () => (
    <div>
      {/* Hero */}
      <section style={{background:S.charcoal, color:S.cream, padding:"72px 16px 60px", position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute", inset:0, backgroundImage:"radial-gradient(circle at 70% 50%, rgba(196,82,42,0.12) 0%, transparent 60%), radial-gradient(circle at 20% 80%, rgba(196,154,42,0.08) 0%, transparent 40%)", pointerEvents:"none"}}/>
        <div style={{maxWidth:860, margin:"0 auto", textAlign:"center", position:"relative"}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", border:"1px solid #C4522A44",
            color:S.terra, fontSize:"0.62rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase",
            padding:"5px 14px", borderRadius:99, marginBottom:18}}>
            🏆 USA's #1 Verified South Asian Professional Network
          </div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.4rem,6vw,4rem)", fontWeight:700, lineHeight:1.06, marginBottom:14}}>
            Find Trusted Desi Professionals.<br/>
            <span style={{color:S.terra}}>Certified. Verified. Yours.</span>
          </h1>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", marginBottom:28, lineHeight:1.75, maxWidth:520, margin:"0 auto 28px", textAlign:"center"}}>
            Every professional is license-verified, background-checked, and community-reviewed.
          </p>

          {/* HERO TABS – Find a Pro | Post a Need */}
          <div style={{display:"flex", justifyContent:"center", marginBottom:20, gap:0}}>
            {[["find","🔍 Find a Professional"],["post","📋 Post a Need"]].map(([tab,label])=>(
              <button key={tab} onClick={()=>setHeroTab(tab)}
                style={{padding:"10px 28px", fontSize:"0.82rem", fontWeight:700, border:"none", cursor:"pointer",
                  background:heroTab===tab?S.cream:"transparent",
                  color:heroTab===tab?S.charcoal:S.mutedLt,
                  borderRadius:tab==="find"?"10px 0 0 10px":"0 10px 10px 0",
                  borderBottom:heroTab===tab?"none":"1px solid #3E3C38",
                  boxShadow:heroTab===tab?"0 4px 20px rgba(0,0,0,0.2)":"none",
                  transition:"all 0.2s"}}>
                {label}
              </button>
            ))}
          </div>

          {heroTab==="find" ? (
            <div style={{maxWidth:720, margin:"0 auto", position:"relative"}} className="cd-search-outer">
              <div style={{background:S.cream, borderRadius:12, display:"flex", alignItems:"stretch", boxShadow:"0 6px 40px rgba(0,0,0,0.35)", overflow:"hidden", height:54}}>
                {/* Category – auto-width */}
                <div style={{position:"relative", flexShrink:0, display:"flex", alignItems:"center", borderRight:"1px solid #DDD8CE", background:"#EDE7DC"}}>
                  <select
                    value={searchCat}
                    onChange={e=>setSearchCat(e.target.value)}
                    style={{background:"transparent", border:"none", padding:"0 26px 0 14px", fontSize:"0.8rem", color:S.charcoal, cursor:"pointer", outline:"none", fontFamily:"inherit", height:"100%", appearance:"none", WebkitAppearance:"none", minWidth:90}}>
                    <option value="">All Categories</option>
                    {CATS.map(cat=><option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>)}
                  </select>
                  <span style={{position:"absolute", right:8, pointerEvents:"none", color:S.muted, fontSize:"0.6rem"}}>▾</span>
                </div>
                {/* Query – uses global userCity from header location pill */}
                <div style={{flex:1, display:"flex", alignItems:"center", gap:10, padding:"0 18px"}}>
                  <Search size={15} style={{color:S.muted, flexShrink:0}}/>
                  <input id="hero-search" style={{flex:1, border:"none", outline:"none", background:"transparent", color:S.charcoal, fontSize:"0.92rem"}}
                    placeholder='Search by name, specialty, language…'
                    onKeyDown={e=>{ if(e.key==="Enter"){e.preventDefault();goSearch(e.target.value,searchCat,userCity);}}}/>
                </div>
                <button onClick={()=>{goSearch(document.getElementById("hero-search")?.value||"",searchCat,userCity);}}
                  className="cd-btn-primary" style={{borderRadius:0, padding:"0 28px", fontSize:"0.92rem", fontWeight:700, flexShrink:0}}>
                  Search
                </button>
              </div>
            {recentSearches.length > 0 && (
              <div className="cd-recent-dropdown">
                <div style={{padding:"8px 14px 4px", fontSize:"0.6rem", fontWeight:800, color:S.muted, letterSpacing:"0.1em", textTransform:"uppercase"}}>Recent searches</div>
                {recentSearches.map((r,i)=>(
                  <div key={i}
                    onMouseDown={e=>{e.preventDefault(); const el=document.getElementById("hero-search"); if(el)el.value=r; goSearch(r,"");}}
                    style={{display:"flex", alignItems:"center", gap:10, padding:"9px 14px", cursor:"pointer", fontSize:"0.82rem", color:S.charcoal, borderTop:i>0?`1px solid ${S.creamDark}`:"none"}}
                    className="cd-recent-item">
                    <Clock size={11} style={{color:S.muted, flexShrink:0}}/> {r}
                    <button
                      onMouseDown={e=>{e.stopPropagation(); e.preventDefault(); setRecentSearches(prev=>prev.filter(x=>x!==r));}}
                      style={{marginLeft:"auto", background:"none", border:"none", cursor:"pointer", color:S.muted, fontSize:"0.72rem", padding:"2px 6px", borderRadius:4}}
                      className="cd-recent-remove">✕</button>
                  </div>
                ))}
              </div>
            )}
            </div>
          ) : (
            /* POST A NEED TAB – same fields as dedicated page */
            <div style={{background:S.cream, borderRadius:12, padding:24, maxWidth:560, margin:"0 auto", boxShadow:"0 6px 40px rgba(0,0,0,0.35)"}}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.3rem", color:S.charcoal, marginBottom:4}}>Describe what you need</h3>
              <p style={{fontSize:"0.72rem", color:S.muted, marginBottom:14, lineHeight:1.5}}>Verified Desi professionals will reach out – no hunting required.</p>
              <div style={{display:"flex", flexDirection:"column", gap:10}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
                  <div>
                    <label style={{display:"block",fontSize:"0.6rem",fontWeight:800,color:S.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Category *</label>
                    <select className="cd-input" value={postNeed.cat} style={{fontSize:"0.78rem"}} onChange={e=>setPostNeed(p=>({...p,cat:e.target.value}))}>
                      <option value="">Select...</option>
                      {CATS.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:"block",fontSize:"0.6rem",fontWeight:800,color:S.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Budget Range</label>
                    <select className="cd-input" value={postNeed.budget||""} style={{fontSize:"0.78rem"}} onChange={e=>setPostNeed(p=>({...p,budget:e.target.value}))}>
                      <option value="">Not sure yet</option>
                      <option>Under $500</option><option>$500–$1,000</option><option>$1,000–$2,500</option><option>$2,500–$5,000</option><option>$5,000+</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{display:"block",fontSize:"0.6rem",fontWeight:800,color:S.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Describe Your Situation *</label>
                  <textarea className="cd-input" rows={3}
                    id="hero-pn-desc"
                    placeholder="E.g. H-1B transfer NJ, Hindi-speaking attorney, 3-month timeline..."
                    defaultValue={postNeed.desc}
                    onBlur={e=>setPostNeed(p=>({...p,desc:e.target.value}))}/>
                </div>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
                  <div>
                    <label style={{display:"block",fontSize:"0.6rem",fontWeight:800,color:S.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Preferred Language</label>
                    <select className="cd-input" value={postNeed.lang||""} style={{fontSize:"0.78rem"}} onChange={e=>setPostNeed(p=>({...p,lang:e.target.value}))}>
                      <option value="">Any language</option>
                      {["English","Hindi","Gujarati","Tamil","Telugu","Punjabi","Bengali","Malayalam","Marathi"].map(l=><option key={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:"block",fontSize:"0.6rem",fontWeight:800,color:S.muted,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:4}}>Email / Phone *</label>
                    <div style={{display:"flex",gap:6}}>
                      <select id="hero-pn-cc" defaultValue="+1"
                        style={{background:S.creamDark,border:"1px solid #DDD8CE",borderRadius:8,padding:"0 8px",fontSize:"0.72rem",color:S.charcoal,outline:"none",cursor:"pointer",fontFamily:"inherit",flexShrink:0,height:38}}>
                        {[
      ["+1","🇺🇸 +1"],  ["+91","🇮🇳 +91"], ["+44","🇬🇧 +44"],
      ["+61","🇦🇺 +61"], ["+971","🇦🇪 +971"],["+65","🇸🇬 +65"],
      ["+64","🇳🇿 +64"], ["+60","🇲🇾 +60"]
    ].map(([code,label])=>(
                          <option key={code} value={code}>{label}</option>
                        ))}
                      </select>
                      <input id="hero-pn-contact" className="cd-input" placeholder="email or 10-digit number"
                        style={{fontSize:"0.78rem",flex:1}}
                        defaultValue={postNeed.contact}
                        onBlur={e=>setPostNeed(p=>({...p,contact:e.target.value}))}/>
                    </div>
                  </div>
                </div>
                <button onClick={async()=>{
                  const descVal2 = document.getElementById("hero-pn-desc")?.value||postNeed.desc||"";
                  if(!postNeed.cat||!descVal2.trim()){showToast("Please select a category and describe your need","error");return;}
                  const fd=new FormData();
                  fd.append("category",postNeed.cat);
                  fd.append("description",descVal2);
                  fd.append("budget",postNeed.budget||"");
                  fd.append("language",postNeed.lang||"");
                  fd.append("city",userCity||"");
                  const ccVal=document.getElementById("hero-pn-cc")?.value||"+1";
                  const contactVal=document.getElementById("hero-pn-contact")?.value||postNeed.contact||"";
                  fd.append("contact",ccVal+" "+contactVal);
                  fd.append("user_name",user?.name||"");
                  try{
                    const r=await fetch(API_BASE+"/submit_post_need.php",{method:"POST",body:fd});
                    const d=await r.json();
                    if(d.success){setPostSent(true);setPostNeed({cat:"",desc:"",budget:"",lang:"",contact:""});showToast("Posted! Professionals will contact you soon 🙏");}
                    else showToast(d.message||"Something went wrong","error");
                  }catch(e){setPostSent(true);setPostNeed({cat:"",desc:"",budget:"",lang:"",contact:""});showToast("Posted! ✓");}
                }} className="cd-btn-primary" style={{padding:"11px",fontSize:"0.85rem"}}>
                  {postSent?"✓ Posted – Pros Will Contact You →":"Post My Need →"}
                </button>
              </div>
            </div>
          )}

          <div id="hero-search-sentinel" style={{height:1,width:"100%"}}/>
          {/* TRENDING QUICK LINKS */}
          <div style={{marginTop:16, display:"flex", flexWrap:"wrap", justifyContent:"center", gap:6}}>
            {TRENDING.map((t,i)=>(
              <button key={i} onClick={()=>goSearch(t.q, t.cat, t.city)}
                style={{background:"#FFFFFF14", border:"1px solid #FFFFFF22", color:S.mutedLt, fontSize:"0.72rem",
                  padding:"5px 12px", borderRadius:99, cursor:"pointer", transition:"all 0.15s"}}
                className="cd-trend-pill">
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Auto-scrolling trust bar – hover pauses, drag to scroll */}
      <TrustBar/>



      {/* Elite providers */}
      <section style={{padding:"72px 16px", background:S.creamDark}}>
        <div style={{maxWidth:1024, margin:"0 auto"}}>
          <div style={{display:"flex", alignItems:"flex-end", justifyContent:"space-between", marginBottom:28}}>
            <div>
              <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.2rem", fontWeight:700, color:S.charcoal}}>🏆 Elite Certified Professionals</h2>
              <p style={{color:S.muted, fontSize:"0.72rem", marginTop:4}}>25+ out of 30 verification points completed</p>
            </div>
            <button onClick={()=>goSearch("","")} style={{color:S.terra, fontSize:"0.75rem", fontWeight:700, background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:4}}>
              View all <ChevronRight size={12}/>
            </button>
          </div>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:16}}>
            {PROVIDERS.filter(p=>p.badge==="elite").slice(0,6).map(p=><ProvCard key={p.id} p={p}/>)}
          </div>
        </div>
      </section>

      {/* How it works preview */}
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto", textAlign:"center"}}>
          <SectionHead label="Simple Process" title="How It Works" sub="Simple, safe, and designed for the Desi community"/>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:40, marginBottom:32}}>
            {[{s:"1",ic:"🔍",t:"Search & Discover",d:"Smart search by category, language, city – across 14 service categories from immigration to wedding to elder care."},{s:"2",ic:"✅",t:"Verify & Compare",d:"Review 30-point badges, credentials, ratings and community reviews."},{s:"3",ic:"🤝",t:"Connect & Transact",d:"Message, request quotes, and book safely with escrow protection."}].map(s=>(
              <div key={s.s}>
                <div style={{width:56, height:56, background:S.creamDark, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.6rem", margin:"0 auto 12px"}}>{s.ic}</div>
                <div style={{fontSize:"0.62rem", fontWeight:800, color:S.terra, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6}}>STEP {s.s}</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.05rem", color:S.charcoal, marginBottom:6}}>{s.t}</h3>
                <p style={{color:S.muted, fontSize:"0.75rem", lineHeight:1.65}}>{s.d}</p>
              </div>
            ))}
          </div>
          <button onClick={()=>nav("how-it-works")} className="cd-btn-outline">Learn More →</button>
        </div>
      </section>


      {/* Interactive Verification System */}
      <InteractiveVerification/>

      {/* Provider CTA */}
      <section style={{padding:"72px 16px", background:S.charcoal, color:S.cream, position:"relative", overflow:"hidden"}}>
        <div style={{position:"absolute", inset:0, backgroundImage:"radial-gradient(circle at 80% 50%, rgba(196,154,42,0.07) 0%, transparent 50%)", pointerEvents:"none"}}/>
        <div style={{maxWidth:720, margin:"0 auto", textAlign:"center", position:"relative"}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.5rem", fontWeight:700, marginBottom:12}}>Are You a Desi Professional?</h2>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", marginBottom:32, lineHeight:1.75}}>Join 500+ verified professionals. Get your CertifiedDESI badge and grow your practice with qualified leads from the South Asian diaspora.</p>
          <div style={{display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap"}}>
            <button onClick={()=>{setAuthMode("signup");setUType("provider");nav("auth");}} className="cd-btn-primary" style={{padding:"14px 32px"}}>
              List Your Business Free
            </button>
            <button onClick={()=>nav("verification-process")}
              className="cd-btn-dark" style={{padding:"14px 32px"}}>
              View Verification Process
            </button>
          </div>
          <p style={{color:"#A8A298", fontSize:"0.72rem", marginTop:16, letterSpacing:"0.02em"}}>Free listing · No credit card · Verified in 48 hours</p>
        </div>
      </section>

      {/* ALL CATEGORIES GRID – 3×5 symmetric grid */}
      <section style={{padding:"60px 16px", background:S.cream}}>
        <div style={{maxWidth:960, margin:"0 auto"}}>
          <SectionHead label="All Services" title="Browse Every Category" sub="14 verified service categories – all professionals license-checked and community-reviewed"/>
          <div style={{display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:20}}>
            {CATS.map(c=>(
              <div key={c.id} onClick={()=>goSearch("",c.id)} className="cd-cat-card"
                style={{padding:"20px 16px", borderLeft:`3px solid ${c.color}`}}
                role="button" tabIndex={0} onKeyDown={e=>e.key==="Enter"&&goSearch("",c.id)}>
                <div style={{fontSize:"1.6rem", marginBottom:8}}>{c.icon}</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, color:S.charcoal, fontSize:"0.88rem", marginBottom:3}}>{c.label}</h3>
                <p style={{fontSize:"0.62rem", color:S.muted, lineHeight:1.4}}>{c.desc}</p>
              </div>
            ))}
            <div className="cd-cat-all" onClick={()=>nav("india-services")} role="button" tabIndex={0}
              style={{borderLeft:"3px solid #C49A2A"}}>
              <span style={{fontSize:"1.6rem"}}>🇮🇳</span>
              <span style={{fontSize:"0.7rem", color:S.charcoal, marginTop:8, fontWeight:700, letterSpacing:"0.04em", textAlign:"center"}}>India Services</span>
              <span style={{fontSize:"0.62rem", color:S.muted, textAlign:"center", lineHeight:1.4, marginTop:4}}>OCI, NRI banking, property, legal & more</span>
            </div>
          </div>
        </div>
      </section>

      {/* APP DOWNLOAD CTA */}
      <section style={{padding:"48px 16px", background:S.charMid}}>
        <div style={{maxWidth:800, margin:"0 auto", display:"grid", gap:32, alignItems:"center"}} className="app-cta-grid">
          <style>{".app-cta-grid{grid-template-columns:1fr!important} @media(min-width:640px){.app-cta-grid{grid-template-columns:1fr 1fr!important}}"}</style>
          <div>
            <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", border:"1px solid #C4522A44", color:S.terra, fontSize:"0.6rem", fontWeight:800, letterSpacing:"0.1em", textTransform:"uppercase", padding:"4px 12px", borderRadius:99, marginBottom:12}}>
              Coming Soon
            </div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"1.8rem", fontWeight:700, color:S.cream, marginBottom:8}}>
              CertifiedDESI App
            </h2>
            <p style={{color:"#A8A298", fontSize:"0.82rem", lineHeight:1.7, marginBottom:20}}>
              Find verified professionals, post needs, save favorites, and get instant quote responses – all on mobile. iOS & Android, coming soon.
            </p>
            <div style={{display:"flex", gap:10, flexWrap:"wrap"}}>
              <button onClick={()=>setNotifyModal(true)}
                style={{background:"#F2EDE4", color:"#1C1B19", border:"none", borderRadius:10, padding:"10px 20px", fontSize:"0.8rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:8}}>
                🍎 Notify me – iOS
              </button>
              <button onClick={()=>setNotifyModal(true)}
                style={{background:"#F2EDE4", color:"#1C1B19", border:"none", borderRadius:10, padding:"10px 20px", fontSize:"0.8rem", fontWeight:700, cursor:"pointer", display:"flex", alignItems:"center", gap:8}}>
                🤖 Notify me – Android
              </button>
            </div>
            {/* Notify Modal */}
            {notifyModal && (
              <div style={{position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:60, display:"flex", alignItems:"center", justifyContent:"center", padding:16}}
                onClick={e=>{if(e.target===e.currentTarget)setNotifyModal(false);}}>
                <div style={{background:S.creamMid, borderRadius:16, padding:32, maxWidth:380, width:"100%", border:`1px solid #DDD8CE`}}>
                  <div style={{fontSize:"2.5rem", textAlign:"center", marginBottom:12}}>📱</div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal, textAlign:"center", marginBottom:6}}>Get notified at launch</h3>
                  <p style={{color:S.muted, fontSize:"0.78rem", textAlign:"center", marginBottom:20, lineHeight:1.6}}>We'll send you a one-time email when CertifiedDESI iOS &amp; Android app is live.</p>
                  {user ? (
                    <div>
                      <p style={{fontSize:"0.78rem", color:S.charLight, textAlign:"center", marginBottom:16}}>We'll notify <strong>{user.name}</strong> at your registered email.</p>
                      <button onClick={async()=>{
                        const fd=new FormData();
                        fd.append("email",user?.email||user?.name||"");
                        fd.append("type","app");
                        try{await fetch(API_BASE+"/submit_notify.php",{method:"POST",body:fd});}catch(e){}
                        setNotifyModal(false);showToast("You're on the list! We'll notify you at launch 🚀");
                      }} className="cd-btn-primary" style={{width:"100%", padding:"12px"}}>
                        Confirm – Notify Me →
                      </button>
                    </div>
                  ) : (
                    <div style={{display:"flex", flexDirection:"column", gap:10}}>
                      <input id="notify-email" className="cd-input" type="email" placeholder="your@email.com" style={{fontSize:"0.85rem"}}/>
                      <button onClick={async()=>{
                        const email=document.getElementById("notify-email")?.value||"";
                        if(!email||!email.includes("@")){showToast("Please enter a valid email","error");return;}
                        const fd=new FormData();
                        fd.append("email",email);
                        fd.append("type","app");
                        try{
                          const r=await fetch(API_BASE+"/submit_notify.php",{method:"POST",body:fd});
                          const d=await r.json();
                          if(d.success){setNotifyModal(false);showToast("You're on the list! We'll notify you at launch 🚀");}
                          else showToast(d.message||"Something went wrong","error");
                        }catch(e){setNotifyModal(false);showToast("You're on the list! We'll notify you at launch 🚀");}
                      }} className="cd-btn-primary" style={{padding:"12px"}}>
                        Notify Me When Live →
                      </button>
                      <p style={{fontSize:"0.68rem", color:S.muted, textAlign:"center"}}>or <span onClick={()=>{setNotifyModal(false);nav("auth");}} style={{color:S.terra, cursor:"pointer", textDecoration:"underline"}}>Login</span> for one-click notification</p>
                    </div>
                  )}
                  <button onClick={()=>setNotifyModal(false)} style={{width:"100%", marginTop:10, background:"none", border:"none", color:S.muted, fontSize:"0.75rem", cursor:"pointer"}}>Maybe later</button>
                </div>
              </div>
            )}
          </div>
          <div style={{display:"flex", flexDirection:"column", gap:10}}>
            {[["📬","Instant quote requests","Professionals respond within hours"],["🔖","Save & compare","Bookmark your top choices"],["🔔","Real-time alerts","Get notified when new pros join"],["📍","Location-aware","Find the best pros near you"]].map(([ic,t,d])=>(
              <div key={t} style={{display:"flex", gap:12, alignItems:"flex-start", background:"#2E2C28", borderRadius:10, padding:"12px 14px"}}>
                <span style={{fontSize:"1.2rem", flexShrink:0}}>{ic}</span>
                <div>
                  <p style={{color:"#F2EDE4", fontSize:"0.78rem", fontWeight:600}}>{t}</p>
                  <p style={{color:"#8A8578", fontSize:"0.68rem", marginTop:2}}>{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section style={{padding:"48px 16px", background:S.cream, borderTop:`1px solid #DDD8CE`}}>
        <div style={{maxWidth:896, margin:"0 auto", display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:24, textAlign:"center"}}>
          {[["500+","Verified Professionals"],["12K+","Happy Clients"],["14","Service Categories"],["50+","US Cities"]].map(([n,l])=>(
            <div key={l} className="cd-stat-card">
              <div style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.5rem", fontWeight:700, color:S.terra}}>{n}</div>
              <div style={{color:S.muted, fontSize:"0.72rem", marginTop:4}}>{l}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: AUTH (with email verification flow)
  // ────────────────────────────────────────────────────
  const Auth = () => {
    const isSignup = authMode==="signup";

    // "card" = which signup card was picked: null | "consumer" | "provider"
    const [card,    setCard]     = useState(isSignup ? (authDirect||null) : "consumer");

    const [fName,    setFName]    = useState("");
    const [fEmail,   setFEmail]   = useState("");
    const [fPwd,     setFPwd]     = useState("");
    const [fPhone,   setFPhone]   = useState("");
    const [fCountry, setFCountry] = useState("+1");
    const [fCat,     setFCat]     = useState("");
    const [fCity,    setFCity]    = useState(TOP_CITIES.filter(c=>c!=="All Cities")[0]||"");
    const [fAgreed,  setFAgreed]  = useState(false);
    const [fShowPwd, setFShowPwd] = useState(false);
    const [fCode,    setFCode]    = useState("");
    const [fErrors,  setFErrors]  = useState({});  // field-level inline validation
    const [verifyCode,  setVerifyCode]  = useState("");  // OTP sent to email
    const [enteredCode, setEnteredCode] = useState("");  // OTP typed by user

    const COUNTRY_CODES = [
      ["+1","🇺🇸 +1 US/CA"],["+91","🇮🇳 +91 India"],["+44","🇬🇧 +44 UK"],
      ["+61","🇦🇺 +61 AU"],["+971","🇦🇪 +971 UAE"],["+65","🇸🇬 +65 SG"],
      ["+64","🇳🇿 +64 NZ"],["+60","🇲🇾 +60 MY"]
    ];

    const handleAuth = async () => {
      if(isSignup && emailStep===0){
        // Inline field validation
        const errs={};
        if(!fName.trim()) errs.name="Please enter your full name";
        if(!fEmail.trim()||!fEmail.includes("@")) errs.email="Please enter a valid email address";
        if(fPwd.length<6) errs.pwd="Password must be at least 6 characters";
        if(!fAgreed) errs.agreed="You must agree to the Terms to continue";
        if(Object.keys(errs).length>0){setFErrors(errs);return;}
        setFErrors({});
        // Generate code
        const code = String(Math.floor(100000+Math.random()*900000));
        setVerifyCode(code);
        // Send real email via PHP
        const fdCode=new FormData();
        fdCode.append("email",fEmail); fdCode.append("code",code); fdCode.append("name",fName.split(" ")[0]);
        try{
          await fetch(API_BASE+"/submit_send_code.php",{method:"POST",body:fdCode});
          showToast("Verification code sent to "+fEmail+" 📧");
        }catch(e){
          showToast("Verification code sent to your email 📧");
        }
        setEmailStep(1);
        return;
      }
      if(isSignup && emailStep===1){
        // This path reached via old handleAuth – the verify button handles it directly
        setEmailStep(2); return;
      }
      // This path only runs for edge cases – main flows handled by dedicated buttons
      const name = fName.trim() || "User";
      const accType = card || "consumer";
      const u = {type:accType, name, email:fEmail, isNew:true};
      setUser(u); setEmailStep(0);
      nav(accType==="provider"?"provider-dash":"consumer-dash");
      showToast("Welcome, "+name.split(" ")[0]+"! 🎉");
    };

    return (
      <div style={{minHeight:"100vh", background:S.creamDark, display:"flex", alignItems:"center", justifyContent:"center", padding:"40px 16px"}}>
        <div style={{background:S.creamMid, borderRadius:20, boxShadow:"0 12px 48px rgba(0,0,0,0.12)", width:"100%", maxWidth: (isSignup&&card===null) ? 580 : 440, padding:36, border:`1px solid #DDD8CE`, transition:"max-width 0.3s ease"}}>
          {/* Logo */}
          <div style={{textAlign:"center", marginBottom:22}}>
            <div style={{width:46, height:46, background:S.terra, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 10px"}}>
              <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontStyle:"italic", fontSize:"1.1rem"}}>CD</span>
            </div>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.65rem", color:S.charcoal}}>
              {!isSignup?"Welcome back":emailStep===1?"Check your email":emailStep===2?"Email verified!":"Create account"}
            </h2>
            <p style={{color:S.muted, fontSize:"0.72rem", marginTop:4}}>CertifiedDESI · Verified South Asian Network</p>
          </div>

          {/* Progress bar */}
          {isSignup && (
            <div style={{display:"flex", gap:4, marginBottom:20}}>
              {["Account","Verify Email","Complete"].map((s,i)=>(
                <div key={s} style={{flex:1, textAlign:"center"}}>
                  <div style={{height:3, borderRadius:2, background:i<=emailStep?S.terra:"#DDD8CE", marginBottom:4, transition:"background 0.3s"}}/>
                  <span style={{fontSize:"0.58rem", color:i<=emailStep?S.terra:S.muted, fontWeight:i===emailStep?700:400}}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {/* ── SIGNUP CARD PICKER – shown before form ── */}
          {isSignup && card===null && emailStep===0 && (
            <div>
              <p style={{textAlign:"center",fontSize:"0.78rem",color:S.muted,marginBottom:20}}>
                How would you like to join CertifiedDESI?
              </p>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>
                {/* Consumer card */}
                <button onClick={()=>{setCard("consumer");setUType("consumer");setAuthMode("signup");setEmailStep(0);}}
                  style={{padding:"32px 20px",borderRadius:18,border:"2px solid #DDD8CE",
                    background:S.cream,cursor:"pointer",textAlign:"center",fontFamily:"inherit",
                    transition:"all 0.25s",boxShadow:"0 2px 12px rgba(0,0,0,0.04)"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=S.terra;e.currentTarget.style.boxShadow="0 6px 24px rgba(196,82,42,0.15)";e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="#DDD8CE";e.currentTarget.style.boxShadow="0 2px 12px rgba(0,0,0,0.04)";e.currentTarget.style.transform="none";}}>
                  <div style={{width:56,height:56,borderRadius:"50%",background:"#C4522A11",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",fontSize:"1.8rem"}}>🤝</div>
                  <p style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.2rem",color:S.charcoal,marginBottom:8}}>
                    I'm looking to hire
                  </p>
                  <p style={{fontSize:"0.72rem",color:S.muted,lineHeight:1.6}}>
                    Find a verified Desi CPA, lawyer, doctor, or any trusted professional
                  </p>
                  <div style={{marginTop:14,color:S.terra,fontSize:"0.72rem",fontWeight:700}}>Sign up as a Client →</div>
                </button>
                {/* Provider card */}
                <button onClick={()=>{setAuthDirect(null);nav("join");}}
                  style={{padding:"32px 20px",borderRadius:18,border:"2px solid #C4522A44",
                    background:"#C4522A08",cursor:"pointer",textAlign:"center",fontFamily:"inherit",
                    transition:"all 0.25s",boxShadow:"0 2px 12px rgba(196,82,42,0.08)"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=S.terra;e.currentTarget.style.boxShadow="0 6px 24px rgba(196,82,42,0.2)";e.currentTarget.style.transform="translateY(-2px)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="#C4522A44";e.currentTarget.style.boxShadow="0 2px 12px rgba(196,82,42,0.08)";e.currentTarget.style.transform="none";}}>
                  <div style={{width:56,height:56,borderRadius:"50%",background:"#C4522A22",display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 14px",fontSize:"1.8rem"}}>⭐</div>
                  <p style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.2rem",color:S.charcoal,marginBottom:8}}>
                    I want to offer Pro services
                  </p>
                  <p style={{fontSize:"0.72rem",color:S.muted,lineHeight:1.6}}>
                    Get verified, get listed, and grow your practice with the Desi community
                  </p>
                  <div style={{marginTop:14,color:S.terra,fontSize:"0.72rem",fontWeight:700}}>See what you get →</div>
                </button>
              </div>
              <div style={{textAlign:"center",fontSize:"0.72rem",color:S.muted}}>
                Already have an account?{" "}
                <button onClick={()=>{setAuthMode("login");setEmailStep(0);setCard("consumer");}}
                  style={{color:S.terra,fontWeight:700,background:"none",border:"none",cursor:"pointer"}}>
                  Login
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 0: Form ── */}
          {((!isSignup)||(isSignup&&card!==null)) && emailStep===0 && (
            <>
              {isSignup && card && (
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,padding:"8px 12px",background:S.creamDark,borderRadius:8}}>
                  <span style={{fontSize:"1rem"}}>{card==="consumer"?"🔍":"🏆"}</span>
                  <span style={{fontSize:"0.75rem",color:S.charcoal,fontWeight:600}}>{card==="consumer"?"I'm looking to hire":"I want to offer services"}</span>
                  <button onClick={()=>{setCard(null);setEmailStep(0);}} style={{marginLeft:"auto",background:"none",border:"none",color:S.muted,cursor:"pointer",fontSize:"0.7rem"}}>Change ↩</button>
                </div>
              )}

              <div style={{display:"flex", flexDirection:"column", gap:12}}>
                {isSignup && (
                  <div>
                    <label className="cd-label">Full Name {uType==="provider"?"/ Business Name":""}</label>
                    <input className="cd-input" placeholder={uType==="provider"?"Rajesh Sharma, CPA":"Your full name"}
                      value={fName} onChange={e=>{setFName(e.target.value);setFErrors(p=>({...p,name:""}));}}
                      style={{borderColor:fErrors.name?"#C4522A":undefined}}/>
                    {fErrors.name&&<p style={{color:"#C4522A",fontSize:"0.68rem",marginTop:3}}>⚠ {fErrors.name}</p>}
                  </div>
                )}
                <div>
                  <label className="cd-label">Email Address</label>
                  <input className="cd-input" type="email" placeholder="you@example.com"
                    value={fEmail} onChange={e=>{setFEmail(e.target.value);setFErrors(p=>({...p,email:""}));}}
                    style={{borderColor:fErrors.email?"#C4522A":undefined}}/>
                  {fErrors.email&&<p style={{color:"#C4522A",fontSize:"0.68rem",marginTop:3}}>⚠ {fErrors.email}</p>}
                </div>
                <div>
                  <label className="cd-label">Password</label>
                  <div style={{position:"relative"}}>
                    <input className="cd-input"
                      type={fShowPwd?"text":"password"}
                      placeholder="Min. 6 characters"
                      value={fPwd}
                      onChange={e=>{setFPwd(e.target.value);setFErrors(p=>({...p,pwd:""}));}}
                      style={{paddingRight:40,borderColor:fErrors.pwd?"#C4522A":undefined}}/>
                    {/* Eye icon uses LOCAL fShowPwd – never wipes other fields */}
                    <button type="button" onClick={()=>setFShowPwd(v=>!v)}
                      style={{position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:S.muted, cursor:"pointer", padding:0}}>
                      {fShowPwd?<EyeOff size={14}/>:<Eye size={14}/>}
                    </button>
                  </div>
                </div>
                {isSignup && (
                  <div>
                    <label className="cd-label">Phone Number</label>
                    {/* Country code + number – separate inputs */}
                    <div style={{display:"flex", gap:8}}>
                      <select value={fCountry} onChange={e=>setFCountry(e.target.value)}
                        style={{background:S.creamDark, border:`1px solid #DDD8CE`, borderRadius:8, padding:"0 8px", fontSize:"0.75rem", color:S.charcoal, outline:"none", cursor:"pointer", fontFamily:"inherit", flexShrink:0, height:42}}>
                        {COUNTRY_CODES.map(([code,label])=>(
                          <option key={code} value={code}>{label}</option>
                        ))}
                      </select>
                      <input className="cd-input" style={{flex:1}} type="tel"
                        placeholder="10-digit number"
                        value={fPhone}
                        maxLength={10}
                        onInput={e=>{e.target.value=e.target.value.replace(/[^0-9]/g,"").slice(0,10);}}
                        onChange={e=>setFPhone(e.target.value.replace(/[^0-9]/g,"").slice(0,10))}/>
                    </div>
                  </div>
                )}
                {isSignup && uType==="provider" && (
                  <div>
                    <label className="cd-label">Primary Category</label>
                    <select className="cd-input" value={fCat} onChange={e=>setFCat(e.target.value)}>
                      <option value="">Select your main service...</option>
                      {CATS.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                    </select>
                  </div>
                )}
                {isSignup && uType==="provider" && (
                  <div>
                    <label className="cd-label">Primary City</label>
                    <select className="cd-input" value={fCity} onChange={e=>setFCity(e.target.value)}>
                      {TOP_CITIES.filter(c=>c!=="All Cities").map(c=><option key={c}>{c}</option>)}
                    </select>
                  </div>
                )}
                {isSignup && (
                  <div style={{display:"flex", alignItems:"flex-start", gap:8}}>
                    {/* Checkbox uses LOCAL fAgreed – never wipes other fields */}
                    <input type="checkbox" checked={fAgreed} onChange={e=>setFAgreed(e.target.checked)}
                      style={{marginTop:3, width:15, height:15, accentColor:S.terra, cursor:"pointer", flexShrink:0}}/>
                    <div>
                    <label style={{fontSize:"0.72rem", color:S.muted, lineHeight:1.6}}>
                      I agree to the <span onClick={()=>nav("terms")} style={{color:S.terra, textDecoration:"underline", cursor:"pointer"}}>Terms of Service</span> and <span onClick={()=>nav("privacy")} style={{color:S.terra, textDecoration:"underline", cursor:"pointer"}}>Privacy Policy</span>
                    </label>
                    {fErrors.agreed&&<p style={{color:"#C4522A",fontSize:"0.68rem",marginTop:3}}>⚠ {fErrors.agreed}</p>}
                    </div>
                  </div>
                )}
                <button id="auth-continue-btn" onClick={async()=>{
                  if(isSignup){
                    const btn=document.getElementById("auth-continue-btn");
                    if(emailStep===0){ // Only show spinner when sending email
                      if(btn){btn.disabled=true;btn.textContent="Sending code...";}
                      await handleAuth();
                      if(btn){btn.disabled=false;btn.textContent="Continue – Verify Email";}
                    } else {
                      handleAuth();
                    }
                    return;
                  }
                  // Login – verify against DB
                  const loginErrs={};
                  if(!fEmail.trim()||!fEmail.includes("@")) loginErrs.email="Please enter a valid email address";
                  if(!fPwd.trim()) loginErrs.pwd="Please enter your password";
                  if(Object.keys(loginErrs).length>0){setFErrors(loginErrs);return;}
                  setFErrors({});
                  // Prevent double-submit
                  const loginBtn=document.getElementById("login-submit-btn");
                  if(loginBtn){loginBtn.disabled=true;loginBtn.textContent="Signing in…";}
                  const fd=new FormData();
                  fd.append("action","login"); fd.append("email",fEmail); fd.append("password",fPwd);
                  fd.append("csrf_token", csrfToken);
                  try{
                    const r=await fetch(API_BASE+"/submit_auth.php",{method:"POST",body:fd});
                    const d=await r.json();
                                    if(d.success){
                      const u={type:d.user.type,name:d.user.name,email:d.user.email,id:d.user.id,verified:d.user.verified,avatar_url:d.user.avatar_url};
                      setUser(u); setEmailStep(0);
                      nav(u.type==="provider"?"provider-dash":"consumer-dash");
                      showToast("Welcome back, "+u.name.split(" ")[0]+"! 👋");
                    }else showToast(d.message||"Invalid credentials","error");
                  }catch(e){
                    // Fallback – allow login if PHP unreachable (dev mode)
                    showToast("Login failed – check your connection","error");
                  }
                }} className="cd-btn-primary" style={{marginTop:4, padding:"12px"}}>
                  {!isSignup?"Sign In →":"Continue – Verify Email →"}
                </button>
              </div>
              <div style={{marginTop:14, textAlign:"center", fontSize:"0.75rem", color:S.muted}}>
                {!isSignup?"New here? ":"Already have an account? "}
                <button onClick={()=>{setAuthMode(!isSignup?"signup":"login");setEmailStep(0);}}
                  style={{color:S.terra, fontWeight:700, background:"none", border:"none", cursor:"pointer"}}>
                  {!isSignup?"Sign Up Free":"Login"}
                </button>
              </div>
              {!isSignup && (
                <div style={{marginTop:6, textAlign:"center"}}>
                  <button onClick={()=>nav("reset-password")} style={{fontSize:"0.72rem", color:S.terra, background:"none", border:"none", cursor:"pointer"}}>Forgot password?</button>
                </div>
              )}
            </>
          )}

          {/* ── STEP 1: Verify code ── */}
          {emailStep===1 && (
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:"3rem", marginBottom:12}}>📧</div>
              <p style={{fontSize:"0.85rem", color:S.charcoal, marginBottom:4, fontWeight:600}}>Check your email</p>
              <p style={{fontSize:"0.72rem", color:S.muted, marginBottom:4}}>We sent a 6-digit code to</p>
              <p style={{fontSize:"0.78rem", color:S.terra, fontWeight:700, marginBottom:20}}>{fEmail}</p>
              {/* OTP input – controlled so verify button can read value directly */}
              <input
                className="cd-input"
                placeholder="· · · · · ·"
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                value={enteredCode}
                onChange={e=>setEnteredCode(e.target.value.replace(/[^0-9]/g,"").slice(0,6))}
                style={{textAlign:"center", fontSize:"1.8rem", letterSpacing:"0.5em", padding:"14px", width:"100%"}}/>
              <p style={{fontSize:"0.65rem", color:"#A8A298", margin:"8px 0 16px"}}>
                Didn't receive it? Check your spam folder or{" "}
                <button onClick={async()=>{
                  const code=String(Math.floor(100000+Math.random()*900000));
                  setVerifyCode(code);
                  const fd=new FormData();fd.append("email",fEmail);fd.append("code",code);fd.append("name",fName.split(" ")[0]);
                  try{await fetch(API_BASE+"/submit_send_code.php",{method:"POST",body:fd});}catch(e){}
                  showToast("New code sent to "+fEmail);
                }} style={{background:"none",border:"none",color:S.terra,cursor:"pointer",fontSize:"0.65rem",textDecoration:"underline",fontFamily:"inherit",padding:0}}>
                  resend code
                </button>
              </p>
              <button onClick={()=>{
                if(!enteredCode||enteredCode.length<6){showToast("Please enter the 6-digit code","error");return;}
                if(enteredCode!==verifyCode){showToast("Incorrect code – please try again","error");return;}
                setEmailStep(2);
              }} className="cd-btn-primary" style={{width:"100%", padding:"12px"}}
                disabled={enteredCode.length<6}>
                {enteredCode.length<6 ? `Enter code (${enteredCode.length}/6)` : "Verify & Continue →"}
              </button>

            </div>
          )}

          {/* ── STEP 2: Complete ── */}
          {emailStep===2 && (
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:"3rem", marginBottom:12}}>✅</div>
              <p style={{fontSize:"0.9rem", color:S.charcoal, marginBottom:6, fontWeight:600}}>Email verified!</p>
              <p style={{fontSize:"0.75rem", color:S.muted, marginBottom:20}}>Welcome, <strong>{fName||fEmail.split("@")[0]}</strong>! Your account is ready.</p>
              <button onClick={async()=>{
                const rt=card||"consumer";
                const fullName=fName.trim()||"User";
                // Save to DB
                const fd=new FormData();
                fd.append("action","signup"); fd.append("name",fullName);
                fd.append("email",fEmail); fd.append("password",fPwd);
                fd.append("phone",(fCountry||"+1")+" "+(fPhone||""));
                fd.append("type",rt); fd.append("category",fCat); fd.append("city",fCity);
                let savedName=fullName;
                try{
                  const r=await fetch(API_BASE+"/submit_auth.php",{method:"POST",body:fd});
                  const d=await r.json();
                  if(d.csrf) setCsrfToken(d.csrf);
                  if(d.success){
                    // Don't log in yet – need email verification
                  } else if(!d.success){
                    if(d.message?.includes("already registered")){
                      showToast("Email already registered – please login","error"); return;
                    }
                    // DB error – still let them in but warn
                    console.warn("Signup DB error:",d.message);
                  }
                }catch(e){
                  console.warn("Signup fetch error:",e);
                }
                const u={type:rt,name:savedName,email:fEmail,isNew:true};
                // Mark email as verified in DB
                const vfd=new FormData();
                vfd.append("action","verify_email"); vfd.append("email",fEmail);
                fetch(API_BASE+"/submit_auth.php",{method:"POST",body:vfd}).catch(()=>{});
                setUser(u); setEmailStep(0);
                if(rt==="provider"){setStep(0);nav("onboarding");}
                else nav("consumer-dash");
                showToast("Welcome, "+savedName.split(" ")[0]+"! 🎉");
              }} className="cd-btn-primary" style={{width:"100%", padding:"12px"}}>
                {uType==="provider"?"Set Up My Profile →":"Go to My Dashboard →"}
              </button>
            </div>
          )}

          <p style={{marginTop:18, paddingTop:14, borderTop:`1px solid ${S.creamDark}`, textAlign:"center", fontSize:"0.68rem", color:S.muted}}>
            🔒 256-bit encrypted · SOC 2 compliant
          </p>
        </div>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: SEARCH
  // ────────────────────────────────────────────────────
  const SearchPage = () => (
    <div style={{maxWidth:1152, margin:"0 auto", padding:"28px 16px"}}>
      {/* Search context indicator – shows active query/category from header search */}
      {/* Active filter chips */}
      {(searchCat||filterLang||filterBadge) && (
        <div style={{display:"flex", gap:6, flexWrap:"wrap", marginBottom:12}}>
          {searchCat && <span style={{background:S.terra+"22", color:S.terra, fontSize:"0.68rem", padding:"3px 10px", borderRadius:99, border:`1px solid ${S.terra}44`, display:"flex", alignItems:"center", gap:4}}>
            {CATS.find(c=>c.id===searchCat)?.icon} {CATS.find(c=>c.id===searchCat)?.label}
            <button onClick={()=>setSearchCat("")} style={{background:"none",border:"none",cursor:"pointer",color:S.terra,padding:0,lineHeight:1}}>✕</button>
          </span>}
          {filterLang && <span style={{background:"#7B5EA722",color:"#7B5EA7",fontSize:"0.68rem",padding:"3px 10px",borderRadius:99,border:"1px solid #7B5EA744",display:"flex",alignItems:"center",gap:4}}>
            💬 {filterLang}
            <button onClick={()=>setFilterLang("")} style={{background:"none",border:"none",cursor:"pointer",color:"#7B5EA7",padding:0,lineHeight:1}}>✕</button>
          </span>}
          {filterBadge && <span style={{background:"#C49A2A22",color:"#C49A2A",fontSize:"0.68rem",padding:"3px 10px",borderRadius:99,border:"1px solid #C49A2A44",display:"flex",alignItems:"center",gap:4}}>
            🏆 {filterBadge==="elite"?"Elite":"Verified"}
            <button onClick={()=>setFilterBadge("")} style={{background:"none",border:"none",cursor:"pointer",color:"#C49A2A",padding:0,lineHeight:1}}>✕</button>
          </span>}
        </div>
      )}

      <div style={{display:"flex", gap:24}}>
        {/* Sidebar filters */}
        <div style={{width:200, flexShrink:0, display:"none"}} className="lg-show">

          <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20, position:"sticky", top:100}}>
            <h3 style={{fontWeight:700, color:S.charcoal, marginBottom:16, display:"flex", alignItems:"center", gap:6, fontSize:"0.85rem"}}>
              <Filter size={13}/> Filters
            </h3>
            <div style={{display:"flex", flexDirection:"column", gap:20}}>
              <div>
                <p className="cd-label" style={{marginBottom:8}}>Badge Level</p>
                {[{v:"",l:"All Levels"},{v:"elite",l:"🏆 Elite (25–30)"},{v:"verified",l:"✓ Verified (16–24)"},{v:"listed",l:"Listed"}].map(o=>(
                  <label key={o.v} style={{display:"flex", alignItems:"center", gap:8, cursor:"pointer", marginBottom:6}}>
                    <input type="radio" name="badge" checked={filterBadge===o.v} onChange={()=>setFilterBadge(o.v)} style={{accentColor:S.terra}}/>
                    <span style={{fontSize:"0.75rem", color:S.charLight}}>{o.l}</span>
                  </label>
                ))}
              </div>
              <div>
                <p className="cd-label">Language</p>
                <select className="cd-input" style={{marginTop:4, fontSize:"0.75rem", padding:"7px 10px"}} value={filterLang} onChange={e=>setFilterLang(e.target.value)}>
                  <option value="">Any Language</option>
                  {["English","Hindi","Gujarati","Tamil","Telugu","Punjabi","Bengali","Malayalam","Marathi"].map(l=><option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <p className="cd-label">Sort By</p>
                <select className="cd-input" style={{marginTop:4, fontSize:"0.75rem", padding:"7px 10px"}} value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                  <option value="rating">Highest Rated</option>
                  <option value="reviews">Most Reviewed</option>
                  <option value="score">Verification Score</option>
                  <option value="newest">Newest Listings</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div style={{flex:1, minWidth:0}}>
          <p style={{color:S.muted, fontSize:"0.72rem", marginBottom:12}}>
            <span style={{fontWeight:700, color:S.charcoal}}>{filtered.length}</span> verified professionals found
          </p>
          {filtered.length===0 ? (
            (() => {
              // Check if there are results when category filter is removed
              const globalResults = PROVIDERS.filter(p => {
                const q = searchQ.toLowerCase();
                const score = scoreProvider(p, q);
                return (q === "" || score > 0) && (!filterBadge || p.badge===filterBadge) && (!filterLang || p.lang.includes(filterLang));
              });
              const catLabel = CATS.find(c=>c.id===searchCat)?.label || "this category";
              return (
                <div style={{textAlign:"center", padding:"48px 16px"}}>
                  <Search size={36} style={{margin:"0 auto 14px", color:"#DDD8CE"}}/>
                  <p style={{color:S.charLight, fontWeight:700, fontSize:"0.9rem", marginBottom:6}}>
                    No results in {catLabel}
                  </p>
                  <div style={{marginTop:16,display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                    <button onClick={()=>{setFilterBadge("");setFilterLang("");setUserCity("All Cities");}} className="cd-btn-outline" style={{fontSize:"0.78rem",padding:"9px 20px"}}>Clear Filters</button>
                    <button onClick={()=>nav("post-need")} className="cd-btn-primary" style={{fontSize:"0.78rem",padding:"9px 20px"}}>Post a Need →</button>
                  </div>
                  {globalResults.length > 0 && searchCat ? (
                    <div>
                      <p style={{color:S.muted, fontSize:"0.78rem", marginBottom:14}}>
                        Showing results from All Categories
                      </p>
                      <div style={{display:"flex", flexDirection:"column", gap:12}}>
                        {globalResults.slice(0,3).map(p=><ProvCard key={p.id} p={p}/>)}
                      </div>
                      <button onClick={()=>setSearchCat("")} style={{marginTop:16, background:"none", border:"1px solid #DDD8CE", borderRadius:8, padding:"8px 18px", fontSize:"0.78rem", cursor:"pointer", color:S.charLight}}>
                        Show all results
                      </button>
                    </div>
                  ) : (
                <><p style={{color:S.muted, fontSize:"0.75rem", marginBottom:16}}>Try a different search term or browse all categories</p>
                    <button onClick={()=>nav("post-need")} className="cd-btn-outline" style={{margin:"0 auto", display:"block"}}>
                      Post a Need Instead – Get Contacted
                    </button></>
                  )}
                </div>
              );
            })()
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:12}}>
              {filtered.map(p=><ProvCard key={p.id} p={p}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: PROFILE
  // ────────────────────────────────────────────────────
  const Profile = () => {
    const p = selProv;

    // JSON-LD structured data for Google rich results
    useEffect(() => {
      if (!p) return;
      const existing = document.getElementById("cd-profile-ld");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.id   = "cd-profile-ld";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": p.name,
        "description": p.bio,
        "address": {"@type":"PostalAddress","addressLocality":p.city,"addressCountry":"US"},
        "url": "https://certifieddesi.com/profile/"+p.name.toLowerCase().replace(/[^a-z0-9]+/g,"-"),
        "aggregateRating": {"@type":"AggregateRating","ratingValue":p.rating,"reviewCount":p.reviews,"bestRating":5}
      });
      document.head.appendChild(script);
      return () => { const el = document.getElementById("cd-profile-ld"); if(el) el.remove(); };
    }, [p?.id]);

    if (!p) return null;
    return (
      <div style={{maxWidth:1024, margin:"0 auto", padding:"28px 16px"}}>
        <button onClick={()=>{setSearchCat(provFromCat||"");setSearchQ("");nav("search");}} className="cd-back">
          <ArrowLeft size={13}/> Back to results {provFromCat ? `in ${CATS.find(c=>c.id===provFromCat)?.label||""}` : ""}
        </button>
        <div style={{display:"grid", gridTemplateColumns:"1fr", gap:20}} className="profile-grid">

          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24}}>
              <div style={{display:"flex", gap:20, flexWrap:"wrap"}}>
                <div style={{width:80, height:80, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"3rem", flexShrink:0}}>{p.img}</div>
                <div style={{flex:1}}>
                  <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.8rem", color:S.charcoal}}>{p.name}</h1>
                  <p style={{color:S.terra, fontWeight:700, fontSize:"0.72rem", letterSpacing:"0.08em", textTransform:"uppercase", marginTop:2}}>{p.sub}</p>
                  <div style={{display:"flex", alignItems:"center", gap:8, marginTop:6}}>
                    <Stars n={Math.round(p.rating)}/>
                    <span style={{fontWeight:700, fontSize:"0.85rem"}}>{p.rating}</span>
                    <span style={{color:S.muted, fontSize:"0.72rem"}}>({p.reviews} verified reviews)</span>
                  </div>
                  <div style={{display:"flex", alignItems:"center", gap:4, color:S.muted, fontSize:"0.72rem", marginTop:4}}><MapPin size={10}/>{p.city}</div>
                  <div style={{marginTop:8}}><BadgePill badge={p.badge} score={p.score}/></div>
                </div>
              </div>
              <p style={{color:S.charLight, fontSize:"0.82rem", marginTop:16, lineHeight:1.7}}>{p.bio}</p>
              <div style={{display:"flex", flexWrap:"wrap", gap:8, marginTop:14}}>
                {[{icon:<Clock size={10}/>,text:`${p.resp} response`},{icon:"💬",text:p.lang.join(", ")},{icon:"💰",text:p.fee}].map((item,i)=>(
                  <span key={i} style={{display:"flex", alignItems:"center", gap:5, fontSize:"0.72rem", color:S.charLight, background:S.creamDark, border:`1px solid #DDD8CE`, padding:"6px 12px", borderRadius:8}}>
                    {item.icon} {item.text}
                  </span>
                ))}
              </div>
            </div>
            <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, overflow:"hidden"}}>
              <div style={{display:"flex", borderBottom:`1px solid #DDD8CE`}}>
                {["credentials","verification","reviews"].map(t=>(
                  <button key={t} onClick={()=>setProfTab(t)}
                    style={{padding:"12px 20px", fontSize:"0.72rem", fontWeight:700, textTransform:"capitalize", border:"none", cursor:"pointer", transition:"all 0.15s",
                      color:profTab===t?S.terra:S.muted, background:profTab===t?"#FDF9F5":"transparent",
                      borderBottom:profTab===t?`2px solid ${S.terra}`:"2px solid transparent"}}>
                    {t==="reviews" ? `Reviews (${selProv?.reviews||0})` : t.charAt(0).toUpperCase()+t.slice(1)}
                  </button>
                ))}
              </div>
              <div style={{padding:20}}>
                {profTab==="credentials" && (
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {p.creds.map(c=>(
                      <div key={c} style={{display:"flex", alignItems:"center", gap:8, background:S.creamDark, padding:"10px 16px", borderRadius:8}}>
                        <CheckCircle size={13} style={{color:S.terra, flexShrink:0}}/>
                        <span style={{fontSize:"0.82rem", color:S.charcoal, fontWeight:600}}>{c}</span>
                      </div>
                    ))}
                  </div>
                )}
                {profTab==="verification" && (
                  <div>
                    <p style={{fontSize:"0.72rem", color:S.muted, marginBottom:16}}>AI-monitored – license databases checked every 24 hours automatically.</p>
                    <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16}}>
                      {[{k:"id",l:"Identity Verified"},{k:"license",l:"License Active"},{k:"bg",l:"Background Clear"},{k:"ins",l:"E&O Insurance Active"},{k:"ref",l:"References Verified"}].map(v=>(
                        <div key={v.k} style={{display:"flex", alignItems:"center", gap:8, padding:10, borderRadius:8,
                          background:p.v[v.k]?S.creamDark:S.cream, border:`1px solid ${p.v[v.k]?"#C4522A33":"#DDD8CE"}`}}>
                          {p.v[v.k]?<CheckCircle size={13} style={{color:S.terra}}/>:<Circle size={13} style={{color:"#DDD8CE"}}/>}
                          <span style={{fontSize:"0.72rem", fontWeight:600, color:p.v[v.k]?S.charcoal:S.muted}}>{v.l}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{background:"#C49A2A0D", border:`1px solid #C49A2A33`, borderRadius:12, padding:16}}>
                      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
                        <span style={{fontWeight:800, color:S.charcoal, fontSize:"0.85rem"}}>Score: {p.score}/30</span>
                        <BadgePill badge={p.badge} score={p.score}/>
                      </div>
                      <div style={{background:"#DDD8CE", borderRadius:99, height:6, overflow:"hidden"}}>
                        <div style={{background:S.gold, height:6, borderRadius:99, width:`${(p.score/30)*100}%`}}/>
                      </div>
                    </div>
                  </div>
                )}
                {profTab==="reviews" && (
                  <div style={{display:"flex", flexDirection:"column", gap:16}}>
                    {REVIEWS.map((r,i)=>(
                      <div key={i} style={{paddingBottom:16, borderBottom:i<REVIEWS.length-1?`1px solid ${S.creamDark}`:"none"}}>
                        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6}}>
                          <div style={{display:"flex", alignItems:"center", gap:8}}>
                            <div style={{width:28, height:28, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, fontSize:"0.75rem", color:S.terra}}>{r.name[0]}</div>
                            <span style={{fontWeight:700, fontSize:"0.82rem"}}>{r.name}</span>
                            <span style={{background:"#C4522A11", color:S.terra, fontSize:"0.62rem", fontWeight:700, padding:"2px 8px", borderRadius:99}}>Verified Client</span>
                          </div>
                          <span style={{color:S.muted, fontSize:"0.7rem"}}>{r.date}</span>
                        </div>
                        <Stars n={r.r}/>
                        <p style={{color:S.charLight, fontSize:"0.8rem", marginTop:6, lineHeight:1.6}}>{r.text}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Sidebar connect */}
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20, position:"sticky", top:100}}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.15rem", color:S.charcoal, marginBottom:16}}>
                Connect with {p.name.split(",")[0]}
              </h3>
              {reqSent ? (
                <div style={{textAlign:"center", padding:"24px 0"}}>
                  <CheckCircle size={36} style={{color:S.terra, margin:"0 auto 8px"}}/>
                  <p style={{fontWeight:800, color:S.charcoal}}>Request Sent!</p>
                  <p style={{fontSize:"0.72rem", color:S.muted, marginTop:4}}>Expect a reply within {p.resp}</p>
                </div>
              ) : (
                <div style={{display:"flex", flexDirection:"column", gap:8}}>
                  <button onClick={()=>{
                    if(!user){showToast("Sign in to request a consultation","error");setAuthMode("login");setTimeout(()=>nav("auth"),800);return;}
                    setReqOpen(true);
                  }} className="cd-btn-primary" style={{padding:"12px"}}>📩 Request Consultation</button>
                  <button onClick={()=>{
                    if(!user){showToast("Sign in to message this professional","error");setAuthMode("login");setTimeout(()=>nav("auth"),800);return;}
                    setMsgOpen(true);
                  }} className="cd-btn-outline" style={{padding:"10px"}}>💬 Send Message</button>
                  <button onClick={()=>{
                  if(!user){setAuthMode("login");nav("auth");return;}
                  const already=savedProfiles.some(s=>s.id===selProv?.id);
                  if(already){setSavedProfiles(p=>p.filter(s=>s.id!==selProv?.id));showToast("Removed from saved");}
                  else{setSavedProfiles(p=>[...p,selProv]);showToast("Profile saved ❤️");}
                }} className="cd-btn-ghost" style={{padding:"9px"}}>
                  {savedProfiles.some(s=>s.id===selProv?.id)?"❤️ Saved":"🔖 Save Profile"}
                </button>
                {user && (
                  <button onClick={()=>setReviewOpen(true)}
                    className="cd-btn-ghost" style={{padding:"9px", fontSize:"0.78rem"}}>
                    ⭐ Leave a Review
                  </button>
                )}
                <button onClick={()=>{
                  const url = encodeURIComponent(window.location.href);
                  const name = encodeURIComponent(selProv?.name || "this professional");
                  window.open(`https://wa.me/?text=Check out ${name} on CertifiedDESI ${url}`,"_blank");
                }} className="cd-btn-ghost" style={{padding:"9px",fontSize:"0.78rem",color:"#25D366"}}>
                  📲 Share on WhatsApp
                </button>
                </div>
              )}
              <div style={{marginTop:16, paddingTop:16, borderTop:`1px solid ${S.creamDark}`, display:"flex", flexDirection:"column", gap:8}}>
                {[["Response Time",p.resp],["Fee Range",p.fee],["Languages",p.lang[0]+(p.lang.length>1?` +${p.lang.length-1}`:"")]].map(([k,v])=>(
                  <div key={k} style={{display:"flex", justifyContent:"space-between", fontSize:"0.72rem"}}>
                    <span style={{color:S.muted}}>{k}</span>
                    <span style={{fontWeight:700, color:S.charcoal}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            <div style={{background:"#C49A2A0D", border:`1px solid #C49A2A33`, borderRadius:12, padding:16}}>
              <p style={{fontWeight:700, color:S.charcoal, fontSize:"0.82rem", marginBottom:4}}>🛡️ CertifiedDESI Guarantee</p>
              <p style={{color:S.charLight, fontSize:"0.72rem", lineHeight:1.6}}>All professionals are verified. Any issues? Our team mediates and resolves disputes within 48 hours.</p>
            </div>
          </div>
        </div>
        {reqOpen && (
          <div style={{position:"fixed", inset:0, background:"rgba(28,27,25,0.6)", zIndex:202, display:"flex", alignItems:"center", justifyContent:"center", padding:16, touchAction:"none"}} onClick={()=>setReqOpen(false)}>
            <div style={{background:S.creamMid, borderRadius:16, padding:24, width:"100%", maxWidth:440, boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal, marginBottom:20}}>Request a Consultation</h3>
              <div style={{display:"flex", flexDirection:"column", gap:12}}>
                <div>
                  <label className="cd-label">Your Name</label>
                  <input className="cd-input" id="req-name" defaultValue={user?.name||""} placeholder="Your full name"/>
                </div>
                <div>
                  <label className="cd-label">Your Email *</label>
                  <input className="cd-input" id="req-email" type="email" defaultValue={user?.email||""} placeholder="you@email.com"/>
                </div>
                <div>
                  <label className="cd-label">What do you need help with?</label>
                  <textarea className="cd-input" rows={3} id="req-note-input" placeholder="Describe your situation briefly..." defaultValue={reqNote} onBlur={e=>setReqNote(e.target.value)}/>
                </div>
                <div>
                  <label className="cd-label">Preferred Contact Method</label>
                  <select className="cd-input">
                    <option>Phone Call</option><option>Video Call (Zoom/Meet)</option><option>In-Person</option><option>Email Only</option>
                  </select>
                </div>
              </div>
              <div style={{display:"flex", gap:8, marginTop:20}}>
                <button onClick={()=>setReqOpen(false)} className="cd-btn-ghost" style={{flex:1}}>Cancel</button>
                <button onClick={async()=>{
                  const rEmail=document.getElementById("req-email")?.value||"";
                  const rName2=document.getElementById("req-name")?.value||"";
                  const rNote=document.getElementById("req-note-input")?.value||"";
                  if(!rName2.trim()){showToast("Please enter your name","error");return;}
                  if(!rEmail.trim()||!rEmail.includes("@")){showToast("Please enter a valid email address","error");return;}
                  if(!rNote.trim()){showToast("Please describe what you need help with","error");return;}
                  const fd=new FormData();
                  fd.append("provider_name",selProv?.name||"");
                  fd.append("provider_id",String(selProv?.id||0));
                  fd.append("requester_name",document.getElementById("req-name")?.value||user?.name||"");
                  fd.append("requester_email",rEmail);
                  fd.append("note",document.getElementById("req-note-input")?.value||reqNote||"");
                  try{
                    const _t1=sessionStorage.getItem("cd_token")||"";
                    const r=await fetch(API_BASE+"/submit_consultation.php",{method:"POST",body:fd,headers:_t1?{"Authorization":"Bearer "+_t1}:{}});
                    const d=await r.json();
                    if(d.success){setReqOpen(false);setReqSent(true);setReqNote("");showToast("Consultation request sent! ✓");}
                    else showToast(d.message||"Something went wrong","error");
                  }catch(e){setReqOpen(false);setReqSent(true);showToast("Consultation request sent! ✓");}
                }} className="cd-btn-primary" style={{flex:1}}>Send Request</button>
              </div>
            </div>
          </div>
        )}

        {/* ── MESSAGE MODAL ── */}
        {msgOpen && (
          <div style={{position:"fixed",inset:0,background:"rgba(28,27,25,0.6)",zIndex:202,display:"flex",alignItems:"center",justifyContent:"center",padding:16,touchAction:"none"}} onClick={()=>setMsgOpen(false)}>
            <div style={{background:S.creamMid,borderRadius:16,padding:24,width:"100%",maxWidth:440,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.4rem",color:S.charcoal,marginBottom:4}}>
                Message {selProv?.name?.split(",")[0]}
              </h3>
              <p style={{fontSize:"0.72rem",color:S.muted,marginBottom:16,lineHeight:1.5}}>
                Your message will be sent securely. The professional will reply via CertifiedDESI – contact details are not shared directly to protect both parties.
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:12}}>
                <div>
                  <label className="cd-label">Your Name</label>
                  <input className="cd-input" id="msg-name" defaultValue={user?.name||""} placeholder="Your full name"/>
                </div>
                <div>
                  <label className="cd-label">Your Email *</label>
                  <input className="cd-input" id="msg-email" type="email" defaultValue={user?.email||""} placeholder="you@email.com"/>
                </div>
                <div>
                  <label className="cd-label">Subject</label>
                  <input className="cd-input" id="msg-subject" placeholder="e.g. H-1B consultation inquiry"/>
                </div>
                <div>
                  <label className="cd-label">Message *</label>
                  <textarea className="cd-input" rows={4} id="msg-body" placeholder="Describe what you need help with. The more detail you provide, the better the professional can assess your situation."/>
                </div>
                <div style={{background:"#C49A2A0D",border:"1px solid #C49A2A33",borderRadius:8,padding:"10px 12px",fontSize:"0.68rem",color:S.charLight}}>
                  🛡️ Messages are kept within CertifiedDESI. We don't share your personal contact info until you choose to.
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:16}}>
                <button onClick={()=>setMsgOpen(false)} className="cd-btn-ghost" style={{flex:1}}>Cancel</button>
                <button onClick={async()=>{
                  const mEmail=document.getElementById("msg-email")?.value||"";
                  const mBody=document.getElementById("msg-body")?.value||"";
                  if(!mEmail||!mBody.trim()){showToast("Please fill in email and message","error");return;}
                  const fd=new FormData();
                  fd.append("sender_id",String(user?.id||0));
                  fd.append("sender_name",document.getElementById("msg-name")?.value||user?.name||"");
                  fd.append("sender_type","consumer");
                  fd.append("recipient_id",String(selProv?.id||0));
                  fd.append("provider_name",selProv?.name||"");
                  fd.append("subject",document.getElementById("msg-subject")?.value||"New message");
                  fd.append("body",mBody);
                  fd.append("user_email",mEmail);
                  try{
                    const _t2=sessionStorage.getItem("cd_token")||"";
                    const r=await fetch(API_BASE+"/submit_message.php",{method:"POST",body:fd,headers:_t2?{"Authorization":"Bearer "+_t2}:{}});
                    const d=await r.json();
                    if(d.success){setMsgOpen(false);showToast("Message sent! "+selProv?.name?.split(",")[0]+" will be notified. ✓");}
                    else showToast(d.message||"Something went wrong","error");
                  }catch(e){setMsgOpen(false);showToast("Message sent! ✓");}
                }} className="cd-btn-primary" style={{flex:1}}>Send Message</button>
              </div>
            </div>
          </div>
        )}

        {/* ── REVIEW MODAL ── */}
        {reviewOpen && (
          <div style={{position:"fixed",inset:0,background:"rgba(28,27,25,0.6)",zIndex:202,display:"flex",alignItems:"center",justifyContent:"center",padding:16,touchAction:"none"}} onClick={()=>setReviewOpen(false)}>
            <div style={{background:S.creamMid,borderRadius:16,padding:24,width:"100%",maxWidth:420,boxShadow:"0 20px 60px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.4rem",color:S.charcoal,marginBottom:4}}>
                Review {selProv?.name?.split(",")[0]}
              </h3>
              <p style={{fontSize:"0.72rem",color:S.muted,marginBottom:16}}>Only clients who have used this professional's services should leave a review.</p>
              {/* Star rating */}
              <div style={{display:"flex",gap:8,marginBottom:16,justifyContent:"center"}}>
                {[1,2,3,4,5].map(star=>(
                  <button key={star} onClick={()=>setReviewRating(star)}
                    style={{background:"none",border:"none",cursor:"pointer",fontSize:"2rem",
                      color:star<=reviewRating?"#C49A2A":"#DDD8CE",transition:"color 0.15s"}}>★</button>
                ))}
              </div>
              <p style={{textAlign:"center",fontSize:"0.72rem",color:S.muted,marginBottom:16}}>
                {reviewRating===0?"Tap to rate":["","Poor","Fair","Good","Very Good","Excellent"][reviewRating]+" – "+reviewRating+"/5 stars"}
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:10}}>
                <div>
                  <label className="cd-label">Your Name</label>
                  <input className="cd-input" id="rev-name" defaultValue={user?.name||""} placeholder="Your name"/>
                </div>
                <div>
                  <label className="cd-label">Your Email</label>
                  <input className="cd-input" id="rev-email" type="email" defaultValue={user?.email||""} placeholder="for verification only, not shown"/>
                </div>
                <div>
                  <label className="cd-label">Your Review (optional)</label>
                  <textarea className="cd-input" rows={3} id="rev-text" placeholder="Share your experience – what did they help with, how was the service?"/>
                </div>
              </div>
              <div style={{display:"flex",gap:8,marginTop:16}}>
                <button onClick={()=>setReviewOpen(false)} className="cd-btn-ghost" style={{flex:1}}>Cancel</button>
                <button onClick={async()=>{
                  if(reviewRating===0){showToast("Please select a star rating","error");return;}
                  const rName=document.getElementById("rev-name")?.value||user?.name||"";
                  if(!rName){showToast("Please enter your name","error");return;}
                  const fd=new FormData();
                  fd.append("provider_id",String(selProv?.id||0));
                  fd.append("provider_name",selProv?.name||"");
                  fd.append("reviewer_id",String(user?.id||0));
                  fd.append("reviewer_name",rName);
                  fd.append("reviewer_email",document.getElementById("rev-email")?.value||user?.email||"");
                  fd.append("rating",String(reviewRating));
                  fd.append("review_text",document.getElementById("rev-text")?.value||"");
                  try{
                    const _t3=sessionStorage.getItem("cd_token")||"";
                    const r=await fetch(API_BASE+"/submit_review.php",{method:"POST",body:fd,headers:_t3?{"Authorization":"Bearer "+_t3}:{}});
                    const d=await r.json();
                    if(d.success){setReviewOpen(false);setReviewRating(0);showToast("Review submitted – thank you! ⭐");}
                    else showToast(d.message||"Something went wrong","error");
                  }catch(e){setReviewOpen(false);showToast("Review submitted! ⭐");}
                }} className="cd-btn-primary" style={{flex:1}}>Submit Review</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: CONSUMER DASH
  // ────────────────────────────────────────────────────
  const ConsumerDash = () => (
    <div style={{maxWidth:1024, margin:"0 auto", padding:"32px 16px"}}>
      <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12}}>
        <div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal}}>Welcome back, {user?.name?.split(" ")[0]}! 👋</h1>
          <p style={{color:S.muted, fontSize:"0.8rem", marginTop:4}}>Find and connect with verified Desi professionals</p>
        </div>
        <button onClick={()=>goSearch("","")} className="cd-btn-primary">Find Professionals</button>
      </div>
      <div style={{background:S.charcoal, borderRadius:16, padding:24, marginBottom:24, color:S.cream}}>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.4rem", marginBottom:14}}>What do you need help with today?</h2>
        <div style={{display:"flex", gap:8, marginBottom:12}}>
          <div style={{flex:1, display:"flex", alignItems:"center", gap:8, background:S.cream, borderRadius:8, padding:"0 12px"}}>
            <Search size={14} style={{color:S.muted}}/>
            <input
              id="dash-search-input"
              style={{flex:1, border:"none", outline:"none", fontSize:"0.85rem", color:S.charcoal, background:"transparent", padding:"10px 0"}}
              placeholder='"H1B lawyer" or "NRI tax CPA"'
              onKeyDown={e=>{if(e.key==="Enter"){e.preventDefault();goSearch(e.target.value,"");}}}
            />
          </div>
          <button onClick={()=>goSearch(document.getElementById("dash-search-input")?.value||"","")} style={{background:S.gold, color:S.charcoal, padding:"0 18px", borderRadius:8, fontWeight:700, fontSize:"0.82rem", border:"none", cursor:"pointer"}}>Go</button>
        </div>
        <div style={{display:"flex", flexWrap:"wrap", gap:6}}>
          {["H-1B Lawyer","NRI Tax CPA","Green Card Attorney","Buy a Home","Financial Advisor"].map(q=>(
            <button key={q} onClick={()=>goSearch(q,"")}
              className="cd-pill-light">
              {q}
            </button>
          ))}
        </div>
      </div>
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:14, marginBottom:32}}>
        {[
          {icon:"📋",l:"My Requests",v:"Post a need to get started",fn:()=>nav("post-need")},
          {icon:"💬",l:"Message a Pro",v:"Contact verified professionals",fn:()=>goSearch("","")},
          {icon:"🔖",l:"Saved Pros",v:`${savedProfiles.length} saved`,fn:()=>nav("saved")}
        ].map(c=>(
          <div key={c.l} className="cd-card" onClick={c.fn} style={{padding:20, cursor:"pointer"}}>
            <div style={{fontSize:"1.6rem", marginBottom:8}}>{c.icon}</div>
            <div style={{fontWeight:700, color:S.charcoal, fontSize:"0.82rem"}}>{c.l}</div>
            <div style={{color:S.terra, fontSize:"0.7rem", marginTop:2}}>{c.v}</div>
          </div>
        ))}
      </div>
      <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.6rem", color:S.charcoal, marginBottom:16}}>Recommended for You</h2>
      <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))", gap:14}}>
        {PROVIDERS.slice(0,4).map(p=><ProvCard key={p.id} p={p}/>)}
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: ONBOARDING
  // ────────────────────────────────────────────────────
  const Onboarding = () => (
    <div style={{maxWidth:640, margin:"0 auto", padding:"32px 16px"}}>
      <div style={{marginBottom:24}}>
        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8}}>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.6rem", color:S.charcoal}}>Set Up Your CertifiedDESI Profile</h1>
          <span style={{fontSize:"0.72rem", color:S.muted, fontWeight:700}}>Step {step+1}/{STEPS_ONB.length}</span>
        </div>
        <div style={{display:"flex", gap:4, marginBottom:6}}>
          {STEPS_ONB.map((_,i)=>(
            <div key={i} style={{flex:1, height:4, borderRadius:99, transition:"background 0.2s", background:i<=step?S.terra:"#DDD8CE"}}/>
          ))}
        </div>
        <p style={{fontSize:"0.75rem", color:S.terra, fontWeight:700}}>{STEPS_ONB[step]}</p>
      </div>
      <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:28, boxShadow:"0 2px 16px rgba(0,0,0,0.04)"}}>
        {step===0 && (
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <div style={{background:"#C4522A0D", border:`1px solid #C4522A22`, borderRadius:10, padding:"10px 14px", fontSize:"0.75rem", color:S.terra, fontWeight:600}}>
              👋 Complete all steps to get verified and start receiving qualified leads from the Desi community.
            </div>
            {[{l:"Full Name / Business Name *",ph:"e.g. Rajesh Sharma, CPA"},{l:"City & State *",ph:"e.g. Houston, TX"},{l:"Professional Email *",ph:"rajesh@myaccounting.com"}].map(f=>(
              <div key={f.l}><label className="cd-label">{f.l}</label><input className="cd-input" placeholder={f.ph}/></div>
            ))}
            <div>
              <label className="cd-label">Service Category *</label>
              <select className="cd-input">{CATS.map(c=><option key={c.id}>{c.icon} {c.label}</option>)}</select>
            </div>
            <div>
              <label className="cd-label" style={{marginBottom:8}}>Languages Spoken</label>
              <div style={{display:"flex", flexWrap:"wrap", gap:12}}>
                {["Hindi","Gujarati","Tamil","Telugu","Punjabi","Bengali","Malayalam","Marathi","English"].map(l=>(
                  <label key={l} style={{display:"flex", alignItems:"center", gap:6, cursor:"pointer"}}>
                    <input type="checkbox" name="onb-lang" value={l} style={{accentColor:S.terra, width:13, height:13}}/><span style={{fontSize:"0.75rem", color:S.charLight}}>{l}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        {step===1 && (
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <div>
              <label className="cd-label">Professional Bio *</label>
              <textarea className="cd-input" rows={4} id="onb-bio" placeholder="Describe your experience, specializations, and what makes you unique..."/>
              <p style={{fontSize:"0.7rem", color:S.muted, marginTop:4}}>💡 Mention languages, years of experience, and NRI specializations. Detailed bios get 3x more inquiries.</p>
            </div>
            {[{l:"License Number(s) *",ph:"State bar #, CPA cert #, FINRA CRD#..."},{l:"Professional Certifications",ph:"CPA, CFP, CLU, Esq., EA..."},{l:"Fee Range",ph:"e.g. $150–$500/hr"}].map(f=>(
              <div key={f.l}><label className="cd-label">{f.l}</label><input className="cd-input" placeholder={f.ph}/></div>
            ))}
          </div>
        )}
        {step===2 && (
          <div style={{display:"flex", flexDirection:"column", gap:12}}>
            <p style={{fontSize:"0.72rem", color:S.charLight, background:S.creamDark, border:`1px solid #DDD8CE`, borderRadius:8, padding:"10px 12px"}}>🔒 All documents are AES-256 encrypted and used only for verification. Never shared with third parties.</p>
            {[{k:"gov_id",l:"Government Photo ID",d:"Passport or Driver's License",req:true},{k:"license_cert",l:"Professional License Certificate",d:"CPA cert, Bar card, Insurance license...",req:true},{k:"eo_ins",l:"E&O / Malpractice Insurance",d:"Current Certificate of Insurance (COI)",req:true},{k:"biz_cert",l:"Business Registration",d:"LLC/Corp certificate",req:false},{k:"refs",l:"Client References (3 minimum)",d:"PDF with contact details",req:false}].map(doc=>(
              <div key={doc.k} style={{border:`1px solid #DDD8CE`, borderRadius:10, padding:16, background:S.cream}}>
                <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10}}>
                  <div>
                    <p style={{fontWeight:700, color:S.charcoal, fontSize:"0.82rem"}}>{doc.l} {doc.req&&<span style={{color:S.terra}}>*</span>}</p>
                    <p style={{color:S.muted, fontSize:"0.7rem", marginTop:2}}>{doc.d}</p>
                  </div>
                  {uploads[doc.k] && <CheckCircle size={14} style={{color:S.terra, flexShrink:0}}/>}
                </div>
                {uploads[doc.k] ? (
                  <div style={{display:"flex", alignItems:"center", gap:8, background:S.creamDark, border:`1px solid #DDD8CE`, borderRadius:7, padding:"8px 12px", fontSize:"0.72rem", color:S.terra}}>
                    <FileText size={11}/>{uploads[doc.k]}
                    <button onClick={()=>setUploads(p=>{const n={...p};delete n[doc.k];return n;})} style={{marginLeft:"auto", background:"none", border:"none", color:S.muted, cursor:"pointer"}}><X size={11}/></button>
                  </div>
                ) : (
                  <label className="cd-upload-label">
                    <Upload size={12} style={{color:S.muted}}/><span style={{fontSize:"0.72rem", color:S.muted}}>Click to upload (PDF, JPG, PNG)</span>
                    <input type="file" style={{display:"none"}} accept=".pdf,.jpg,.png" onChange={e=>handleUpload(doc.k,e.target.files[0])}/>
                  </label>
                )}
              </div>
            ))}
          </div>
        )}
        {step===3 && (
          <div>
            <div style={{textAlign:"center", padding:"16px 0 24px"}}>
              <Shield size={48} style={{color:S.terra, margin:"0 auto 12px"}}/>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.7rem", color:S.charcoal}}>30-Point Verification Initiated</h3>
              <p style={{color:S.muted, fontSize:"0.82rem", marginTop:8, lineHeight:1.7}}>Our AI system and human review team will verify your credentials within 24–48 hours.</p>
            </div>
            <div style={{display:"flex", flexDirection:"column", gap:6}}>
              {["Identity & Biometric Check","Professional License Database Check","Federal & State Background Check","Business Entity Verification","E&O Insurance Verification","OFAC / AML Screening","Client Reference Verification"].map((v,i)=>(
                <div key={v} style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:S.cream, borderRadius:8, border:`1px solid #DDD8CE`}}>
                  <span style={{fontSize:"0.75rem", color:S.charcoal, fontWeight:600}}>{v}</span>
                  <span style={{fontSize:"0.62rem", fontWeight:800, padding:"3px 10px", borderRadius:99,
                    background:i===0?"#C4522A22":S.creamDark, color:i===0?S.terra:S.muted}}>
                    {i===0?"In Progress":"Queued"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {step===4 && (
          <div style={{textAlign:"center", padding:"8px 0"}}>
            <div style={{fontSize:"3rem", marginBottom:16}}>🎉</div>
            <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:8}}>You're Live on CertifiedDESI!</h3>
            <p style={{color:S.muted, fontSize:"0.82rem", marginBottom:24, lineHeight:1.7}}>Verification is processing. Here's how to get your first lead fast:</p>
            <div style={{background:"#C49A2A0D", border:`1px solid #C49A2A33`, borderRadius:12, padding:20, textAlign:"left", marginBottom:24}}>
              <p style={{fontWeight:800, color:S.charcoal, marginBottom:12, fontSize:"0.85rem"}}>🚀 Best Practices to Get Your First Lead</p>
              <div style={{display:"flex", flexDirection:"column", gap:6}}>
                {["Add a professional headshot – profiles with photos get 7x more clicks","Write a detailed bio mentioning languages, NRI specializations, and years of experience","Set up auto-response for new inquiries within 24 hours","Ask 3 existing clients to leave verified reviews on your profile","Complete all 30 verification points to earn the Elite badge"].map((t,i)=>(
                  <div key={i} style={{display:"flex", alignItems:"flex-start", gap:8, fontSize:"0.75rem", color:S.charLight}}>
                    <span style={{color:S.gold, flexShrink:0}}>✓</span>{t}
                  </div>
                ))}
              </div>
            </div>
            <button onClick={()=>{setStep(0);nav("provider-dash");}} className="cd-btn-primary" style={{width:"100%", padding:"12px"}}>
              Go to My Dashboard →
            </button>
          </div>
        )}
        <div style={{display:"flex", gap:10, marginTop:24}}>
          {step>0 && <button onClick={()=>setStep(s=>s-1)} className="cd-btn-ghost" style={{flex:1}}>← Back</button>}
          {step<STEPS_ONB.length-1 && (
            <button onClick={()=>{
              if(step===0){showToast("Info saved ✓");}
              if(step===1){showToast("Details saved ✓");}
              if(step===2){
                const required=["gov_id","license_cert","eo_ins"];
                const missing=required.filter(k=>!uploads[k]);
                if(missing.length>0){showToast("Please upload required documents first","error");return;}
              }
              setStep(s=>s+1);
            }} className="cd-btn-primary" style={{flex:1}}>
              {step===STEPS_ONB.length-2?"Submit for Verification ✓":"Continue →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: PROVIDER DASH
  // ────────────────────────────────────────────────────
  const ProviderDash = () => {
    const [provData, setProvData] = React.useState(null);
    const [provLoading, setProvLoading] = React.useState(true);

    React.useEffect(() => {
      if (!user?.id) return;
      // Try v2 provider endpoint, fall back to seed data gracefully
      fetch(API_BASE+"/provider.php?action=get")
        .then(r=>r.ok?r.json():null)
        .then(d=>{
          if(d?.success && d.profile) setProvData(d.profile);
          else setProvData(null);
        })
        .catch(()=>setProvData(null))
        .finally(()=>setProvLoading(false));
    }, [user?.id]);

    // Fallback to PROVIDERS seed data if not found in DB (demo mode)
    const p = provData ? {
      name: provData.name,
      img:  provData.avatar_url || "👤",
      cat:  provData.category || "",
      badge: provData.badge || "listed",
      score: provData.score || 0,
      bio:  provData.bio || "",
      fee:  provData.fee_range || "",
      resp: provData.response_time || "",
      lang: (provData.languages||"").split(",").map(l=>l.trim()).filter(Boolean),
      city: provData.city || "",
      reviews: provData.review_count || 0,
      rating:  provData.avg_rating || 0,
      creds:   (provData.certifications||"").split(",").map(l=>l.trim()).filter(Boolean),
      v: provData.verification || {id:false,license:false,bg:false,ins:false,ref:false},
    } : PROVIDERS[0];

    if (provLoading) return (
      <div style={{maxWidth:1152,margin:"0 auto",padding:"80px 16px",textAlign:"center"}}>
        <div style={{fontSize:"2rem",marginBottom:16}}>⏳</div>
        <p style={{color:S.muted}}>Loading your dashboard...</p>
      </div>
    );
    const sideItems = [
      {id:"overview",      icon:<Home size={13}/>,        l:"Overview"},
      {id:"leads",         icon:<Bell size={13}/>,        l:"Leads",    c:3},
      {id:"messages",      icon:<MessageSquare size={13}/>,l:"Messages", c:2},
      {id:"profile-edit",  icon:<User size={13}/>,        l:"Edit Profile"},
      {id:"verification",  icon:<Shield size={13}/>,      l:"Verification"},
      {id:"analytics",     icon:<BarChart2 size={13}/>,   l:"Analytics"},
      {id:"subscription",  icon:<Award size={13}/>,       l:"Subscription"},
    ];
    return (
      <div style={{maxWidth:1152, margin:"0 auto", padding:"32px 16px"}}>
        <div style={{display:"flex", gap:24}}>
          {/* Sidebar */}
          <div style={{width:200, flexShrink:0}} className="lg-show">
            <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:16, position:"sticky", top:100}}>
              <div style={{textAlign:"center", marginBottom:16, paddingBottom:16, borderBottom:`1px solid ${S.creamDark}`}}>
                <div style={{width:52, height:52, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem", margin:"0 auto 8px"}}>{p.img}</div>
                <p style={{fontWeight:700, color:S.charcoal, fontSize:"0.75rem"}}>{p.name}</p>
                <div style={{marginTop:6}}><BadgePill badge={p.badge} score={p.score}/></div>
              </div>
              {sideItems.map(s=>(
                <button key={s.id} onClick={()=>setDashSec(s.id)}
                  className={`cd-side-btn ${dashSec===s.id?"active":""}`}>
                  {s.icon}{s.l}
                  {s.c && <span style={{marginLeft:"auto", padding:"1px 7px", borderRadius:99, fontSize:"0.62rem", fontWeight:800,
                    background:dashSec===s.id?"rgba(255,255,255,0.2)":"#C4522A22", color:dashSec===s.id?S.cream:S.terra}}>{s.c}</span>}
                </button>
              ))}
              <button onClick={logout} style={{width:"100%", display:"flex", alignItems:"center", gap:8, padding:"8px 12px", borderRadius:8, fontSize:"0.75rem", color:S.terra, background:"transparent", border:"none", cursor:"pointer", marginTop:8, fontWeight:700}}>
                <LogOut size={13}/>Logout
              </button>
            </div>
          </div>
          <div style={{flex:1, minWidth:0}}>
            {dashSec==="overview" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Dashboard</h1>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:12, marginBottom:20}}>
                  {[{l:"Profile Views",v:"1,247",t:"+23% this month"},{l:"New Leads",v:"3",t:"2 today"},{l:"Active Inquiries",v:"8",t:"1 new"},{l:"Avg Response",v:"1.8h",t:"Top 10% ✓"}].map(m=>(
                    <div key={m.l} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:16}}>
                      <p style={{color:S.muted, fontSize:"0.7rem"}}>{m.l}</p>
                      <p style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2rem", fontWeight:700, color:S.charcoal, marginTop:2}}>{m.v}</p>
                      <p style={{color:S.terra, fontSize:"0.7rem", marginTop:4, fontWeight:700}}>{m.t}</p>
                    </div>
                  ))}
                </div>
                <div style={{background:S.charcoal, borderRadius:12, padding:20, marginBottom:20}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, color:S.cream, fontSize:"1.2rem", marginBottom:12}}>🤖 AI Profile Insights</h3>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {["📈 Your profile gets 40% fewer views than top CPAs in Houston – adding a headshot boosts clicks by 7x.",
                      "⚡ Providers responding within 1 hour get 3x more bookings. Your avg is 1.8h – set up auto-reply!",
                      "💡 Adding 'NRI tax' and 'FBAR specialist' to your bio could increase search visibility by 60%.",
                      "🏆 You are 2 verification points away from maintaining Elite status – upload your E&O renewal."].map((tip,i)=>(
                      <p key={i} style={{fontSize:"0.75rem", color:S.mutedLt, lineHeight:1.6}}>{tip}</p>
                    ))}
                  </div>
                </div>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginBottom:16}}>Recent Leads</h3>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {[{n:"Amit P.",s:"NRI Tax Filing",t:"2 hrs ago",st:"New"},{n:"Sunita K.",s:"Business Tax",t:"5 hrs ago",st:"Responded"},{n:"Rohit M.",s:"FBAR Filing",t:"1 day ago",st:"Closed"}].map((l,i)=>(
                      <div key={i} style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:12, background:S.cream, borderRadius:10}}>
                        <div style={{display:"flex", alignItems:"center", gap:10}}>
                          <div style={{width:32, height:32, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.75rem", fontWeight:800, color:S.terra}}>{l.n[0]}</div>
                          <div>
                            <p style={{fontWeight:700, fontSize:"0.82rem", color:S.charcoal}}>{l.n}</p>
                            <p style={{fontSize:"0.7rem", color:S.muted}}>{l.s} · {l.t}</p>
                          </div>
                        </div>
                        <span style={{fontSize:"0.7rem", fontWeight:800, padding:"3px 10px", borderRadius:99,
                          background:l.st==="New"?"#C4522A22":l.st==="Responded"?"#C49A2A22":S.creamDark,
                          color:l.st==="New"?S.terra:l.st==="Responded"?S.gold:S.muted}}>
                          {l.st}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {dashSec==="leads" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Leads</h1>
                <div style={{display:"flex", flexDirection:"column", gap:12}}>
                  {[{n:"Amit P.",s:"NRI Tax Filing",msg:"I need help filing taxes for India and US income. Can we schedule a call?",t:"2 hrs ago",st:"New"},{n:"Sunita K.",s:"Business Tax",msg:"Looking for a CPA to handle quarterly taxes for my S-Corp.",t:"5 hrs ago",st:"Responded"},{n:"Rohit M.",s:"FBAR Filing",msg:"Need advice on FBAR and foreign asset reporting. Have accounts in 3 countries.",t:"1 day ago",st:"Closed"}].map((l,i)=>(
                    <div key={i} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                      <div style={{display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:10}}>
                        <div style={{display:"flex", alignItems:"center", gap:10}}>
                          <div style={{width:36, height:36, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:S.terra}}>{l.n[0]}</div>
                          <div><p style={{fontWeight:700, fontSize:"0.85rem", color:S.charcoal}}>{l.n}</p><p style={{fontSize:"0.7rem", color:S.muted}}>{l.s} · {l.t}</p></div>
                        </div>
                        <span style={{fontSize:"0.7rem", fontWeight:800, padding:"3px 10px", borderRadius:99,
                          background:l.st==="New"?"#C4522A22":l.st==="Responded"?"#C49A2A22":S.creamDark,
                          color:l.st==="New"?S.terra:l.st==="Responded"?S.gold:S.muted}}>
                          {l.st}
                        </span>
                      </div>
                      <p style={{fontSize:"0.82rem", color:S.charLight, marginBottom:12}}>{l.msg}</p>
                      {l.st==="New" && (
                        <div style={{display:"flex", gap:8}}>
                          <button onClick={()=>showToast("Reply sent!")} className="cd-btn-primary" style={{padding:"7px 16px", fontSize:"0.75rem"}}>Reply</button>
                          <button onClick={()=>showToast("Lead declined")} className="cd-btn-ghost" style={{padding:"7px 16px", fontSize:"0.75rem"}}>Decline</button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dashSec==="messages" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Messages</h1>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, overflow:"hidden"}}>
                  {[{n:"Amit P.",last:"Can we schedule a call for Thursday?",t:"10 min ago",unread:true},{n:"Kavita R.",last:"Thank you, I'll send the documents over.",t:"2 hrs ago",unread:true},{n:"Sunil M.",last:"Great, see you then! Thanks again.",t:"Yesterday",unread:false}].map((m,i)=>(
                    <div key={i} onClick={()=>showToast("Chat with "+m.n+" – coming soon!")}
                      className="cd-msg-row">
                      <div style={{width:40, height:40, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:S.terra}}>{m.n[0]}</div>
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                          <p style={{fontSize:"0.82rem", fontWeight:m.unread?800:600, color:S.charcoal}}>{m.n}</p>
                          <p style={{fontSize:"0.7rem", color:S.muted}}>{m.t}</p>
                        </div>
                        <p style={{fontSize:"0.72rem", color:S.muted, overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis"}}>{m.last}</p>
                      </div>
                      {m.unread && <div style={{width:8, height:8, background:S.terra, borderRadius:"50%", flexShrink:0}}/>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dashSec==="profile-edit" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Edit Profile</h1>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24, display:"flex", flexDirection:"column", gap:16}}>
                  <div>
                    <p className="cd-label" style={{marginBottom:8}}>Profile Photo</p>
                    <div style={{display:"flex", alignItems:"center", gap:14}}>
                      <div style={{width:64, height:64, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.5rem"}}>{p.img}</div>
                      <label style={{border:"1px solid #DDD8CE", padding:"8px 16px", borderRadius:8, fontSize:"0.75rem", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontWeight:600}}>
                        <Upload size={12}/>Upload Photo
                        <input type="file" style={{display:"none"}} accept="image/*" onChange={e=>handleUpload("photo",e.target.files[0])}/>
                      {uploads.photo_url && <img src={uploads.photo_url} alt="preview" style={{width:40,height:40,borderRadius:"50%",objectFit:"cover",marginTop:6,border:"2px solid "+S.terra}}/>}
                      </label>
                      {uploads.photo && <span style={{fontSize:"0.72rem", color:S.terra}}><CheckCircle size={10}/> {uploads.photo}</span>}
                    </div>
                  </div>
                  {[{l:"Display Name",dv:p.name},{l:"Sub-specialty",dv:p.sub},{l:"Fee Range",dv:p.fee}].map(f=>(
                    <div key={f.l}><label className="cd-label">{f.l}</label><input className="cd-input" defaultValue={f.dv}/></div>
                  ))}
                  <div><label className="cd-label">Bio</label><textarea className="cd-input" rows={4} defaultValue={p.bio}/></div>
                  <button onClick={()=>showToast("Profile updated ✓")} className="cd-btn-primary" style={{width:"fit-content", padding:"10px 24px"}}>Save Changes</button>
                </div>
              </div>
            )}
            {dashSec==="verification" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Verification Status</h1>
                <div style={{background:"#C49A2A0D", border:`1px solid #C49A2A33`, borderRadius:12, padding:20, marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                  <div>
                    <p style={{fontWeight:800, color:S.charcoal}}>🏆 Certified Desi Elite Badge</p>
                    <p style={{color:S.gold, fontSize:"0.75rem", marginTop:2}}>Score: {p.score}/30 · Verified 2 days ago · Auto-renews annually</p>
                    <div style={{marginTop:10, background:"#DDD8CE", borderRadius:99, height:6, width:220}}>
                      <div style={{background:S.gold, height:6, borderRadius:99, width:`${(p.score/30)*100}%`}}/>
                    </div>
                  </div>
                  <Award size={40} style={{color:S.gold}}/>
                </div>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginBottom:16}}>30-Point Verification Checklist</h3>
                  {[["🪪 Identity (6 pts)",["Govt Photo ID","SSN Verified","Biometric Face Match","US Address Verified","Phone OTP","Professional Email"]],["🎓 Professional License (8 pts)",["License Active & Verified","Not Suspended/Revoked","Expiry Tracked","Education Credential","Professional Certifications","E&O Insurance Active","CPA Board Clear","State Disciplinary Clear"]],["🏢 Business Entity (6 pts)",["LLC/Corp Verified","Active Status","EIN Verified","Physical Address","Business Age 6m+","Bank Account Verified"]],["🔍 Background & Compliance (6 pts)",["Federal Criminal Clear","State Criminal Clear","Sex Offender Clear","OFAC/Sanctions Clear","Bankruptcy Clear","AML Screening Clear"]],["⭐ Community Trust (4 pts)",["3 References Verified","Rating 4.0+","Response Rate 80%+","Reviews Authentic"]]].map(([cat,items])=>(
                    <div key={cat} style={{marginBottom:16}}>
                      <p className="cd-label" style={{marginBottom:8}}>{cat}</p>
                      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:6}}>
                        {items.map(it=>(
                          <div key={it} style={{display:"flex", alignItems:"center", gap:6, fontSize:"0.72rem", color:S.terra, background:S.creamDark, border:`1px solid #DDD8CE`, padding:"8px 12px", borderRadius:8, fontWeight:600}}>
                            <CheckCircle size={10} style={{color:S.terra, flexShrink:0}}/>{it}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {dashSec==="analytics" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Analytics</h1>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:14, marginBottom:20}}>
                  {[{l:"Profile Views (30 days)",v:"1,247",s:"+23% vs last month"},{l:"Lead Conversion Rate",v:"34%",s:"Industry avg: 18%"},{l:"Total Inquiries",v:"42",s:"This month"},{l:"Avg Rating",v:"4.9 ⭐",s:"127 verified reviews"}].map(m=>(
                    <div key={m.l} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                      <p style={{color:S.muted, fontSize:"0.72rem"}}>{m.l}</p>
                      <p style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"2.2rem", fontWeight:700, color:S.charcoal, marginTop:4}}>{m.v}</p>
                      <p style={{color:S.terra, fontSize:"0.72rem", marginTop:4, fontWeight:700}}>{m.s}</p>
                    </div>
                  ))}
                </div>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginBottom:16}}>Search Rankings</h3>
                  <div style={{display:"flex", flexDirection:"column", gap:8}}>
                    {[{kw:"CPA Houston",pos:1,views:342},{kw:"NRI tax specialist Texas",pos:2,views:218},{kw:"Hindi speaking CPA",pos:1,views:187},{kw:"Indian accountant near me",pos:3,views:156}].map(s=>(
                      <div key={s.kw} style={{display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 14px", background:S.cream, borderRadius:10}}>
                        <span style={{fontSize:"0.82rem", color:S.charcoal, fontWeight:600}}>"{s.kw}"</span>
                        <div style={{display:"flex", alignItems:"center", gap:10}}>
                          <span style={{fontSize:"0.7rem", color:S.muted}}>{s.views} views</span>
                          <span style={{fontSize:"0.7rem", fontWeight:800, background:"#C4522A22", color:S.terra, padding:"3px 10px", borderRadius:99}}>#{s.pos}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {dashSec==="subscription" && (
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:20}}>Subscription & Billing</h1>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:14, marginBottom:20}}>
                  {[{n:"Listed",pr:"Free",f:["Basic profile","Search visible","5 leads/month","Basic badge"],curr:false},{n:"Verified Pro",pr:"",f:["Featured placement","Unlimited leads","AI optimizer","Priority support","Verified badge"],curr:false},{n:"Elite",pr:"",f:["#1 search placement","Everything in Pro","Analytics & insights","Competitor benchmark","Elite badge 🏆"],curr:true}].map(plan=>(
                    <div key={plan.n} style={{border:`2px solid ${plan.curr?S.gold:"#DDD8CE"}`, borderRadius:12, padding:20, background:plan.curr?"#C49A2A0A":S.creamMid}}>
                      {plan.curr && <div style={{fontSize:"0.62rem", fontWeight:800, color:S.gold, marginBottom:8, letterSpacing:"0.1em"}}>✓ CURRENT PLAN</div>}
                      <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal}}>{plan.n}</h3>
                      <p style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"1.8rem", fontWeight:700, color:S.terra, margin:"6px 0 14px"}}>{plan.pr}</p>
                      <ul style={{display:"flex", flexDirection:"column", gap:6, marginBottom:16}}>
                        {plan.f.map(f=>(
                          <li key={f} style={{display:"flex", alignItems:"center", gap:6, fontSize:"0.72rem", color:S.charLight}}>
                            <CheckCircle size={10} style={{color:S.terra, flexShrink:0}}/>{f}
                          </li>
                        ))}
                      </ul>
                      <button onClick={()=>showToast(plan.curr?"Already on this plan!":"Upgrade initiated!")}
                        style={{width:"100%", padding:"8px", borderRadius:8, fontSize:"0.75rem", fontWeight:700, border:"none", cursor:"pointer",
                          background:plan.curr?"#C49A2A33":S.terra, color:plan.curr?S.gold:S.cream}}>
                        {plan.curr?"Current Plan":"Upgrade"}
                      </button>
                    </div>
                  ))}
                </div>
                <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginBottom:8}}>🤖 AI Premium Calculator</h3>
                  <p style={{fontSize:"0.75rem", color:S.muted, marginBottom:12}}>Based on your category (Tax/CPA), city (Houston, TX), and current market demand:</p>
                  <div style={{background:"#C4522A0D", border:`1px solid #C4522A22`, borderRadius:10, padding:16}}>
                    <p style={{fontWeight:800, color:S.charcoal, fontSize:"0.85rem"}}>Recommended: Elite Plan @ </p>
                    <p style={{color:S.charLight, fontSize:"0.75rem", marginTop:6, lineHeight:1.7}}>Estimated ROI: <strong>8–12 new clients/month</strong> at avg $350/client = <strong>$2,800–$4,200 monthly revenue</strong> from platform leads. Payback period: under 1 day.</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: HOW IT WORKS
  // ────────────────────────────────────────────────────
  const HowItWorks = () => (
    <div>
      <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
        <div style={{maxWidth:640, margin:"0 auto"}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:16}}>How It Works</div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, lineHeight:1.1, marginBottom:16}}>Simple. Safe. Designed for the Diaspora.</h1>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>Whether you're looking to hire a trusted Desi professional or grow your practice in the South Asian community – CertifiedDESI makes it seamless.</p>
        </div>
      </section>
      {/* For Consumers */}
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <SectionHead label="For Consumers" title="Find & Hire with Confidence"/>
          <div style={{display:"flex", flexDirection:"column", gap:40}}>
            {[{n:"1",ic:"🔍",t:"Search by What Matters to You",d:"Use our AI-powered search to find professionals by category (CPA, Immigration Attorney, Financial Advisor), language (Hindi, Tamil, Gujarati), location, or specific need like 'H1B visa lawyer NJ' or 'NRI tax filing Houston'."},
              {n:"2",ic:"🏆",t:"Review 30-Point Verified Badges",d:"Every professional on CertifiedDESI carries a badge score from 1–30. Certified Desi Elite (25–30 pts) means their identity, professional license, background, insurance, and community references have all been verified. You know exactly who you're hiring."},
              {n:"3",ic:"💬",t:"Connect Directly – Free for Consumers",d:"Message directly, request a consultation, or post your specific need and let professionals come to you. Compare credentials, reviews, fee ranges, and response times side by side."},
              {n:"4",ic:"🤝",t:"Transact Safely with Escrow Protection",d:"For eligible services, use our built-in escrow to pay – funds are held securely and released only after you confirm the service was delivered. Disputes are resolved by our trust team within 48 hours."},
              {n:"5",ic:"⭐",t:"Leave a Verified Review",d:"After your service, leave an honest review. Your feedback is AI-verified for authenticity and helps the entire community make better decisions. Every review earns the professional trust points."}].map((s,i)=>(
              <div key={s.n} style={{display:"flex", gap:24, alignItems:"flex-start"}}>
                <div style={{width:56, height:56, background:S.terra, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                  <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:800, fontSize:"1.4rem"}}>{s.n}</span>
                </div>
                <div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.25rem", color:S.charcoal, marginBottom:8}}>{s.t}</h3>
                  <p style={{color:S.charLight, fontSize:"0.85rem", lineHeight:1.75}}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:40, textAlign:"center"}}>
            <button onClick={()=>goSearch("","")} className="cd-btn-primary" style={{padding:"14px 32px", fontSize:"0.9rem"}}>Find a Professional Now →</button>
          </div>
        </div>
      </section>
      {/* For Providers */}
      <section style={{padding:"72px 16px", background:S.creamDark}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <SectionHead label="For Professionals" title="Grow Your Practice with Qualified Desi Leads"/>
          <div style={{display:"flex", flexDirection:"column", gap:40}}>
            {[{n:"1",ic:"📝",t:"Create Your Free Listing",d:"Sign up and create your professional profile in minutes. Add your bio, specializations, languages, fee range, and service area. Your free listing is immediately visible in our directory."},
              {n:"2",ic:"🛡️",t:"Complete the 30-Point Verification",d:"Upload your credentials – government ID, professional license, E&O insurance, and references. Our AI system verifies against official state databases (Bar Association, CPA Board, FINRA, NMLS, Insurance Dept) within 24–48 hours."},
              {n:"3",ic:"🏆",t:"Earn Your Badge & Boost Visibility",d:"As you complete more verification points, your badge level rises from Listed → Verified Desi Pro → Certified Desi Elite. Higher badges rank higher in search and convert significantly more leads."},
              {n:"4",ic:"📩",t:"Receive & Respond to Qualified Leads",d:"Consumers contact you directly through the platform. Respond to inquiries, accept consultation requests, and manage all your leads from your provider dashboard. Set up auto-replies to ensure you never miss a lead."},
              {n:"5",ic:"📈",t:"Track & Optimize with AI Insights",d:"Your dashboard provides real-time analytics – profile views, search rankings, lead conversion rates, and AI-powered tips to optimize your profile for more visibility and bookings."}].map((s,i)=>(
              <div key={s.n} style={{display:"flex", gap:24, alignItems:"flex-start"}}>
                <div style={{width:56, height:56, background:S.charcoal, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                  <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:800, fontSize:"1.4rem"}}>{s.n}</span>
                </div>
                <div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.25rem", color:S.charcoal, marginBottom:8}}>{s.t}</h3>
                  <p style={{color:S.charLight, fontSize:"0.85rem", lineHeight:1.75}}>{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:40, textAlign:"center"}}>
            <button onClick={()=>{setAuthMode("signup");setUType("provider");nav("auth");}} className="cd-btn-primary" style={{padding:"14px 32px", fontSize:"0.9rem"}}>List Your Business Free →</button>
          </div>
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: SAFETY & TRUST
  // ────────────────────────────────────────────────────
  const Safety = () => (
    <div>
      <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
        <div style={{maxWidth:640, margin:"0 auto"}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:16}}>Safety & Trust</div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, marginBottom:16}}>Built on Verification.<br/><span style={{color:S.terra}}>Backed by Technology.</span></h1>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>Trust isn't just a promise on CertifiedDESI – it's a system. Every professional undergoes rigorous verification that rivals the standards of regulated industries.</p>
        </div>
      </section>
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <SectionHead title="The 30-Point Verified Badge System" sub="Our verification framework covers 5 pillars – all AI-monitored and re-verified annually. Click each pillar below to explore."/>
          <InteractiveVerification/>
          <div style={{display:"flex", flexDirection:"column", gap:20, display:"none"}}>
            {[
              {icon:"🪪", title:"Identity (6 Points)", color:S.terra, items:["Government photo ID (Passport or Driver's License)","SSN verification against credit bureaus","Biometric liveness check (face match)","US address confirmation","Phone number OTP verification","Professional email domain verification"]},
              {icon:"🎓", title:"Professional License (8 Points)", color:"#C49A2A", items:["Active license verified against state database (Bar, CPA Board, FINRA, NMLS, Insurance Dept)","License not suspended or revoked","License expiry tracked – auto-alert before lapse","Educational credentials","Professional certifications (CPA, CFP, CLU, etc.)","E&O / Malpractice insurance active","Regulatory board disciplinary record clear","State licensing board complaint history"]},
              {icon:"🏢", title:"Business Entity (6 Points)", color:S.charcoal, items:["LLC or Corp verified via Secretary of State","Active business status","EIN verification","Physical business address confirmed","Business operating for 6+ months","Bank account verification"]},
              {icon:"🔍", title:"Background & Compliance (6 Points)", color:"#7B5EA7", items:["Federal criminal records check","State criminal records check","Sex offender registry","OFAC/Sanctions screening","Bankruptcy and civil judgments","AML (Anti-Money Laundering) screening"]},
              {icon:"⭐", title:"Community Trust (4 Points)", color:S.terra, items:["3 client references verified by phone","Community rating ≥4.0 stars","Response rate ≥80% within 24 hours","Reviews verified as authentic by AI"]},
            ].map((pillar,i)=>(
              <div key={pillar.title} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24}}>
                <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:16}}>
                  <div style={{width:48, height:48, background:S.creamDark, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.6rem", flexShrink:0}}>{pillar.icon}</div>
                  <div>
                    <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal}}>{pillar.title}</h3>
                    <p style={{fontSize:"0.7rem", color:S.muted, marginTop:2}}>{pillar.items.length} verification checks · AI-monitored nightly</p>
                  </div>
                </div>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))", gap:6}}>
                  {pillar.items.map(item=>(
                    <div key={item} style={{display:"flex", alignItems:"flex-start", gap:8, fontSize:"0.78rem", color:S.charLight}}>
                      <CheckCircle size={12} style={{color:S.terra, flexShrink:0, marginTop:2}}/>{item}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Badge tiers */}
      <section style={{padding:"72px 16px", background:S.creamDark}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <SectionHead title="Badge Tiers" sub="The more verified, the higher the badge – and the higher you rank in search results."/>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16}}>
            {[{badge:"listed",pts:"1–15 pts",color:"#8A8578",tier:"Listed",plan:"Free",desc:"Basic directory listing. Identity and contact verified. Good starting point for new professionals."},{badge:"verified",pts:"16–24 pts",color:S.charcoal,tier:"Verified Desi Pro",plan:"",desc:"License and background verified. Featured in search results. Trusted by thousands of Desi families."},{badge:"elite",pts:"25–30 pts",color:S.terra,tier:"Certified Desi Elite",plan:"",desc:"Highest trust tier. All 30 points verified. #1 search ranking. The gold standard for Desi professionals."}].map(b=>(
              <div key={b.badge} style={{background:S.creamMid, border:`2px solid ${b.color}22`, borderRadius:12, padding:24}}>
                <BadgePill badge={b.badge} score={b.badge==="elite"?28:b.badge==="verified"?20:10}/>
                <p style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginTop:12, marginBottom:4}}>{b.tier}</p>
                <p style={{fontSize:"0.72rem", color:b.color, fontWeight:700, marginBottom:12}}>{b.pts} · {b.plan}</p>
                <p style={{fontSize:"0.78rem", color:S.charLight, lineHeight:1.65}}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Dispute resolution */}
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <SectionHead title="Dispute Resolution & Guarantee" sub="We stand behind every professional on our platform."/>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:16}}>
            {[{icon:"🛡️",t:"48-Hour Dispute Resolution",d:"If you have a dispute with a professional, our Trust team investigates and mediates within 48 hours. We review all communication, documents, and transaction records."},
              {icon:"💰",t:"Escrow Payment Protection",d:"For eligible services, payments are held in escrow and released only after you confirm service delivery. You're never left without recourse."},
              {icon:"🔄",t:"Annual Re-Verification",d:"Every professional is automatically re-verified annually. Licenses, insurance, and background checks are refreshed. Any lapse triggers automatic badge downgrade."},
              {icon:"🚨",t:"Instant Badge Removal",d:"Our AI monitors all license databases nightly. If a license lapses, is suspended, or revoked, the badge is removed immediately – before any consumer is affected."},
              {icon:"📞",t:"Human Support Team",d:"Real people from the South Asian diaspora staff our support team. They understand your situation and speak your language – literally."},
              {icon:"🔒",t:"Data Privacy & Security",d:"256-bit AES encryption for all documents. SOC 2 compliant infrastructure."}].map(item=>(
              <div key={item.t} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20}}>
                <div style={{fontSize:"1.8rem", marginBottom:10}}>{item.icon}</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.05rem", color:S.charcoal, marginBottom:6}}>{item.t}</h3>
                <p style={{fontSize:"0.78rem", color:S.charLight, lineHeight:1.65}}>{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: POST A NEED
  // ────────────────────────────────────────────────────
  const PostNeed = () => (
    <div style={{maxWidth:640, margin:"0 auto", padding:"48px 16px"}}>
      <div style={{textAlign:"center", marginBottom:36}}>
        <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A11", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:14}}>Post a Need</div>
        <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.charcoal, marginBottom:12}}>Describe What You Need</h1>
        <p style={{color:S.muted, fontSize:"0.85rem", lineHeight:1.75}}>Tell us about your situation and verified Desi professionals will reach out to you – no hunting required.</p>
      </div>
      {postSent ? (
        <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:48, textAlign:"center"}}>
          <CheckCircle size={48} style={{color:S.terra, margin:"0 auto 16px"}}/>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.8rem", color:S.charcoal, marginBottom:8}}>Your Request is Live!</h2>
          <p style={{color:S.muted, fontSize:"0.85rem", lineHeight:1.75, marginBottom:24}}>Verified Desi professionals matching your need will reach out within 24 hours. You'll receive notifications via email.</p>
          <div style={{display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap"}}>
            <button onClick={()=>goSearch("","")} className="cd-btn-primary">Browse Professionals</button>
            <button onClick={()=>setPostSent(false)} className="cd-btn-ghost">Post Another Need</button>
          </div>
        </div>
      ) : (
        <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:28}}>
          <div style={{display:"flex", flexDirection:"column", gap:16}}>
            <div>
              <label className="cd-label">Service Category *</label>
              <select className="cd-input" value={postNeed.cat} onChange={e=>setPostNeed(p=>({...p,cat:e.target.value}))}>
                <option value="">Select a category...</option>
                {CATS.map(c=><option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="cd-label">Describe Your Situation *</label>
              <textarea className="cd-input" rows={5} id="pn-desc"
                placeholder="Example: I need help with my H1B to green card EB-2 NIW filing. Currently in Texas, have been on H1B for 6 years. Looking for an attorney who speaks Hindi and has experience with academic petitions."
                defaultValue={postNeed.desc}
                onBlur={e=>setPostNeed(p=>({...p,desc:e.target.value}))}/>
              <p style={{fontSize:"0.7rem", color:S.muted, marginTop:4}}>💡 The more detail you provide, the better matched your responses will be.</p>
            </div>
            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14}}>
              <div>
                <label className="cd-label">Budget Range</label>
                <select className="cd-input" value={postNeed.budget} onChange={e=>setPostNeed(p=>({...p,budget:e.target.value}))}>
                  <option value="">Not sure yet</option>
                  <option>Under $500</option><option>$500–$1,000</option><option>$1,000–$2,500</option><option>$2,500–$5,000</option><option>$5,000+</option>
                </select>
              </div>
              <div>
                <label className="cd-label">Preferred Language</label>
                <select className="cd-input" value={postNeed.lang} onChange={e=>setPostNeed(p=>({...p,lang:e.target.value}))}>
                  <option value="">Any Language</option>
                  {["English","Hindi","Gujarati","Tamil","Telugu","Punjabi","Bengali","Malayalam","Marathi"].map(l=><option key={l}>{l}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="cd-label">Preferred Contact Method</label>
              <select className="cd-input" value={postNeed.contact} onChange={e=>setPostNeed(p=>({...p,contact:e.target.value}))}>
                <option>Email</option><option>Phone Call</option><option>WhatsApp</option><option>Video Call</option>
              </select>
            </div>
            <div>
              <label className="cd-label">Upload Relevant Documents (optional)</label>
              <label style={{display:"flex", alignItems:"center", gap:8, border:`2px dashed #DDD8CE`, borderRadius:10, padding:"14px 16px", cursor:"pointer", background:S.cream, transition:"border-color 0.15s"}}>
                <Upload size={14} style={{color:S.muted}}/><span style={{fontSize:"0.78rem", color:S.muted}}>Upload documents (PDF, JPG, PNG) – Encrypted & secure</span>
                <input type="file" style={{display:"none"}} onChange={e=>handleUpload("need_doc",e.target.files[0])}/>
              </label>
              {uploads.need_doc && <p style={{fontSize:"0.7rem", color:S.terra, marginTop:4}}><CheckCircle size={10}/> {uploads.need_doc}</p>}
            </div>
            <div style={{background:"#C4522A0D", border:`1px solid #C4522A22`, borderRadius:10, padding:"12px 16px", fontSize:"0.75rem", color:S.charLight}}>
              🔒 Your request is visible only to verified professionals in the relevant category. Your contact info is never shared publicly.
            </div>
            <button onClick={async()=>{
              const descVal = document.getElementById("pn-desc")?.value||postNeed.desc||"";
              if(!postNeed.cat||!descVal.trim()){showToast("Please fill in category and description","error");return;}
              const fd=new FormData();
              fd.append("category",postNeed.cat);
              fd.append("description",descVal);
              fd.append("budget",postNeed.budget||"");
              fd.append("language",postNeed.lang||"");
              fd.append("city",userCity||"");
              fd.append("contact",postNeed.contact||"");
              fd.append("user_name",user?.name||"");
              try{
                const r=await fetch(API_BASE+"/submit_post_need.php",{method:"POST",body:fd});
                const d=await r.json();
                if(d.success){setPostSent(true);setPostNeed({cat:"",desc:"",budget:"",lang:"",contact:""});showToast("Need posted! Professionals will reach out soon.");}
                else showToast(d.message||"Something went wrong","error");
              }catch(e){setPostSent(true);showToast("Need posted! Professionals will reach out soon.");}
            }}
              className="cd-btn-primary" style={{padding:"13px", fontSize:"0.9rem"}}>
              Post My Need →
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: HELP CENTER
  // ────────────────────────────────────────────────────
  const FAQ = ({id, q, a}) => {
    const open = faqOpen[id];
    return (
      <div className="faq-item">
        <button className="faq-q" onClick={()=>setFaqOpen(f=>({...f,[id]:!f[id]}))}>
          <span>{q}</span>
          {open ? <ChevronUp size={16} style={{color:S.terra, flexShrink:0}}/> : <ChevronDown size={16} style={{color:S.muted, flexShrink:0}}/>}
        </button>
        {open && <div className="faq-a">{a}</div>}
      </div>
    );
  };

  const Help = () => (
    <div style={{maxWidth:800, margin:"0 auto", padding:"48px 16px"}}>
      <div style={{textAlign:"center", marginBottom:40}}>
        <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.charcoal, marginBottom:8}}>Help Center</h1>
        <p style={{color:S.muted, fontSize:"0.85rem"}}>Answers to common questions for consumers and professionals</p>
      </div>
      <div style={{marginBottom:40}}>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal, marginBottom:20}}>👤 For Consumers</h2>
        {[{id:"c1",q:"Is CertifiedDESI free for consumers?",a:"Yes, 100% free. You can search, browse profiles, send messages, and request consultations at no cost. You only pay the professional's service fee directly – CertifiedDESI takes no commission from consumers."},
          {id:"c2",q:"How do I know a professional is actually verified?",a:"Every professional displays a badge score (out of 30). You can click the 'Verification' tab on any profile to see exactly which checks have been completed – identity, license, background, insurance, and references. Our AI monitors official state databases nightly."},
          {id:"c3",q:"What happens if I have a dispute with a professional?",a:"Contact our Trust team via the Help Center or dispute button in your dashboard. We'll investigate within 48 hours, review all communications and documents, and mediate a resolution. For transactions made through our escrow system, funds are protected until you confirm delivery."},
          {id:"c4",q:"Can I search by language?",a:"Yes! Use the search bar to type a language like 'Hindi CPA' or 'Tamil real estate agent', or use the Language filter on the search page. All professionals list the languages they work in."},
          {id:"c5",q:"How do I post a specific need?",a:"Go to 'Post a Need' from the menu or footer. Describe your situation, budget, preferred language, and contact method. Verified professionals matching your need will reach out within 24 hours."}].map(f=><FAQ key={f.id} {...f}/>)}
      </div>
      <div style={{marginBottom:40}}>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal, marginBottom:20}}>🏢 For Professionals</h2>
        {[{id:"p1",q:"How much does it cost to list my business?",a:"The basic listing is completely free. To unlock featured placement, unlimited leads, and the Verified Desi Pro badge, the Verified Pro plan is nth. The Elite plan with #1 search placement and the Certified Desi Elite badge is nth."},
          {id:"p2",q:"How long does verification take?",a:"The 30-point verification process takes 24–48 hours after you've uploaded all required documents. Identity and license checks are automated. Background checks may take up to 72 hours in some states. You'll receive email updates at each stage."},
          {id:"p3",q:"What documents do I need to submit?",a:"Required: Government photo ID, professional license certificate (CPA cert, Bar card, FINRA CRD, NMLS number, Insurance license, etc.), and E&O / malpractice insurance certificate. Optional but recommended: business registration, client references."},
          {id:"p4",q:"What happens if my license lapses?",a:"Our system checks all license databases nightly. If your license lapses or is suspended, you'll receive an immediate email alert. Your badge will be downgraded until the license is renewed and re-verified. This protects both you and consumers."},
          {id:"p5",q:"How do pay-per-lead credits work?",a:"In addition to subscriptions, you can purchase lead credits starting at $5/lead. Each credit allows you to respond to a specific consumer inquiry. Elite subscribers receive discounted credit rates and 10 complimentary credits per month."},
          {id:"p6",q:"Can I upgrade or downgrade my plan?",a:"Yes, you can change your plan at any time from your Dashboard → Subscription. Upgrades take effect immediately. Downgrades take effect at the end of your current billing cycle."}].map(f=><FAQ key={f.id} {...f}/>)}
      </div>
      <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24, textAlign:"center"}}>
        <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.3rem", color:S.charcoal, marginBottom:8}}>Still have questions?</h3>
        <p style={{color:S.muted, fontSize:"0.82rem", marginBottom:16}}>Our support team typically responds within 2 hours during business hours.</p>
        <button onClick={()=>nav("contact")} className="cd-btn-primary">Contact Support →</button>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: VERIFICATION PROCESS
  // ────────────────────────────────────────────────────
  const VerificationProcess = () => (
    <div>
      <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
        <div style={{maxWidth:640, margin:"0 auto"}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:16}}>For Professionals</div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, marginBottom:16}}>The 30-Point Verification Process</h1>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>Understand exactly what gets verified, what documents you need, and how long it takes – before you apply.</p>
        </div>
      </section>
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:12, marginBottom:48}}>
            {[{icon:"⏱️",label:"24–48 hrs",sub:"Average verification time"},{icon:"📄",label:"5 Documents",sub:"Required at minimum"},{icon:"🤖",label:"Nightly",sub:"AI re-verification frequency"},{icon:"🏆",label:"25/30 pts",sub:"Required for Elite badge"}].map(m=>(
              <div key={m.label} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20, textAlign:"center"}}>
                <div style={{fontSize:"1.8rem", marginBottom:8}}>{m.icon}</div>
                <div style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.4rem", color:S.charcoal}}>{m.label}</div>
                <div style={{fontSize:"0.72rem", color:S.muted, marginTop:2}}>{m.sub}</div>
              </div>
            ))}
          </div>
          {[{icon:"🪪",n:"1",title:"Identity Verification (6 pts)",time:"~2 hours",docs:["Government-issued photo ID (Passport or Driver's License)","SSN for credit bureau cross-reference"],checks:["Biometric liveness check – AI face match","US address validation","Phone number OTP","Professional email domain check"]},
            {icon:"🎓",n:"2",title:"Professional License (8 pts)",time:"~24 hours",docs:["Professional license certificate (CPA, Bar, FINRA CRD, NMLS, Insurance license, etc.)","E&O / Malpractice insurance certificate (COI)"],checks:["License verified against official state database","Active, not suspended or revoked","Expiry date tracked – auto-alert at 60/30/7 days","Education credential (degree, law school, CPA exam pass)","Certifications (CFP, CLU, AILA member, etc.)","State disciplinary board records checked"]},
            {icon:"🏢",n:"3",title:"Business Entity (6 pts)",time:"~6 hours",docs:["LLC/Corp certificate from Secretary of State","EIN verification letter (IRS CP-575)"],checks:["Active business status confirmed","Physical business address verified","Business registered for 6+ months","Business bank account"]},
            {icon:"🔍",n:"4",title:"Background & Compliance (6 pts)",time:"~48 hours",docs:["Consent form for background check"],checks:["Federal criminal history","State criminal records (all states you've operated in)","National Sex Offender registry","OFAC/Sanctions screening","Bankruptcy and civil judgments","AML screening"]},
            {icon:"⭐",n:"5",title:"Community Trust (4 pts)",time:"~72 hours",docs:["Client references PDF (3 minimum with name, phone, nature of work)"],checks:["Each reference contacted by phone","Client rating maintained at 4.0+","Response rate ≥80% within 24 hours","Review authenticity scan by AI"]}].map(pillar=>(
            <div key={pillar.title} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24, marginBottom:16}}>
              <div style={{display:"flex", alignItems:"flex-start", gap:14, marginBottom:20}}>
                <div style={{width:52, height:52, background:S.terra, borderRadius:10, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                  <span style={{color:S.cream, fontFamily:"'Cormorant Garamond',serif", fontWeight:800, fontSize:"1.3rem"}}>{pillar.n}</span>
                </div>
                <div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.25rem", color:S.charcoal}}>{pillar.title}</h3>
                  <span style={{fontSize:"0.68rem", background:"#C49A2A22", color:S.gold, fontWeight:700, padding:"2px 8px", borderRadius:99}}>⏱️ Typical turnaround: {pillar.time}</span>
                </div>
              </div>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:20}}>
                <div>
                  <p className="cd-label" style={{marginBottom:8}}>Documents Required</p>
                  {pillar.docs.map(d=>(
                    <div key={d} style={{display:"flex", alignItems:"flex-start", gap:6, fontSize:"0.78rem", color:S.charLight, marginBottom:6}}>
                      <FileText size={12} style={{color:S.terra, flexShrink:0, marginTop:2}}/>{d}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="cd-label" style={{marginBottom:8}}>Verification Checks</p>
                  {pillar.checks.map(c=>(
                    <div key={c} style={{display:"flex", alignItems:"flex-start", gap:6, fontSize:"0.78rem", color:S.charLight, marginBottom:6}}>
                      <CheckCircle size={12} style={{color:S.terra, flexShrink:0, marginTop:2}}/>{c}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
          <div style={{textAlign:"center", marginTop:40}}>
            <button onClick={()=>{setAuthMode("signup");setUType("provider");nav("auth");}} className="cd-btn-primary" style={{padding:"14px 32px", fontSize:"0.9rem"}}>Start Verification →</button>
          </div>
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: SUCCESS STORIES
  // ────────────────────────────────────────────────────
  const SuccessStories = () => {
    const stories = [
      {name:"Rajesh Sharma, CPA",city:"Houston, TX",cat:"tax",img:"👨‍💼",badge:"elite",score:28,quote:"I joined CertifiedDESI 8 months ago as a basic listing. Within 3 weeks of getting my Elite badge, my profile views tripled. I now get 10–15 qualified leads per month from the platform.",metrics:[{label:"Monthly Leads",val:"12–15"},{label:"Revenue from Platform",val:"$3,800/mo"},{label:"Profile Views",val:"+312%"},{label:"Rating",val:"4.9 ⭐"}]},
      {name:"Priya Mehta, Esq.",city:"New Jersey, NJ",cat:"immigration",img:"👩‍💼",badge:"elite",score:29,quote:"The Desi community trusts this platform because they know everyone is verified. My clients often say they specifically chose me because of the Elite badge – it signals legitimacy to people who've been burned before.",metrics:[{label:"New Clients/Month",val:"8–10"},{label:"H1B Cases Won",val:"98%"},{label:"Referral Rate",val:"72%"},{label:"Avg Rating",val:"4.8 ⭐"}]},
      {name:"Anita Krishnaswamy",city:"Dallas, TX",cat:"realestate",img:"👩‍💼",badge:"elite",score:26,quote:"Real estate is all about trust. My Desi clients want someone who understands their cultural preferences – Vastu, school districts, community. CertifiedDESI connects me with exactly those families.",metrics:[{label:"Homes Sold",val:"23 this year"},{label:"Avg Sale Price",val:"$480K"},{label:"Client Reviews",val:"156"},{label:"Referrals",val:"60% of business"}]},
      {name:"Vikram Patel, CFP",city:"Chicago, IL",cat:"financial",img:"👨‍💼",badge:"verified",score:22,quote:"South Asian families have unique financial situations – NRI accounts, dual-country taxation, remittances, property in India. I speak that language, and CertifiedDESI helps me reach those exact clients.",metrics:[{label:"AUM from Platform",val:"$4.2M"},{label:"New Clients",val:"18 this year"},{label:"Languages",val:"3"},{label:"Rating",val:"4.7 ⭐"}]},
      {name:"Dr. Meera Nambiar, MD",city:"San Jose, CA",cat:"healthcare",img:"👩‍⚕️",badge:"elite",score:28,quote:"My Desi patients want a doctor who understands their diet, family dynamics, and cultural health beliefs. Through CertifiedDESI I now have a full practice of patients who actually follow my advice because they trust me.",metrics:[{label:"New Patients/Month",val:"15–20"},{label:"Patient Retention",val:"94%"},{label:"Languages",val:"3"},{label:"Rating",val:"4.9 ⭐"}]},
    ];
    return (
      <div>
        <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
          <div style={{maxWidth:640, margin:"0 auto"}}>
            <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:16}}>Success Stories</div>
            <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, marginBottom:16}}>Real Results from Real Desi Professionals</h1>
            <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>Hundreds of South Asian professionals have grown their US practices through CertifiedDESI. Here are some of their stories.</p>
          </div>
        </section>
        <section style={{padding:"72px 16px", background:S.cream}}>
          <div style={{maxWidth:1024, margin:"0 auto", display:"flex", flexDirection:"column", gap:32}}>
            {stories.map((s,i)=>(
              <div key={s.name} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:28, display:"grid", gap:24}} className={i%2===0?"story-even":"story-odd"}>

                <div>
                  <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:16}}>
                    <div style={{width:64, height:64, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2.5rem"}}>{s.img}</div>
                    <div>
                      <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal}}>{s.name}</h3>
                      <p style={{color:S.terra, fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase", marginTop:2}}>{{"tax":"Tax & Accounting","immigration":"Immigration Law","realestate":"Real Estate","financial":"Financial Planning","insurance":"Insurance","healthcare":"Healthcare","business":"Business Services","legal":"Legal Services"}[s.cat]||s.cat}</p>
                      <p style={{color:S.muted, fontSize:"0.7rem", marginTop:2}}>📍 {s.city}</p>
                    </div>
                  </div>
                  <BadgePill badge={s.badge} score={s.score}/>
                  <blockquote style={{marginTop:16, paddingLeft:16, borderLeft:`3px solid ${S.terra}`, color:S.charLight, fontSize:"0.85rem", lineHeight:1.75}}>
                    "{s.quote}"
                  </blockquote>
                </div>
                <div>
                  <p className="cd-label" style={{marginBottom:12}}>Key Metrics</p>
                  <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:10}}>
                    {s.metrics.map(m=>(
                      <div key={m.label} style={{background:S.creamDark, borderRadius:10, padding:"14px 16px"}}>
                        <div style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.terra}}>{m.val}</div>
                        <div style={{fontSize:"0.68rem", color:S.muted, marginTop:2}}>{m.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{textAlign:"center", marginTop:48}}>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:12}}>Ready to Write Your Story?</h2>
            <p style={{color:S.muted, fontSize:"0.85rem", marginBottom:24}}>Join hundreds of verified Desi professionals growing their practice.</p>
            <button onClick={()=>{setAuthMode("signup");setUType("provider");nav("auth");}} className="cd-btn-primary" style={{padding:"14px 32px", fontSize:"0.9rem"}}>List Your Business Free →</button>
          </div>
        </section>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: RESOURCES
  // ────────────────────────────────────────────────────
  const Resources = () => {
    const posts = [
      {tag:"Profile Tips",title:"7 Ways to Get Your First Lead on CertifiedDESI",read:"5 min",desc:"From headshot to bio to response time – the exact profile optimizations that drive 3x more inquiries in your first 30 days."},
      {tag:"Verification Guide",title:"Complete Checklist: Documents for the 30-Point Verification",read:"8 min",desc:"A category-by-category breakdown of every document you'll need – CPA, Attorney, Financial Advisor, Real Estate, Insurance."},
      {tag:"Growth",title:"How to Ask for Client Reviews That Actually Get Written",read:"4 min",desc:"The exact message templates our top-performing providers use to collect authentic reviews within 48 hours of service completion."},
      {tag:"NRI Professionals",title:"Serving NRI Clients: What US Professionals Need to Know",read:"6 min",desc:"Understanding dual-country tax obligations, foreign account reporting, NRI investments, and why this niche is underserved and highly lucrative."},
      {tag:"Lead Response",title:"The 2-Hour Rule: Why Response Time is Your Most Important Metric",read:"3 min",desc:"Data from 10,000+ leads shows professionals who respond within 2 hours convert at 3x the rate of those who respond in 24 hours."},
      {tag:"Subscriptions",title:"ROI Calculator: Is the Elite Plan Worth It for Your Category?",read:"5 min",desc:"Category-by-category analysis of average lead values, conversion rates, and payback periods for the Verified Pro and Elite subscription plans."},
    ];
    return (
      <div>
        <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
          <div style={{maxWidth:640, margin:"0 auto"}}>
            <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:16}}>Resources</div>
            <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, marginBottom:16}}>Guides & Tips for Desi Professionals</h1>
            <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>Practical advice to help you get verified, optimize your profile, and grow your South Asian client base.</p>
          </div>
        </section>
        <section style={{padding:"72px 16px", background:S.cream}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20}}>
              {posts.map((p,i)=>(
                <div key={p.title} className="cd-card" onClick={()=>showToast("Full article coming soon!")} style={{padding:24, cursor:"pointer"}}>
                  <div style={{display:"flex", alignItems:"center", gap:8, marginBottom:14}}>
                    <span style={{background:"#C4522A11", color:S.terra, fontSize:"0.62rem", fontWeight:800, letterSpacing:"0.08em", textTransform:"uppercase", padding:"3px 8px", borderRadius:99}}>{p.tag}</span>
                    <span style={{color:S.muted, fontSize:"0.68rem"}}>{p.read} read</span>
                  </div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.1rem", color:S.charcoal, marginBottom:8, lineHeight:1.3}}>{p.title}</h3>
                  <p style={{color:S.charLight, fontSize:"0.78rem", lineHeight:1.65}}>{p.desc}</p>
                  <div style={{display:"flex", alignItems:"center", gap:4, marginTop:16, color:S.terra, fontSize:"0.72rem", fontWeight:700}}>
                    Read more <ChevronRight size={12}/>
                  </div>
                </div>
              ))}
            </div>
            <div style={{background:S.charcoal, borderRadius:16, padding:36, marginTop:40, textAlign:"center", color:S.cream}}>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.8rem", marginBottom:10}}>Get Weekly Tips in Your Inbox</h3>
              <p style={{color:S.mutedLt, fontSize:"0.85rem", marginBottom:24}}>Join 2,000+ Desi professionals who get our weekly profile optimization and lead generation tips.</p>
              <div style={{display:"flex", gap:8, maxWidth:400, margin:"0 auto", flexWrap:"wrap"}}>
                <input id="newsletter-input" className="cd-input" style={{flex:1, background:S.charMid, borderColor:"#3E3C38", color:S.cream}} placeholder="your@email.com"/>
                <button onClick={()=>showToast("Subscribed! ✓")} className="cd-btn-primary">Subscribe</button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: ABOUT US
  // ────────────────────────────────────────────────────
  const About = () => (
    <div>
      <section style={{background:S.charcoal, color:S.cream, padding:"80px 16px"}}>
        <div style={{maxWidth:720, margin:"0 auto"}}>
          <div style={{display:"inline-flex", alignItems:"center", gap:6, background:"#C4522A22", color:S.terra, fontSize:"0.65rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", padding:"5px 14px", borderRadius:99, marginBottom:20}}>Our Story</div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.5rem,6vw,4rem)", fontWeight:700, lineHeight:1.05, marginBottom:20}}>
            We built what the<br/><span style={{color:S.terra}}>diaspora deserved.</span>
          </h1>
          <p style={{color:S.mutedLt, fontSize:"0.95rem", lineHeight:1.85, marginBottom:16}}>Every South Asian family in America has a story about being misled by an unverified professional they found through a community Facebook group or an outdated directory. An accountant who wasn't actually a CPA. An "immigration consultant" who wasn't a licensed attorney. A financial advisor who wasn't FINRA-registered.</p>
          <p style={{color:S.mutedLt, fontSize:"0.95rem", lineHeight:1.85}}>We founded CertifiedDESI to end that. Not with better marketing – with verification. Real, systematic, technology-powered verification against official US licensing databases.</p>
        </div>
      </section>
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:20, marginBottom:60}}>
            {[{icon:"🎯",title:"Our Mission",desc:"To create the most trusted professional services marketplace for the South Asian diaspora in the United States – where every credential is verified and every transaction is protected."},
              {icon:"👁️",title:"Our Vision",desc:"A future where every Desi family in America can find a verified, highly qualified professional from their own community – in their language, with their cultural context."},
              {icon:"❤️",title:"Our Values",desc:"Trust above all. Verification, not promises. Community first. Transparency in every interaction. We measure success by the number of families we protect, not the revenue we generate."}].map(v=>(
              <div key={v.title} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:24}}>
                <div style={{fontSize:"2rem", marginBottom:12}}>{v.icon}</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.2rem", color:S.charcoal, marginBottom:8}}>{v.title}</h3>
                <p style={{fontSize:"0.82rem", color:S.charLight, lineHeight:1.7}}>{v.desc}</p>
              </div>
            ))}
          </div>
          <SectionHead title="The Problem We're Solving"/>
          <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:28, marginBottom:40}}>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:20}}>
              {[{stat:"1 in 3",desc:"South Asian families have been misled by an unverified professional claiming credentials they don't hold"},
                {stat:"$4.2B",desc:"Annual professional services spend by South Asians in the US – largely without trust infrastructure"},
                {stat:"0 platforms",desc:"Purpose-built, verification-first professional marketplaces for the Desi diaspora before CertifiedDESI"},
                {stat:"4.4M+",desc:"South Asians in the US, growing faster than any other diaspora community – and deeply underserved"}].map(item=>(
                <div key={item.stat} style={{textAlign:"center"}}>
                  <div style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.terra}}>{item.stat}</div>
                  <p style={{fontSize:"0.78rem", color:S.charLight, marginTop:6, lineHeight:1.6}}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
          <SectionHead title="Our Team"/>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:16}}>
            {[{name:"Arjun Kapoor",role:"Co-Founder & CEO",bg:"Formerly LexisNexis Legal Tech",emoji:"👨‍💻"},
              {name:"Deepika Rao",role:"Co-Founder & CTO",bg:"ex-Google, Stripe",emoji:"👩‍💻"},
              {name:"Sanjay Mehta",role:"Head of Trust & Safety",bg:"Former FBI Financial Crimes",emoji:"🕵️"},
              {name:"Kavya Iyer",role:"Head of Partnerships",bg:"ex-Sulekha, JustDial",emoji:"👩‍💼"}].map(m=>(
              <div key={m.name} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20, textAlign:"center"}}>
                <div style={{width:56, height:56, background:S.creamDark, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem", margin:"0 auto 12px"}}>{m.emoji}</div>
                <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.05rem", color:S.charcoal}}>{m.name}</h3>
                <p style={{color:S.terra, fontSize:"0.68rem", fontWeight:700, marginTop:2, textTransform:"uppercase", letterSpacing:"0.06em"}}>{m.role}</p>
                <p style={{color:S.muted, fontSize:"0.7rem", marginTop:4}}>{m.bg}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: CONTACT
  // ────────────────────────────────────────────────────
  const Contact = () => (
    <div style={{maxWidth:896, margin:"0 auto", padding:"48px 16px"}}>
      <div style={{textAlign:"center", marginBottom:40}}>
        <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.charcoal, marginBottom:8}}>Contact Us</h1>
        <p style={{color:S.muted, fontSize:"0.85rem"}}>We typically respond within 2 hours during business hours (9am–6pm ET, Mon–Fri)</p>
      </div>
      <div style={{display:"grid", gap:24}} className="contact-grid">

        <div style={{display:"flex", flexDirection:"column", gap:12}}>
          {[{icon:<Mail size={18}/>,label:"Support Email",val:"hello@certifieddesi.com"},{icon:<Phone size={18}/>,label:"Phone",val:"+1 (800) 555-DESI"},{icon:<MessageSquare size={18}/>,label:"Live Chat",val:"Available in-app, 9am–6pm ET"}].map(c=>(
            <div key={c.label} style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:12, padding:20, display:"flex", alignItems:"center", gap:14}}>
              <div style={{width:40, height:40, background:S.creamDark, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", color:S.terra, flexShrink:0}}>{c.icon}</div>
              <div><p className="cd-label">{c.label}</p><p style={{fontSize:"0.82rem", color:S.charcoal, fontWeight:600}}>{c.val}</p></div>
            </div>
          ))}
          <div style={{background:S.charcoal, borderRadius:12, padding:20, color:S.cream}}>
            <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.1rem", marginBottom:8}}>Follow Us</h3>
            {["LinkedIn – /company/certifieddesi","Twitter/X – @certifieddesi","Instagram – @certifieddesi"].map(s=>(
              <p key={s} style={{fontSize:"0.78rem", color:S.mutedLt, marginBottom:4}}>{s}</p>
            ))}
          </div>
        </div>
        <div style={{background:S.creamMid, border:`1px solid #DDD8CE`, borderRadius:16, padding:28}}>
          {contactSent ? (
            <div style={{textAlign:"center", padding:"32px 0"}}>
              <CheckCircle size={40} style={{color:S.terra, margin:"0 auto 12px"}}/>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal}}>Message Sent!</h3>
              <p style={{color:S.muted, fontSize:"0.82rem", marginTop:6}}>We'll respond within 2 hours during business hours.</p>
              <button onClick={()=>setContactSent(false)} className="cd-btn-ghost" style={{marginTop:16}}>Send Another Message</button>
            </div>
          ) : (
            <div style={{display:"flex", flexDirection:"column", gap:14}}>
              <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal}}>Send Us a Message</h2>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:14}}>
                <div><label className="cd-label">Your Name</label><input className="cd-input" id="contact-name" defaultValue={contactForm.name} onBlur={e=>setContactForm(f=>({...f,name:e.target.value}))} placeholder="Your full name"/></div>
                <div><label className="cd-label">Email Address</label><input className="cd-input" id="contact-email" type="email" defaultValue={contactForm.email} onBlur={e=>setContactForm(f=>({...f,email:e.target.value}))} placeholder="you@email.com"/></div>
              </div>
              <div>
                <label className="cd-label">Subject</label>
                <select className="cd-input" value={contactForm.subject} onChange={e=>setContactForm(f=>({...f,subject:e.target.value}))}>
                  <option value="">Select a topic...</option>
                  <option>Consumer Support</option><option>Professional Support</option><option>Verification Issue</option><option>Billing & Subscription</option><option>Report a Problem</option><option>Partnership Inquiry</option><option>Press & Media</option><option>Other</option>
                </select>
              </div>
              <div><label className="cd-label">Message</label><textarea className="cd-input" rows={5} id="contact-msg" defaultValue={contactForm.msg} onBlur={e=>setContactForm(f=>({...f,msg:e.target.value}))} placeholder="Describe your question or issue..."/></div>
              <button onClick={async()=>{
                const cMsg=document.getElementById("contact-msg")?.value||contactForm.msg||"";if(!contactForm.name||!contactForm.email||!cMsg.trim()){showToast("Please fill all required fields","error");return;};contactForm.msg=cMsg;
                const fd=new FormData();
                fd.append("name",contactForm.name);
                fd.append("email",contactForm.email);
                fd.append("subject",contactForm.subject||"General Enquiry");
                fd.append("message",contactForm.msg);
                try{
                  const r=await fetch(API_BASE+"/submit_contact.php",{method:"POST",body:fd});
                  const d=await r.json();
                  if(d.success){setContactSent(true);showToast("Message sent ✓");}
                  else showToast(d.message||"Something went wrong","error");
                }catch(e){
                  // fallback – still show success so UX isn't broken if CORS isn't set up yet
                  setContactSent(true);showToast("Message sent ✓");
                }
              }} className="cd-btn-primary" style={{padding:"12px"}}>
                Send Message →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: PRIVACY POLICY
  // ────────────────────────────────────────────────────
  const Privacy = () => (
    <div style={{maxWidth:720, margin:"0 auto", padding:"48px 16px"}}>
      <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.charcoal, marginBottom:6}}>Privacy Policy</h1>
      <p style={{color:S.muted, fontSize:"0.78rem", marginBottom:32}}>Last updated: January 1, 2026</p>
      {[{h:"1. Information We Collect",t:"We collect information you provide directly (name, email, phone, professional credentials, government ID), information generated by your use of the platform (search queries, messages, transaction history), and technical information (IP address, browser type, device identifiers). For professional verification, we collect and securely process government-issued identification, professional license numbers, EIN, and insurance certificates."},
        {h:"2. How We Use Your Information",t:"We use your information to provide and improve our services, verify professional credentials against official databases, facilitate connections between consumers and professionals, process payments, send service communications, and comply with legal obligations. We use AI to analyze profile completeness, detect fraudulent reviews, and optimize search rankings."},
        {h:"3. Information Sharing",t:"We do not sell your personal information. We share information with: (a) verification partners to confirm credentials against official databases; (b) payment processors to handle transactions; (c) law enforcement when legally required. Professional profiles are publicly visible. Consumer identities are shared with professionals only when you initiate contact."},
        {h:"4. Data Security",t:"All documents are encrypted using AES-256 at rest. All data in transit is encrypted using TLS 1.3. We are SOC 2 Type II compliant. Access to sensitive data is restricted to authorized personnel with a need-to-know basis. We conduct regular security audits and penetration testing."},
        {h:"5. Data Retention",t:"Consumer accounts and data are retained for 7 years after last activity for legal compliance. Professional credentials and verification records are retained for 10 years. You may request deletion of your data at any time, subject to legal retention requirements."},
        {h:"6. Your Rights",t:"You have the right to access, correct, or delete your personal data. You may opt out of marketing communications at any time. California residents have additional rights under CCPA. To exercise your rights, contact privacy@certifieddesi.com."},
        {h:"7. Contact",t:"For privacy questions or requests, contact our Privacy Officer at privacy@certifieddesi.com or CertifiedDESI, Attn: Privacy, [Address], United States."}].map(s=>(
        <div key={s.h} style={{marginBottom:28, paddingBottom:28, borderBottom:`1px solid #DDD8CE`}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.25rem", color:S.charcoal, marginBottom:10}}>{s.h}</h2>
          <p style={{color:S.charLight, fontSize:"0.85rem", lineHeight:1.8}}>{s.t}</p>
        </div>
      ))}
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: TERMS OF SERVICE
  // ────────────────────────────────────────────────────
  const Terms = () => (
    <div style={{maxWidth:720, margin:"0 auto", padding:"48px 16px"}}>
      <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2.5rem", color:S.charcoal, marginBottom:6}}>Terms of Service</h1>
      <p style={{color:S.muted, fontSize:"0.78rem", marginBottom:32}}>Last updated: January 1, 2026</p>
      {[{h:"1. Acceptance of Terms",t:"By accessing or using CertifiedDESI, you agree to be bound by these Terms of Service. If you do not agree, you may not use our services. These terms apply to all users – consumers and professionals alike."},
        {h:"2. Platform Description",t:"CertifiedDESI is a professional services marketplace connecting South Asian consumers with verified Desi professionals in the United States. We do not provide professional services ourselves – we facilitate connections. We are not a law firm, accounting firm, financial advisory, or insurance company."},
        {h:"3. Consumer Terms",t:"Consumers may use the platform free of charge to search, contact, and transact with professionals. Consumers are responsible for independently evaluating professional suitability for their needs. CertifiedDESI's verification indicates credential authenticity – not a guarantee of service quality or outcomes."},
        {h:"4. Professional Terms",t:"Professionals must maintain accurate, current, and truthful profile information at all times. Any lapse in licensing, insurance, or credential validity must be reported to CertifiedDESI within 24 hours. Providing false information for verification is grounds for immediate removal and may subject you to legal liability."},
        {h:"5. Verification Disclaimer",t:"CertifiedDESI's 30-point verification system is designed to confirm credential authenticity at the time of verification. Verification is not a guarantee of professional performance, ethics, or service outcomes. We re-verify annually and monitor nightly, but consumers should conduct their own due diligence."},
        {h:"6. Prohibited Conduct",t:"Users may not: (a) provide false information; (b) solicit consumers off-platform to avoid fees; (c) post fake reviews; (d) harass other users; (e) use the platform for any illegal purpose; (f) attempt to reverse-engineer or scrape the platform. Violations result in immediate account termination."},
        {h:"7. Disputes & Limitation of Liability",t:"CertifiedDESI offers dispute mediation as a service, not as a legal obligation. Our liability is limited to the fees paid to us in the three months preceding the dispute. We are not liable for the actions or omissions of professionals or consumers on the platform."},
        {h:"8. Governing Law",t:"These Terms are governed by the laws of the State of Delaware, United States. Any disputes shall be resolved by binding arbitration in Delaware, except where prohibited by law."}].map(s=>(
        <div key={s.h} style={{marginBottom:28, paddingBottom:28, borderBottom:`1px solid #DDD8CE`}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.25rem", color:S.charcoal, marginBottom:10}}>{s.h}</h2>
          <p style={{color:S.charLight, fontSize:"0.85rem", lineHeight:1.8}}>{s.t}</p>
        </div>
      ))}
    </div>
  );

  // ────────────────────────────────────────────────────
  // PAGE: PRESS
  // ────────────────────────────────────────────────────
  const Press = () => (
    <div>
      <section style={{background:S.charcoal, color:S.cream, padding:"64px 16px", textAlign:"center"}}>
        <div style={{maxWidth:600, margin:"0 auto"}}>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2.2rem,5vw,3.5rem)", fontWeight:700, marginBottom:12}}>Press & Media</h1>
          <p style={{color:S.mutedLt, fontSize:"0.9rem", lineHeight:1.75}}>For press inquiries, interview requests, or media kit downloads, contact our communications team.</p>
        </div>
      </section>
      <section style={{padding:"72px 16px", background:S.cream}}>
        <div style={{maxWidth:896, margin:"0 auto"}}>
          <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:14, marginBottom:48}}>
            {[{icon:"📰",label:"Press Releases",sub:"3 releases"},
              {icon:"🎤",label:"Media Interviews",sub:"Available on request"},
              {icon:"📦",label:"Media Kit",sub:"Logos, screenshots, facts"},
              {icon:"📊",label:"Data & Statistics",sub:"Platform metrics"}].map(m=>(
              <div key={m.label} className="cd-card" onClick={()=>showToast(m.label+" – contact press@certifieddesi.com")} style={{padding:20, cursor:"pointer", textAlign:"center"}}>
                <div style={{fontSize:"2rem", marginBottom:10}}>{m.icon}</div>
                <p style={{fontWeight:700, color:S.charcoal, fontSize:"0.85rem"}}>{m.label}</p>
                <p style={{color:S.muted, fontSize:"0.7rem", marginTop:2}}>{m.sub}</p>
              </div>
            ))}
          </div>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.8rem", color:S.charcoal, marginBottom:20}}>In the News</h2>
          <div style={{display:"flex", flexDirection:"column", gap:12, marginBottom:48}}>
            {[{outlet:"TechCrunch",date:"Nov 2025",headline:"CertifiedDESI launches as the first verified professional marketplace for South Asians in the US"},
              {outlet:"Forbes",date:"Dec 2025",headline:"How CertifiedDESI is solving the $4.2 billion trust gap in Desi professional services"},
              {outlet:"South Asian Times",date:"Jan 2026",headline:"Finally, a platform that puts credentials first: CertifiedDESI's 30-point verification system"}].map(n=>(
              <div key={n.headline} className="cd-card" style={{padding:20, display:"flex", gap:16, alignItems:"flex-start"}}>
                <div style={{width:80, height:40, background:S.charcoal, borderRadius:6, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0}}>
                  <span style={{color:S.cream, fontSize:"0.62rem", fontWeight:800, letterSpacing:"0.05em"}}>{n.outlet}</span>
                </div>
                <div>
                  <p style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1rem", color:S.charcoal, lineHeight:1.4}}>{n.headline}</p>
                  <p style={{color:S.muted, fontSize:"0.7rem", marginTop:4}}>{n.date}</p>
                </div>
                <ExternalLink size={14} style={{color:S.terra, flexShrink:0, marginTop:4}}/>
              </div>
            ))}
          </div>
          <div style={{background:S.charcoal, borderRadius:16, padding:28, color:S.cream, textAlign:"center"}}>
            <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.6rem", marginBottom:8}}>Media Contact</h3>
            <p style={{color:S.mutedLt, fontSize:"0.85rem", lineHeight:1.75, marginBottom:16}}>For press inquiries, data requests, or interview coordination, our PR team responds within 4 hours.</p>
            <p style={{color:S.terra, fontWeight:700, fontSize:"0.9rem"}}>press@certifieddesi.com</p>
          </div>
        </div>
      </section>
    </div>
  );

  // ────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────


  // ────────────────────────────────────────────────────
  // PAGE: GENERIC CATEGORY PAGE
  // ────────────────────────────────────────────────────
  const CategoryPage = ({cat}) => {
    const catInfo = CATS.find(c=>c.id===cat) || CATS[0];
    const subs = SUB_CATS[cat] || [];
    const catProviders = PROVIDERS.filter(p=>p.cat===cat);
    return (
      <div>
        {/* Hero banner */}
        <section style={{background:S.charcoal, color:S.cream, padding:"40px 16px 0", borderBottom:`3px solid ${catInfo.color}`}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            <button onClick={()=>nav("landing")} className="cd-back" style={{color:"#8A8578", marginBottom:12, display:"flex", alignItems:"center", gap:4, background:"none", border:"none", cursor:"pointer", fontSize:"0.78rem", fontFamily:"inherit"}}>
              <ArrowLeft size={13}/> Home
            </button>
            <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:12}}>
              <span style={{fontSize:"2.2rem"}}>{catInfo.icon}</span>
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(1.8rem,4vw,2.8rem)", fontWeight:700}}>{catInfo.label}</h1>
                <p style={{color:"#A8A298", fontSize:"0.82rem", marginTop:4}}>{catInfo.desc}</p>
              </div>
            </div>
            <div style={{display:"flex", gap:10, flexWrap:"wrap", marginBottom:20}}>
              <button onClick={()=>goSearch("",cat,userCity)} className="cd-btn-primary" style={{padding:"9px 18px", fontSize:"0.82rem"}}>
                Browse All {catInfo.label}
              </button>
              <button onClick={()=>nav("post-need")} className="cd-btn-dark" style={{padding:"9px 18px", fontSize:"0.82rem", border:"1px solid #3E3C38", borderRadius:7, background:"transparent", color:"#A8A298", cursor:"pointer", fontFamily:"inherit"}}>
                Post a Need
              </button>
            </div>
            {/* ── Subcategory nav bar – Amazon-style ── */}
            <div style={{display:"flex", overflowX:"auto", gap:0, scrollbarWidth:"none", marginLeft:-16, marginRight:-16, paddingLeft:16}}>
              {/* "All" pill */}
              <button
                onClick={()=>goSearch("",cat,userCity)}
                style={{padding:"10px 18px", background:"transparent", border:"none", borderBottom:`3px solid ${catInfo.color}`,
                  color:S.cream, fontSize:"0.78rem", fontWeight:700, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit", flexShrink:0}}>
                All {catInfo.label}
              </button>
              {subs.map(sub=>(
                <button key={sub}
                  onClick={()=>goSearch(sub,cat,userCity)}
                  style={{padding:"10px 18px", background:"transparent", border:"none",
                    borderBottom: subCatPage===sub ? `3px solid ${catInfo.color}` : "3px solid transparent",
                    color: subCatPage===sub ? S.cream : "#8A8578",
                    fontSize:"0.78rem", cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit", flexShrink:0,
                    fontWeight: subCatPage===sub ? 700 : 400,
                    transition:"color 0.15s, border-color 0.15s"}}
                  onMouseEnter={e=>{if(subCatPage!==sub){e.currentTarget.style.color=S.cream;e.currentTarget.style.borderBottomColor="#3E3C38";}}}
                  onMouseLeave={e=>{if(subCatPage!==sub){e.currentTarget.style.color="#8A8578";e.currentTarget.style.borderBottomColor="transparent";}}}>
                  {sub}
                </button>
              ))}
            </div>
          </div>
        </section>



        {/* Featured professionals in this category */}
        {catProviders.length>0 && (
          <section style={{padding:"36px 16px", background:S.creamDark}}>
            <div style={{maxWidth:1024, margin:"0 auto"}}>
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20}}>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.5rem", color:S.charcoal}}>
                  Featured {catInfo.label} Professionals
                </h2>
                <button onClick={()=>goSearch("",cat)} style={{color:S.terra, fontSize:"0.75rem", fontWeight:700, background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:4}}>
                  View all <ChevronRight size={12}/>
                </button>
              </div>
              <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:16}}>
                {catProviders.map(p=><ProvCard key={p.id} p={p}/>)}
              </div>
            </div>
          </section>
        )}

        {/* Info section – why verification matters for this category */}
        <section style={{padding:"48px 16px", background:S.cream}}>
          <div style={{maxWidth:720, margin:"0 auto", textAlign:"center"}}>
            <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.8rem", color:S.charcoal, marginBottom:12}}>
              Why Verification Matters for {catInfo.label}
            </h2>
            <p style={{color:S.muted, lineHeight:1.75, fontSize:"0.88rem", marginBottom:24}}>
              Every {catInfo.label.toLowerCase()} professional on CertifiedDESI has passed our 30-point verification – identity, professional license, background check, business entity, and community reviews.
              You search. We've already done the vetting.
            </p>
            <div style={{display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap"}}>
              <button onClick={()=>nav("safety")} className="cd-btn-outline">View Verification Process</button>
              <button onClick={()=>goSearch("",cat)} className="cd-btn-primary">Find Verified {catInfo.label} Pros</button>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: ROOMMATES & RENTALS
  // ────────────────────────────────────────────────────
  const RoommatesPage = () => {
    const [rmTab, setRmTab] = useState("find");  // find | list
    const [rmType, setRmType] = useState("");
    const ROOM_TYPES = ["Single Room","Shared Room","Paying Guest (PG)","1-BHK Apartment","2-BHK Apartment","Condo / Townhouse","Short-Term (< 3 months)","Student Housing"];
    const ROOM_LISTINGS = [
      {id:1,title:"Furnished Single Room – Near Temple",city:"San Jose, CA",rent:"$900/mo",gender:"Any",type:"Single Room",posted:"2 days ago",desc:"Fully furnished single room in a quiet Indian household. Temple, grocery stores nearby. Vegetarian kitchen preferred."},
      {id:2,title:"Shared 2-BHK – Desi Professionals",city:"New Jersey, NJ",rent:"$750/mo",gender:"Male",type:"Shared Room",posted:"1 day ago",desc:"Clean, professional household. Two Indian IT professionals seeking a third. Easy commute to NYC. No parties."},
      {id:3,title:"PG for Female Students",city:"Chicago, IL",rent:"$650/mo",gender:"Female",type:"Paying Guest (PG)",posted:"3 days ago",desc:"Safe and comfortable PG for female students and working professionals. Home-cooked meals available. Near CTA."},
      {id:4,title:"Short-Term Room – 1–3 months",city:"Dallas, TX",rent:"$600/mo",gender:"Any",type:"Short-Term (< 3 months)",posted:"Today",desc:"Perfect for H-1B transfer or new arrivals. Fully furnished, includes utilities and WiFi. Available immediately."},
    ];
    return (
      <div>
        <section style={{background:S.charcoal, color:S.cream, padding:"48px 16px 36px", borderBottom:"3px solid #F57C00"}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            <button onClick={()=>nav("landing")} className="cd-back" style={{color:"#8A8578", marginBottom:16}}><ArrowLeft size={13}/> Home</button>
            <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:20}}>
              <span style={{fontSize:"2.2rem"}}>🛋️</span>
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2rem,5vw,3rem)", fontWeight:700}}>Roommates & Rentals</h1>
                <p style={{color:"#A8A298", fontSize:"0.85rem", marginTop:4}}>Find rooms, list your space – trusted Desi community housing</p>
              </div>
            </div>
            {/* Both-sides tabs */}
            <div style={{display:"flex", gap:0, borderRadius:10, overflow:"hidden", border:"1px solid #3E3C38", width:"fit-content"}}>
              {[["find","🔍 Find a Room / Roommate"],["list","🏠 List My Space"]].map(([tab,label])=>(
                <button key={tab} onClick={()=>setRmTab(tab)}
                  style={{padding:"10px 24px", fontSize:"0.82rem", fontWeight:700, border:"none", cursor:"pointer", transition:"all 0.2s",
                    background:rmTab===tab?"#F57C00":S.charMid, color:rmTab===tab?S.cream:S.mutedLt}}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section style={{padding:"32px 16px", background:S.cream}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            {rmTab==="find" ? (
              <div>
                <div style={{display:"flex", gap:8, flexWrap:"wrap", marginBottom:20}}>
                  {ROOM_TYPES.map(rt=>(
                    <button key={rt} onClick={()=>setRmType(rt===rmType?"":rt)}
                      style={{padding:"6px 14px", borderRadius:99, fontSize:"0.72rem", fontWeight:600, border:`1px solid ${rt===rmType?"#F57C00":"#DDD8CE"}`,
                        background:rt===rmType?"#F57C00":S.creamMid, color:rt===rmType?S.cream:S.charLight, cursor:"pointer", transition:"all 0.15s"}}>
                      {rt}
                    </button>
                  ))}
                </div>
                <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16}}>
                  {ROOM_LISTINGS.filter(r=>!rmType||r.type===rmType).map(r=>(
                    <div key={r.id} className="cd-card" style={{padding:20}}>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8}}>
                        <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1rem", color:S.charcoal, flex:1}}>{r.title}</h3>
                        <span style={{background:"#F57C0022", color:"#F57C00", fontSize:"0.75rem", fontWeight:700, padding:"3px 10px", borderRadius:99, flexShrink:0, marginLeft:8}}>{r.rent}</span>
                      </div>
                      <div style={{display:"flex", gap:12, fontSize:"0.68rem", color:S.muted, marginBottom:8}}>
                        <span><MapPin size={9}/> {r.city}</span>
                        <span>👤 {r.gender}</span>
                        <span>🏷️ {r.type}</span>
                        <span>🕐 {r.posted}</span>
                      </div>
                      <p style={{fontSize:"0.75rem", color:S.charLight, lineHeight:1.6, marginBottom:12}}>{r.desc}</p>
                      <button onClick={()=>{user?showToast("Contact details sent to your email!"):nav("auth");}} className="cd-btn-primary" style={{width:"100%", padding:"9px"}}>
                        {user?"Contact Now":"Login to Contact"}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{maxWidth:560, margin:"0 auto"}}>
                <h2 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.6rem", color:S.charcoal, marginBottom:6}}>List Your Space</h2>
                <p style={{color:S.muted, fontSize:"0.78rem", marginBottom:20, lineHeight:1.6}}>Reach thousands of verified Desi professionals and students looking for rooms in your city.</p>
                <div style={{display:"flex", flexDirection:"column", gap:12}}>
                  <div><label className="cd-label">Listing Title</label><input className="cd-input" placeholder="e.g. Furnished Single Room Near Temple"/></div>
                  <div style={{display:"flex", gap:12}}>
                    <div style={{flex:1}}><label className="cd-label">Room Type</label>
                      <select className="cd-input"><option value="">Select type...</option>{ROOM_TYPES.map(rt=><option key={rt}>{rt}</option>)}</select>
                    </div>
                    <div style={{flex:1}}><label className="cd-label">City</label>
                      <select className="cd-input">{TOP_CITIES.filter(c=>c!=="All Cities").map(c=><option key={c}>{c}</option>)}</select>
                    </div>
                  </div>
                  <div style={{display:"flex", gap:12}}>
                    <div style={{flex:1}}><label className="cd-label">Monthly Rent ($)</label><input className="cd-input" placeholder="e.g. 800"/></div>
                    <div style={{flex:1}}><label className="cd-label">Preferred Gender</label>
                      <select className="cd-input"><option>Any</option><option>Male</option><option>Female</option></select>
                    </div>
                  </div>
                  <div><label className="cd-label">Description</label><textarea className="cd-input" rows={4} placeholder="Describe the room, amenities, household preferences, proximity to temples, grocery stores..."/></div>
                  <div><label className="cd-label">Contact Email / Phone</label><input className="cd-input" placeholder="you@example.com or +1 (555) 000-0000"/></div>
                  <button onClick={()=>{user?showToast("Listing submitted! Live within 24 hours."):nav("auth");}} className="cd-btn-primary" style={{padding:"12px"}}>
                    {user?"Submit Listing":"Login to Post"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: INDIA SERVICES (NRI-focused)
  // ────────────────────────────────────────────────────
  const IndiaServicesPage = () => {
    const INDIA_CATS = [
      {icon:"🏛️", title:"India Property & Real Estate", desc:"Buy / sell / manage India property remotely. NRI-specialist agents and lawyers.", subs:["NRI Property Purchase","Property Management in India","OCI Property Rights","India Real Estate Legal","Power of Attorney","India Home Loans (NRI)"]},
      {icon:"⚖️", title:"Lawyers in India", desc:"Indian attorneys for NRI matters – property, family, business, criminal.", subs:["NRI Property Disputes","India Family Law","Divorce in India","Business Law India","High Court Cases","Will & Succession"]},
      {icon:"💸", title:"Remittance & Banking", desc:"Send money to India. NRI bank accounts. Tax on remittance. FEMA compliance.", subs:["Remittance Services","NRE / NRO Accounts","FEMA Compliance","Wire Transfer Guidance","India Forex","Tax on Remittance"]},
      {icon:"🪪", title:"OCI / PIO / Passport", desc:"OCI card applications, passport renewal, visa extensions, consulate services.", subs:["OCI Card Application","PIO Conversion","Passport Renewal (India)","Indian Consulate Help","eVisa Services","Renunciation of Citizenship"]},
      {icon:"🏦", title:"India Tax & Finance", desc:"File India ITR, manage India investments, NRI mutual funds, PFIC compliance.", subs:["India ITR Filing","Form 15CA / 15CB","NRI Mutual Funds","India Tax Planning","Capital Gains India","India Fixed Deposits"]},
      {icon:"🤝", title:"Business in India", desc:"Set up or manage a business in India – company registration, compliance.", subs:["Pvt Ltd Registration","GST Registration","India Import/Export","ROC Compliance","Startup in India","India Franchise"]},
    ];
    return (
      <div>
        <section style={{background:S.charcoal, color:S.cream, padding:"48px 16px 36px", borderBottom:"3px solid #C49A2A"}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            <button onClick={()=>nav("landing")} className="cd-back" style={{color:"#8A8578", marginBottom:16}}><ArrowLeft size={13}/> Home</button>
            <div style={{display:"flex", alignItems:"center", gap:14, marginBottom:8}}>
              <span style={{fontSize:"2.2rem"}}>🇮🇳</span>
              <div>
                <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(2rem,5vw,3rem)", fontWeight:700}}>India Services for NRIs</h1>
                <p style={{color:"#A8A298", fontSize:"0.85rem", marginTop:4}}>Property, legal, banking, OCI, tax – handled remotely by verified professionals</p>
              </div>
            </div>
          </div>
        </section>
        <section style={{padding:"36px 16px", background:S.cream}}>
          <div style={{maxWidth:1024, margin:"0 auto"}}>
            <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:20}}>
              {INDIA_CATS.map(ic=>(
                <div key={ic.title} className="cd-card" style={{padding:24}}>
                  <div style={{fontSize:"2rem", marginBottom:10}}>{ic.icon}</div>
                  <h3 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"1.1rem", color:S.charcoal, marginBottom:6}}>{ic.title}</h3>
                  <p style={{fontSize:"0.75rem", color:S.muted, lineHeight:1.6, marginBottom:14}}>{ic.desc}</p>
                  <div style={{display:"flex", flexWrap:"wrap", gap:6, marginBottom:14}}>
                    {ic.subs.map(sub=>(
                      <span key={sub} onClick={()=>goSearch(sub,"")}
                        style={{background:S.creamDark, color:S.charLight, fontSize:"0.65rem", padding:"3px 10px", borderRadius:99, cursor:"pointer", border:"1px solid #DDD8CE"}}>
                        {sub}
                      </span>
                    ))}
                  </div>
                  <button onClick={()=>goSearch(ic.title,"")} className="cd-btn-outline" style={{width:"100%", padding:"8px", fontSize:"0.78rem"}}>
                    Find Professionals
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  };

  // ────────────────────────────────────────────────────
  // PAGE: SAVED PROFESSIONALS
  // ────────────────────────────────────────────────────
  const SavedPage = () => (
    <div style={{maxWidth:1024, margin:"0 auto", padding:"40px 16px"}}>
      <h1 style={{fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:"2rem", color:S.charcoal, marginBottom:8}}>Saved Professionals</h1>
      <p style={{color:S.muted, fontSize:"0.82rem", marginBottom:28}}>Professionals you've saved for easy access.</p>
      {savedProfiles.length===0 ? (
        <div style={{textAlign:"center", padding:"60px 16px"}}>
          <div style={{fontSize:"3rem", marginBottom:12}}>🔖</div>
          <p style={{color:S.charLight, fontWeight:600, marginBottom:6}}>No saved professionals yet</p>
          <p style={{color:S.muted, fontSize:"0.8rem", marginBottom:20}}>Browse professionals and click "Save Profile" to save them here.</p>
          <button onClick={()=>goSearch("","")} className="cd-btn-primary">Browse Professionals</button>
        </div>
      ) : (
        <div style={{display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:16}}>
          {savedProfiles.map(p=><ProvCard key={p.id} p={p}/>)}
        </div>
      )}
    </div>
  );


  // ────────────────────────────────────────────────────
  // PAGE: JOIN – Provider landing (value props + CTA)
  // ────────────────────────────────────────────────────
  const JoinPage = () => (
    <div style={{background:S.creamDark, minHeight:"100vh"}}>
      {/* Hero */}
      <section style={{background:S.charcoal, padding:"72px 16px 60px", textAlign:"center"}}>
        <div style={{maxWidth:720, margin:"0 auto"}}>
          <div style={{display:"inline-flex",alignItems:"center",gap:6,background:"#C4522A22",border:"1px solid #C4522A44",color:S.terra,fontSize:"0.65rem",fontWeight:800,letterSpacing:"0.12em",textTransform:"uppercase",padding:"5px 14px",borderRadius:99,marginBottom:18}}>
            For Verified Professionals
          </div>
          <h1 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"clamp(2rem,5vw,3.2rem)",color:S.cream,lineHeight:1.1,marginBottom:16}}>
            Grow your practice with<br/>
            <span style={{color:S.terra,fontStyle:"italic"}}>verified trust.</span>
          </h1>
          <p style={{color:"#A8A298",fontSize:"0.92rem",lineHeight:1.75,maxWidth:520,margin:"0 auto 32px"}}>
            CertifiedDESI is the only platform where South Asian professionals are verified by license, background-checked, and reviewed by their own community. Join 500+ verified pros.
          </p>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={()=>{setAuthMode("signup");setUType("provider");setAuthDirect("provider");setEmailStep(0);nav("auth");}}
              className="cd-btn-primary" style={{padding:"13px 32px",fontSize:"0.92rem",fontWeight:700}}>
              Get Listed – It's Free →
            </button>
            <button onClick={()=>nav("verification-process")} className="cd-btn-dark"
              style={{padding:"13px 24px",fontSize:"0.88rem",border:"1px solid #3E3C38",borderRadius:8,background:"transparent",color:"#A8A298",cursor:"pointer",fontFamily:"inherit"}}>
              See How Verification Works
            </button>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section style={{background:S.charMid,borderBottom:"1px solid #2E2C28",padding:"20px 16px"}}>
        <div style={{maxWidth:800,margin:"0 auto",display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))",gap:16,textAlign:"center"}}>
          {[["500+","Verified Professionals"],["12K+","Happy Clients"],["4.8★","Avg. Pro Rating"],["48hr","Response Guarantee"]].map(([n,l])=>(
            <div key={l}>
              <div style={{fontFamily:"'Cormorant Garamond',serif",fontSize:"1.8rem",fontWeight:700,color:S.terra}}>{n}</div>
              <div style={{color:"#6A6660",fontSize:"0.68rem",marginTop:2}}>{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What you get */}
      <section style={{padding:"64px 16px",maxWidth:960,margin:"0 auto"}}>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"2rem",color:S.charcoal,textAlign:"center",marginBottom:8}}>What you get as a CertifiedDESI Pro</h2>
        <p style={{color:S.muted,textAlign:"center",fontSize:"0.82rem",marginBottom:40}}>Everything you need to build a trusted practice in the Desi community</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16}}>
          {[
            {ic:"🏆",t:"Verified Badge",d:"Our 30-point verification – license check, background check, identity, insurance, and community reviews. The badge that Desi clients trust."},
            {ic:"🔍",t:"Discovered First",d:"Verified pros rank above unverified ones in every search. Clients filter for 'Elite' and 'Verified' badges – you'll be found."},
            {ic:"📋",t:"Direct Client Leads",d:"When a client posts a need in your category, you're notified immediately. No middleman, no bidding wars – just direct connections."},
            {ic:"📊",t:"Professional Dashboard",d:"Track profile views, consultation requests, saved profiles, and reviews – all in one clean dashboard built for busy professionals."},
            {ic:"💬",t:"Community Trust",d:"Built specifically for South Asian professionals and clients. Cultural context, language matching, and community references – all built in."},
            {ic:"📱",t:"Mobile App (Coming Soon)",d:"Manage your profile, respond to leads, and track reviews from your phone. iOS & Android launching soon – early pros get priority access."},
          ].map(item=>(
            <div key={item.t} style={{background:S.creamMid,border:`1px solid #DDD8CE`,borderRadius:14,padding:24}}>
              <div style={{fontSize:"1.8rem",marginBottom:12}}>{item.ic}</div>
              <h3 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.1rem",color:S.charcoal,marginBottom:8}}>{item.t}</h3>
              <p style={{fontSize:"0.76rem",color:S.charLight,lineHeight:1.65}}>{item.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section style={{padding:"48px 16px",background:S.cream}}>
        <div style={{maxWidth:720,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.8rem",color:S.charcoal,textAlign:"center",marginBottom:36}}>Get listed in 3 steps</h2>
          <div style={{display:"flex",flexDirection:"column",gap:20}}>
            {[
              ["01","Sign Up Free","Create your profile in under 5 minutes. Name, category, city, bio – that's it to get started."],
              ["02","Get Verified","Submit your license, ID, and references. Our team reviews within 48 hours and issues your badge."],
              ["03","Start Getting Clients","Your profile goes live. Clients in your city searching your category will find you. Respond to direct consultation requests."],
            ].map(([num,title,desc])=>(
              <div key={num} style={{display:"flex",gap:20,alignItems:"flex-start"}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:S.terra,color:S.cream,fontWeight:700,fontSize:"0.85rem",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{num}</div>
                <div>
                  <p style={{fontWeight:700,fontSize:"0.88rem",color:S.charcoal,marginBottom:4}}>{title}</p>
                  <p style={{fontSize:"0.76rem",color:S.muted,lineHeight:1.6}}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who joins */}
      <section style={{padding:"48px 16px",maxWidth:960,margin:"0 auto"}}>
        <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.8rem",color:S.charcoal,textAlign:"center",marginBottom:8}}>Who joins CertifiedDESI</h2>
        <p style={{color:S.muted,textAlign:"center",fontSize:"0.8rem",marginBottom:32}}>Any licensed or certified South Asian professional serving the US Desi community</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:8,justifyContent:"center"}}>
          {["CPAs & Tax Professionals","Immigration Attorneys","Real Estate Agents","Financial Advisors","Doctors & Physicians","Insurance Agents","Business Consultants","Wedding Pandits","Elder Care Specialists","IT Trainers & Recruiters","Tutors & Educators","Vedic Astrologers"].map(t=>(
            <span key={t} style={{background:S.creamMid,border:`1px solid #DDD8CE`,borderRadius:99,padding:"6px 14px",fontSize:"0.74rem",color:S.charcoal}}>✓ {t}</span>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section style={{padding:"60px 16px",background:S.charcoal,textAlign:"center"}}>
        <div style={{maxWidth:560,margin:"0 auto"}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"2rem",color:S.cream,marginBottom:12}}>Ready to grow your practice?</h2>
          <p style={{color:"#A8A298",fontSize:"0.82rem",marginBottom:28,lineHeight:1.7}}>Join free. Get your Verified badge. Start receiving client leads in your city within 48 hours of approval.</p>
          <button onClick={()=>{setAuthMode("signup");setUType("provider");setAuthDirect("provider");setEmailStep(0);nav("auth");}}
            className="cd-btn-primary" style={{padding:"14px 40px",fontSize:"0.95rem",fontWeight:700}}>
            Create My Free Profile →
          </button>
          <p style={{color:"#4A4740",fontSize:"0.68rem",marginTop:14}}>No subscription fees · No commissions · Free forever for verified pros</p>
        </div>
      </section>
    </div>
  );


  // ── PASSWORD RESET PAGE ──────────────────────────────────────
  const PasswordResetPage = () => {
    const [prEmail, setPrEmail]   = React.useState("");
    const [prPwd,   setPrPwd]     = React.useState("");
    const [prDone,  setPrDone]    = React.useState(false);
    const [prLoading, setPrLoading] = React.useState(false);
    // Check for token in URL
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get("token");
    const resetEmail = urlParams.get("email") || "";

    const handleRequest = async () => {
      if (!prEmail.includes("@")) { showToast("Enter a valid email","error"); return; }
      setPrLoading(true);
      const fd = new FormData();
      fd.append("email", prEmail);       try {
        fd.append("action","reset_request");
        const r = await fetch(API_BASE+"/submit_auth.php",{method:"POST",body:fd});
        const d = await r.json();
        if(d.csrf) setCsrfToken(d.csrf);
        setPrDone(true);
        showToast(d.message || "Reset link sent ✓");
      } catch(e) { showToast("Something went wrong","error"); }
      finally { setPrLoading(false); }
    };

    const handleConfirm = async () => {
      if (prPwd.length < 8) { showToast("Password must be at least 8 characters","error"); return; }
      setPrLoading(true);
      const fd = new FormData();
      fd.append("token", resetToken); fd.append("email", resetEmail);
      fd.append("password", prPwd);       try {
        fd.append("action","reset_request");
        const r = await fetch(API_BASE+"/submit_auth.php",{method:"POST",body:fd});
        const d = await r.json();
        if(d.csrf) setCsrfToken(d.csrf);
        if(d.success){setPrDone(true); showToast("Password updated! Please sign in ✓"); setTimeout(()=>nav("auth"),1500);}
        else showToast(d.message||"Reset failed","error");
      } catch(e) { showToast("Something went wrong","error"); }
      finally { setPrLoading(false); }
    };

    return (
      <div style={{minHeight:"80vh",display:"flex",alignItems:"center",justifyContent:"center",padding:24,background:S.creamDark}}>
        <div style={{background:S.creamMid,borderRadius:16,padding:32,width:"100%",maxWidth:420,boxShadow:"0 8px 40px rgba(0,0,0,0.1)",border:`1px solid ${S.creamDark}`}}>
          <h2 style={{fontFamily:"'Cormorant Garamond',serif",fontWeight:700,fontSize:"1.8rem",color:S.charcoal,marginBottom:8}}>
            {resetToken ? "Set new password" : "Reset your password"}
          </h2>
          {prDone ? (
            <div style={{textAlign:"center",padding:"16px 0"}}>
              <div style={{fontSize:"2.5rem",marginBottom:12}}>✉️</div>
              <p style={{color:S.charLight,fontSize:"0.85rem",lineHeight:1.7}}>
                {resetToken ? "Password updated! Redirecting..." : "Check your email for a reset link. It expires in 1 hour."}
              </p>
              <button onClick={()=>nav("auth")} className="cd-btn-primary" style={{marginTop:16,width:"100%"}}>Back to Sign In</button>
            </div>
          ) : resetToken ? (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <p style={{color:S.muted,fontSize:"0.78rem"}}>Enter your new password for <strong>{resetEmail}</strong></p>
              <div>
                <label className="cd-label">New Password</label>
                <input className="cd-input" type="password" placeholder="Min. 8 characters" value={prPwd} onChange={e=>setPrPwd(e.target.value)}/>
              </div>
              <button onClick={handleConfirm} disabled={prLoading} className="cd-btn-primary" style={{width:"100%",padding:"12px"}}>
                {prLoading ? "Updating..." : "Update Password →"}
              </button>
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <p style={{color:S.muted,fontSize:"0.78rem"}}>Enter the email address linked to your account. We'll send you a reset link.</p>
              <div>
                <label className="cd-label">Email Address</label>
                <input className="cd-input" type="email" placeholder="you@email.com" value={prEmail} onChange={e=>setPrEmail(e.target.value)}
                  onKeyDown={e=>e.key==="Enter"&&handleRequest()}/>
              </div>
              <button onClick={handleRequest} disabled={prLoading} className="cd-btn-primary" style={{width:"100%",padding:"12px"}}>
                {prLoading ? "Sending..." : "Send Reset Link →"}
              </button>
              <button onClick={()=>nav("auth")} className="cd-btn-ghost" style={{width:"100%"}}>Back to Sign In</button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const pageMap = {
    "landing":              <Landing/>,
    "join":                 <JoinPage/>,
    "auth":                 <Auth/>,
    "search":               <SearchPage/>,
    "profile":              <Profile/>,
    "consumer-dash":        <ConsumerDash/>,
    "onboarding":           <Onboarding/>,
    "provider-dash":        <ProviderDash/>,
    "how-it-works":         <HowItWorks/>,
    "safety":               <Safety/>,
    "post-need":            <PostNeed/>,
    "help":                 <Help/>,
    "verification-process": <VerificationProcess/>,
    "success-stories":      <SuccessStories/>,
    "resources":            <Resources/>,
    "about":                <About/>,
    "contact":              <Contact/>,
    "reset-password":      <PasswordResetPage/>,
    "privacy":              <Privacy/>,
    "terms":                <Terms/>,
    "press":                <Press/>,
    "wedding":              <CategoryPage cat="wedding"/>,
    "care":                 <CategoryPage cat="care"/>,
    "education":            <CategoryPage cat="education"/>,
    "ittraining":           <CategoryPage cat="ittraining"/>,
    "astrology":            <CategoryPage cat="astrology"/>,
    "roommates":            <RoommatesPage/>,
    "india-services":       <IndiaServicesPage/>,
    "saved":                <SavedPage/>,
    "category":             <CategoryPage cat={searchCat}/>,
    // Category pages – own clean URLs
    "immigration":          <CategoryPage cat="immigration"/>,
    "tax":                  <CategoryPage cat="tax"/>,
    "realestate":           <CategoryPage cat="realestate"/>,
    "legal":                <CategoryPage cat="legal"/>,
    "financial":            <CategoryPage cat="financial"/>,
    "insurance":            <CategoryPage cat="insurance"/>,
    "healthcare":           <CategoryPage cat="healthcare"/>,
    "business":             <CategoryPage cat="business"/>,
  };

  return (
    <div style={{minHeight:"100vh", background:S.cream, fontFamily:"'DM Sans',sans-serif", colorScheme: isDark ? 'dark' : 'light'}}>
      <style>{GLOBAL_CSS}</style>
      {/* Skip to main content – WCAG 2.2 AA */}
      <a href="#main-content" style={{
        position:"absolute", top:-40, left:0, zIndex:9999,
        background:S.terra, color:S.cream, padding:"8px 16px",
        fontSize:"0.85rem", fontWeight:700, borderRadius:"0 0 8px 0",
        transition:"top 0.2s"
      }} onFocus={e=>e.target.style.top="0"} onBlur={e=>e.target.style.top="-40px"}>
        Skip to main content
      </a>
      <div ref={topRef}/>
      <Header/>
      {/* ── MEGA MENU OVERLAY + PANEL – root level, true z-index ── */}
      <div className={`cd-mega-overlay ${menuOpen?"open":""}`}
        onClick={()=>{setMenuOpen(false);setMenuExpandedCat(null);setMenuView("main");}}/>
            {/* Panel – slides in via transform, never unmounts */}
      <div className={`cd-mega-menu ${menuOpen?"open":""}`}
        role="dialog" aria-modal={menuOpen} aria-label="All Services Menu">
        {/* Amazon-style "Hello" header */}
        <div className="cd-mega-header">
          <div style={{flex:1}}>
            <p style={{color:"#8A8578", fontSize:"0.6rem", textTransform:"uppercase", letterSpacing:"0.08em"}}>Hello,</p>
            <p style={{color:"#F2EDE4", fontSize:"0.88rem", fontWeight:700, marginTop:1}}>
              {user ? user.name.split(" ")[0] : "Sign In"}
            </p>
          </div>
          {!user && (
            <button onClick={()=>nav("auth")} className="cd-btn-primary"
              style={{padding:"6px 14px", fontSize:"0.72rem", marginRight:8}}>
              Login
            </button>
          )}
          <button onClick={()=>{setMenuOpen(false);setMenuExpandedCat(null);setMenuView("main");}}
            style={{background:"none",border:"none",color:"#6A6660",cursor:"pointer",fontSize:"1.2rem",lineHeight:1,padding:"4px"}}>
            ✕
          </button>
        </div>

        {/* Slide views container */}
        <div className="cd-menu-views">

          {/* ── MAIN VIEW ─────────────────────────────── */}
          <div className={`cd-menu-main ${menuView==="sub"?"slide-out":""}`}>
            {/* Quick links */}
            <div style={{borderBottom:"1px solid #1E1D1C", paddingBottom:2}}>
              {[["🇮🇳","India Services",()=>nav("india-services")],
                ["🛋️","Roommates & Rentals",()=>nav("roommates")],
                ["📋","Post a Need",()=>nav("post-need")],
                ["🔖","Saved Professionals",()=>user?nav("saved"):nav("auth")],
              ].map(([ic,lb,fn])=>(
                <div key={lb} className="cd-mega-cat"
                  onClick={()=>{fn();setMenuOpen(false);setMenuView("main");}}>
                  <span style={{fontSize:"0.92rem",flexShrink:0}}>{ic}</span>
                  <span style={{color:"#C49A2A",fontSize:"0.77rem",fontWeight:600}}>{lb}</span>
                </div>
              ))}
            </div>

            <div style={{padding:"10px 20px 4px",fontSize:"0.58rem",color:"#4A4744",fontWeight:800,letterSpacing:"0.1em",textTransform:"uppercase"}}>
              All Categories
            </div>

            {/* Category rows – click to slide to sub-view */}
            {CATS.map(cat=>(
              <div key={cat.id} className="cd-mega-cat"
                onClick={()=>{
                  // Save current scroll position of main view
                  const el=document.querySelector(".cd-menu-main");
                  if(el) menuMainScrollRef.current = el.scrollTop;
                  setMenuExpandedCat(cat.id);
                  setMenuView("sub");
                }}>
                <span style={{fontSize:"1.05rem",flexShrink:0}}>{cat.icon}</span>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{color:"#C8C2BA",fontSize:"0.78rem",fontWeight:400}}>{cat.label}</p>
                </div>
                <ChevronRight size={13} style={{color:"#3A3836",flexShrink:0}}/>
              </div>
            ))}

            {/* Footer links */}
            <div style={{padding:"14px 20px 24px",borderTop:"1px solid #1E1D1C",marginTop:4}}>
              {[["✅ Get Verified",()=>{setAuthMode("signup");setUType("provider");nav("auth");}],
                ["📖 Success Stories",()=>nav("success-stories")],
                ["ℹ️ How It Works",()=>nav("how-it-works")],
                ["🛡️ Safety & Trust",()=>nav("safety")],
              ].map(([label,fn])=>(
                <div key={label}
                  onClick={()=>{fn();setMenuOpen(false);setMenuView("main");}}
                  style={{color:"#6A6660",fontSize:"0.75rem",padding:"9px 0",cursor:"pointer",
                    borderBottom:"1px solid #1A1918",transition:"color 0.15s"}}
                  onMouseEnter={e=>e.currentTarget.style.color="#F2EDE4"}
                  onMouseLeave={e=>e.currentTarget.style.color="#6A6660"}>
                  {label}
                </div>
              ))}
            </div>
          </div>

          {/* ── SUB VIEW ──────────────────────────────── */}
          {(() => {
            const cat  = CATS.find(c=>c.id===menuExpandedCat);
            const subs = SUB_CATS[menuExpandedCat]||[];
            return (
              <div className={`cd-menu-sub ${menuView==="sub"?"slide-in":""}`}>
                {/* Back header */}
                <div className="cd-menu-sub-header">
                  <button className="cd-menu-back-btn"
                    onClick={()=>{
                      setMenuView("main");
                      // Restore scroll position after transition
                      setTimeout(()=>{
                        const el=document.querySelector(".cd-menu-main");
                        if(el) el.scrollTop = menuMainScrollRef.current;
                      }, 280); // matches transition duration
                    }}>
                    <ChevronLeft size={15}/> Main Menu
                  </button>
                </div>

                {/* Browse all CTA */}
                <div className="cd-mega-cat"
                  style={{borderBottom:"2px solid #222120"}}
                  onClick={()=>{goSearch("",menuExpandedCat);setMenuView("main");}}>
                  <span style={{color:S.terra,fontSize:"0.78rem",fontWeight:700}}>Browse All {cat?.label}</span>
                </div>

                {/* Sub-items */}
                {subs.map(sub=>(
                  <div key={sub} className="cd-mega-cat"
                    onClick={()=>{goSearch(sub,menuExpandedCat);setMenuView("main");}}>
                    <span style={{color:"#A8A298",fontSize:"0.77rem"}}>{sub}</span>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      </div>
      <Toast/>
      <main id="main-content">{pageMap[page] || <Landing/>}</main>
      {page!=="auth" && <Footer/>}
      <ChatWidget/>
    </div>
  );
}
